import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from "react-simple-maps";
import { UrlContext } from "../contexts/UrlContext";
import {
  Activity,
  ArrowRight,
  BadgeEuro,
  BarChart3,
  BriefcaseBusiness,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  Database,
  Globe2,
  Info,
  Mail,
  Inbox,
  Send,
  MapPinned,
  MessageCircle,
  PieChart as PieChartIcon,
  RefreshCw,
  Rocket,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingDown,
  TrendingUp,
  Trophy,
  UsersRound,
  Video,
  X,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const BRAND = "#365561";

const pieColors = ["#365561", "#94a3b8"];
const financeColors = ["#365561", "#94a3b8", "#0ea5e9", "#8b5cf6"];
const categoryColors = [
  "#365561",
  "#0ea5e9",
  "#8b5cf6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#14b8a6",
  "#6366f1",
  "#ec4899",
  "#84cc16",
  "#64748b",
  "#f97316",
];

const geoUrl =
  "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const countryCoordinates = {
  Germany: { coordinates: [10.4515, 51.1657] },
  India: { coordinates: [78.9629, 20.5937] },
  "United States": { coordinates: [-95.7129, 37.0902] },
  USA: { coordinates: [-95.7129, 37.0902] },
  "United Kingdom": { coordinates: [-3.436, 55.3781] },
  France: { coordinates: [2.2137, 46.2276] },
  Spain: { coordinates: [-3.7492, 40.4637] },
  Italy: { coordinates: [12.5674, 41.8719] },
  Netherlands: { coordinates: [5.2913, 52.1326] },
  Belgium: { coordinates: [4.4699, 50.5039] },
  Switzerland: { coordinates: [8.2275, 46.8182] },
  Austria: { coordinates: [14.5501, 47.5162] },
  Poland: { coordinates: [19.1451, 51.9194] },
  Portugal: { coordinates: [-8.2245, 39.3999] },
  Sweden: { coordinates: [18.6435, 60.1282] },
  Norway: { coordinates: [8.4689, 60.472] },
  Denmark: { coordinates: [9.5018, 56.2639] },
  Finland: { coordinates: [25.7482, 61.9241] },
  Ireland: { coordinates: [-8.2439, 53.4129] },
  Canada: { coordinates: [-106.3468, 56.1304] },
  Mexico: { coordinates: [-102.5528, 23.6345] },
  Brazil: { coordinates: [-51.9253, -14.235] },
  Argentina: { coordinates: [-63.6167, -38.4161] },
  Chile: { coordinates: [-71.543, -35.6751] },
  Colombia: { coordinates: [-74.2973, 4.5709] },
  Peru: { coordinates: [-75.0152, -9.19] },
  Australia: { coordinates: [133.7751, -25.2744] },
  "South Africa": { coordinates: [22.9375, -30.5595] },
  Nigeria: { coordinates: [8.6753, 9.082] },
  Egypt: { coordinates: [30.8025, 26.8206] },
  UAE: { coordinates: [53.8478, 23.4241] },
  "United Arab Emirates": { coordinates: [53.8478, 23.4241] },
  Saudi: { coordinates: [45.0792, 23.8859] },
  "Saudi Arabia": { coordinates: [45.0792, 23.8859] },
  Singapore: { coordinates: [103.8198, 1.3521] },
  Malaysia: { coordinates: [101.9758, 4.2105] },
  Indonesia: { coordinates: [113.9213, -0.7893] },
  Philippines: { coordinates: [121.774, 12.8797] },
  Japan: { coordinates: [138.2529, 36.2048] },
  China: { coordinates: [104.1954, 35.8617] },
  "South Korea": { coordinates: [127.7669, 35.9078] },
};

function getBubbleSize(count) {
  if (count >= 30) return 12;
  if (count >= 20) return 10;
  if (count >= 10) return 8;
  if (count >= 5) return 6;
  return 4;
}

const mapCountryAliases = {
  "usa": "united states of america",
  "united states": "united states of america",
  "united states of america": "united states of america",
  "uk": "united kingdom",
  "great britain": "united kingdom",
  "uae": "united arab emirates",
  "saudi": "saudi arabia",
  "republic of korea": "south korea",
  "korea, republic of": "south korea",
};

const normalizeMapCountryName = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return mapCountryAliases[normalized] || normalized;
};

const getConsultantCountryFill = (count, maxCount) => {
  const numericCount = Number(count || 0);
  const numericMax = Math.max(1, Number(maxCount || 0));

  if (numericCount <= 0) return "#dbe4ea";

  const ratio = numericCount / numericMax;
  if (ratio >= 0.75) return "#365561";
  if (ratio >= 0.5) return "#67838d";
  if (ratio >= 0.25) return "#91a9b1";
  return "#bdcdd3";
};

const getResponsiveBubbleSize = (count, maxCount) => {
  const numericCount = Math.max(0, Number(count || 0));
  const numericMax = Math.max(1, Number(maxCount || 0));

  if (numericCount <= 0) return 0;

  return 4 + Math.sqrt(numericCount / numericMax) * 11;
};


const consultantMapRegionConfig = [
  {
    key: "world",
    label: "World",
    center: [0, 6],
    zoom: 1,
  },
  {
    key: "europe",
    label: "Europe",
    center: [15, 52],
    zoom: 2.45,
  },
  {
    key: "asia",
    label: "Asia",
    center: [92, 28],
    zoom: 1.72,
  },
  {
    key: "africa",
    label: "Africa",
    center: [20, 3],
    zoom: 2.05,
  },
  {
    key: "north_america",
    label: "N. America",
    center: [-100, 39],
    zoom: 1.82,
  },
  {
    key: "south_america",
    label: "S. America",
    center: [-60, -18],
    zoom: 2.02,
  },
  {
    key: "oceania",
    label: "Oceania",
    center: [135, -25],
    zoom: 2.28,
  },
];

const consultantCountryRegionAliases = {
  Germany: "europe",
  "United Kingdom": "europe",
  France: "europe",
  Spain: "europe",
  Italy: "europe",
  Netherlands: "europe",
  Belgium: "europe",
  Switzerland: "europe",
  Austria: "europe",
  Poland: "europe",
  Portugal: "europe",
  Sweden: "europe",
  Norway: "europe",
  Denmark: "europe",
  Finland: "europe",
  Ireland: "europe",

  India: "asia",
  UAE: "asia",
  "United Arab Emirates": "asia",
  Saudi: "asia",
  "Saudi Arabia": "asia",
  Singapore: "asia",
  Malaysia: "asia",
  Indonesia: "asia",
  Philippines: "asia",
  Japan: "asia",
  China: "asia",
  "South Korea": "asia",

  "South Africa": "africa",
  Nigeria: "africa",
  Egypt: "africa",

  "United States": "north_america",
  USA: "north_america",
  Canada: "north_america",
  Mexico: "north_america",

  Brazil: "south_america",
  Argentina: "south_america",
  Chile: "south_america",
  Colombia: "south_america",
  Peru: "south_america",

  Australia: "oceania",
};

const getConsultantMapRegionKey = (country) => {
  if (country && typeof country === "object" && country.region_key) {
    return country.region_key;
  }

  const countryName =
    country && typeof country === "object" ? country.country : country;

  return consultantCountryRegionAliases[String(countryName || "").trim()] || null;
};

const formatCurrency = (value) =>
  `€${Number(value || 0).toLocaleString(undefined, {
    maximumFractionDigits: 0,
  })}`;

const formatNumber = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    maximumFractionDigits: 0,
  });

const formatPercent = (value) =>
  `${Number(value || 0).toLocaleString(undefined, {
    maximumFractionDigits: 1,
  })}%`;


const formatRateOrDash = (value, denominator) =>
  Number(denominator || 0) > 0 ? formatPercent(value) : "—";

const formatPercentagePointDelta = (value) => {
  const numeric = Number(value || 0);
  return `${numeric > 0 ? "+" : ""}${numeric.toLocaleString(undefined, {
    maximumFractionDigits: 1,
  })} pp`;
};

const formatKpiNumber = (value, maximumFractionDigits = 1) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }

  return Number(value).toLocaleString(undefined, {
    maximumFractionDigits,
  });
};

const formatDate = (value) => {
  if (!value) return "No date";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Invalid date";

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "No date";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Invalid date";

  const time = date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return `${formatDate(value)}, ${time}`;
};


const formatScheduledCallRange = (startValue, endValue) => {
  if (!startValue) return "Client platform";

  const start = new Date(startValue);
  if (Number.isNaN(start.getTime())) return "Client platform";

  const dateLabel = start.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const startTime = start.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });

  if (!endValue) {
    return `${dateLabel} ${startTime}`;
  }

  const end = new Date(endValue);
  if (Number.isNaN(end.getTime())) {
    return `${dateLabel} ${startTime}`;
  }

  const endTime = end.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });

  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();

  if (sameDay) {
    return `${dateLabel} ${startTime} - ${endTime}`;
  }

  const endDateLabel = end.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  return `${dateLabel} ${startTime} - ${endDateLabel} ${endTime}`;
};

const getMonthKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");

  return `${year}-${month}`;
};

const getMonthDateFromKey = (monthKey) => {
  const [year, month] = String(monthKey || getMonthKey())
    .split("-")
    .map(Number);

  return new Date(year, (month || 1) - 1, 1);
};

const addMonthsToKey = (monthKey, amount) => {
  const date = getMonthDateFromKey(monthKey);
  date.setMonth(date.getMonth() + amount);

  return getMonthKey(date);
};

const getMonthLabelFromKey = (monthKey) => {
  const date = getMonthDateFromKey(monthKey);

  return date.toLocaleDateString("en-US", {
    month: "short",
    year: "2-digit",
  });
};

const isCurrentMonthKey = (monthKey) => {
  return monthKey === getMonthKey();
};


const getQuarterKey = (date = new Date()) => {
  const year = date.getFullYear();
  const quarter = Math.floor(date.getMonth() / 3) + 1;

  return `${year}-Q${quarter}`;
};

const addQuartersToKey = (quarterKey, amount) => {
  const [yearPart, quarterPart] = String(quarterKey || getQuarterKey()).split("-Q");
  const year = Number(yearPart);
  const quarter = Number(quarterPart || 1);
  const date = new Date(year, (quarter - 1) * 3, 1);

  date.setMonth(date.getMonth() + amount * 3);

  return getQuarterKey(date);
};

const getQuarterLabelFromKey = (quarterKey) => {
  const [yearPart, quarterPart] = String(quarterKey || getQuarterKey()).split("-Q");
  const quarter = Number(quarterPart || 1);
  const year = String(yearPart || new Date().getFullYear()).slice(-2);

  const ordinal = {
    1: "1st",
    2: "2nd",
    3: "3rd",
    4: "4th",
  }[quarter] || `${quarter}th`;

  return `${ordinal} Quarter ${year}`;
};

const isCurrentQuarterKey = (quarterKey) => {
  return quarterKey === getQuarterKey();
};

const getYearKey = (date = new Date()) => {
  return String(date.getFullYear());
};

const addYearsToKey = (yearKey, amount) => {
  return String(Number(yearKey || getYearKey()) + amount);
};

const isCurrentYearKey = (yearKey) => {
  return yearKey === getYearKey();
};

const getPeriodLabelByType = (type, value) => {
  if (type === "quarter") {
    return getQuarterLabelFromKey(value);
  }

  if (type === "year") {
    return value || getYearKey();
  }

  return getMonthLabelFromKey(value);
};

const getISOWeekInfo = (input = new Date()) => {
  const date = new Date(Date.UTC(
    input.getFullYear(),
    input.getMonth(),
    input.getDate()
  ));

  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - day);

  const year = date.getUTCFullYear();
  const yearStart = new Date(Date.UTC(year, 0, 1));
  const week = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);

  return { year, week };
};

const getWeekKey = (date = new Date()) => {
  const { year, week } = getISOWeekInfo(date);
  return `${year}-W${String(week).padStart(2, "0")}`;
};

const getISOWeeksInYear = (year) => {
  const { week } = getISOWeekInfo(new Date(year, 11, 28));
  return week;
};

const getWeekStartFromKey = (weekKey) => {
  const match = String(weekKey || getWeekKey()).match(/^(\d{4})-W(\d{2})$/);
  const year = Number(match?.[1] || new Date().getFullYear());
  const week = Number(match?.[2] || 1);

  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Day = jan4.getUTCDay() || 7;
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - jan4Day + 1 + (week - 1) * 7);

  return monday;
};

const getWeekLabelFromKey = (weekKey) => {
  const start = getWeekStartFromKey(weekKey);
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);

  const weekNumber = Number(String(weekKey).split("-W")[1] || 1);
  const startLabel = start.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    timeZone: "UTC",
  });
  const endLabel = end.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });

  return `Week ${weekNumber} · ${startLabel} – ${endLabel}`;
};

const getCurrentRangeKey = (type) => {
  if (type === "week") return getWeekKey();
  if (type === "quarter") return getQuarterKey();
  if (type === "year") return getYearKey();
  return getMonthKey();
};

const getRangeYear = (type, value) => {
  if (type === "year") return Number(value || new Date().getFullYear());
  return Number(String(value || getCurrentRangeKey(type)).slice(0, 4));
};

const getRangeLabel = (type, value) => {
  if (type === "week") return getWeekLabelFromKey(value);
  if (type === "quarter") return getQuarterLabelFromKey(value);
  if (type === "year") return String(value || getYearKey());
  return getMonthDateFromKey(value).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
};

const getPreviousRangeKey = (type, value) => {
  if (type === "week") {
    const start = getWeekStartFromKey(value);
    start.setUTCDate(start.getUTCDate() - 7);
    const localDate = new Date(
      start.getUTCFullYear(),
      start.getUTCMonth(),
      start.getUTCDate()
    );
    return getWeekKey(localDate);
  }

  if (type === "quarter") return addQuartersToKey(value, -1);
  if (type === "year") return addYearsToKey(value, -1);
  return addMonthsToKey(value, -1);
};

const getCompactRangeLabel = (type, value) => {
  if (type === "year") return String(value || getYearKey());

  if (type === "quarter") {
    const [yearPart, quarterPart] = String(value || getQuarterKey()).split("-Q");
    return `Q${quarterPart || "1"} '${String(yearPart || "").slice(-2)}`;
  }

  if (type === "week") {
    const weekMatch = String(value || getWeekKey()).match(/^(\d{4})-W(\d{2})$/);
    if (!weekMatch) return String(value || "");
    return `W${Number(weekMatch[2])} '${weekMatch[1].slice(-2)}`;
  }

  const date = getMonthDateFromKey(value);
  return date.toLocaleDateString("en-US", {
    month: "short",
    year: "2-digit",
  });
};

function DashboardRangeControls({
  periodType,
  rangeKey,
  loading,
  onPeriodTypeChange,
  onRangeChange,
  compact = false,
}) {
  const [periodOpen, setPeriodOpen] = useState(false);
  const [rangeOpen, setRangeOpen] = useState(false);
  const controlsRef = useRef(null);
  const [pickerYear, setPickerYear] = useState(() =>
    getRangeYear(periodType, rangeKey)
  );

  const currentYear = new Date().getFullYear();
  const minimumYear = 2022;
  const currentWeek = getISOWeekInfo();
  const currentMonth = new Date().getMonth() + 1;
  const currentQuarter = Math.floor(new Date().getMonth() / 3) + 1;

  useEffect(() => {
    setPickerYear(getRangeYear(periodType, rangeKey));
  }, [periodType, rangeKey]);

  // Both dropdowns are independent, but dismiss together on outside click or Escape.
  useEffect(() => {
    if (!periodOpen && !rangeOpen) return;

    const handleOutsideClick = (event) => {
      if (!controlsRef.current?.contains(event.target)) {
        setPeriodOpen(false);
        setRangeOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setPeriodOpen(false);
        setRangeOpen(false);
      }
    };

    document.addEventListener("pointerdown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("pointerdown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [periodOpen, rangeOpen]);

  const periodOptions = [
    { type: "week", label: "Weekly" },
    { type: "month", label: "Monthly" },
    { type: "quarter", label: "Quarterly" },
    { type: "year", label: "Yearly" },
  ];

  const periodLabel =
    periodOptions.find((item) => item.type === periodType)?.label || "Weekly";

  const selectPeriod = (type) => {
    onPeriodTypeChange(type);
    setPeriodOpen(false);
    setRangeOpen(false);
    setPickerYear(getRangeYear(type, getCurrentRangeKey(type)));
  };

  const selectRange = (key) => {
    onRangeChange(key);
    setRangeOpen(false);
  };

  const rangeItems = (() => {
    if (periodType === "week") {
      const maxWeek =
        pickerYear === currentWeek.year
          ? currentWeek.week
          : getISOWeeksInYear(pickerYear);

      return Array.from({ length: maxWeek }, (_, index) => {
        const week = index + 1;
        return {
          key: `${pickerYear}-W${String(week).padStart(2, "0")}`,
          label: `W${week}`,
        };
      });
    }

    if (periodType === "month") {
      const monthNames = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
      ];
      const maxMonth = pickerYear === currentYear ? currentMonth : 12;
      return monthNames.slice(0, maxMonth).map((label, index) => ({
        key: `${pickerYear}-${String(index + 1).padStart(2, "0")}`,
        label,
      }));
    }

    if (periodType === "quarter") {
      const maxQuarter = pickerYear === currentYear ? currentQuarter : 4;
      return Array.from({ length: maxQuarter }, (_, index) => ({
        key: `${pickerYear}-Q${index + 1}`,
        label: `Q${index + 1}`,
      }));
    }

    return Array.from(
      { length: currentYear - minimumYear + 1 },
      (_, index) => currentYear - index
    ).map((year) => ({ key: String(year), label: String(year) }));
  })();

  return (
    <div ref={controlsRef} className="relative z-40 flex w-full max-w-full justify-end sm:w-auto">
      {/* One visual control, with two independent accessible buttons. */}
      <div className="flex w-full max-w-full items-stretch rounded-xl border border-[#C9D7DC] bg-white shadow-[0_8px_24px_rgba(54,85,97,0.08)] sm:w-auto">
        <div className="relative shrink-0">
          <button
            type="button"
            disabled={loading}
            aria-label={`Select period type, currently ${periodLabel}`}
            aria-haspopup="true"
            aria-expanded={periodOpen}
            onClick={() => {
              setPeriodOpen((current) => !current);
              setRangeOpen(false);
            }}
            className={`inline-flex h-full items-center gap-2 rounded-l-xl text-sm font-semibold text-[#365561] transition-colors hover:bg-[#EAF1F3] disabled:cursor-wait disabled:opacity-60 ${compact ? "px-2.5 py-2" : "px-3.5 py-2.5"}`}
          >
            <CalendarDays className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="whitespace-nowrap">{periodLabel}</span>
            <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-[#67838D] transition-transform ${periodOpen ? "rotate-180" : ""}`} aria-hidden="true" />
          </button>

          {periodOpen ? (
            <div className="absolute left-0 top-full z-[80] mt-2 w-48 overflow-hidden rounded-2xl border border-slate-200 bg-white p-1.5 shadow-[0_22px_60px_rgba(15,23,42,0.18)]">
              {periodOptions.map((option) => (
                <button
                  key={option.type}
                  type="button"
                  onClick={() => selectPeriod(option.type)}
                  className={[
                    "flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm font-semibold transition",
                    periodType === option.type
                      ? "bg-[#365561]/10 text-[#365561]"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
                  ].join(" ")}
                >
                  <span>{option.label}</span>
                  {periodType === option.type ? <span>✓</span> : null}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="my-2.5 w-px shrink-0 bg-[#C9D7DC]" aria-hidden="true" />

        <div className={`relative min-w-0 flex-1 ${compact ? "sm:min-w-[120px]" : "sm:min-w-[250px]"}`}>
          <button
            type="button"
            disabled={loading}
            aria-label={`Select date range, currently ${getRangeLabel(periodType, rangeKey)}`}
            aria-haspopup="true"
            aria-expanded={rangeOpen}
            onClick={() => {
              setRangeOpen((current) => !current);
              setPeriodOpen(false);
            }}
            className={`flex h-full w-full min-w-0 items-center justify-between gap-2 rounded-r-xl text-sm font-semibold text-[#365561] transition-colors hover:bg-[#EAF1F3] disabled:cursor-wait disabled:opacity-60 ${compact ? "px-2.5 py-2" : "px-3.5 py-2.5"}`}
          >
            <span className="truncate whitespace-nowrap">{compact ? getCompactRangeLabel(periodType, rangeKey) : getRangeLabel(periodType, rangeKey)}</span>
            <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-[#67838D] transition-transform ${rangeOpen ? "rotate-180" : ""}`} aria-hidden="true" />
          </button>

          {rangeOpen ? (
            <div className="absolute right-0 top-full z-[80] mt-2 w-[min(390px,calc(100vw-32px))] rounded-3xl border border-slate-200 bg-white p-4 shadow-[0_24px_70px_rgba(15,23,42,0.20)]">
              {periodType !== "year" ? (
                <div className="mb-3 flex items-center justify-between gap-3">
                  <button
                    type="button"
                    disabled={pickerYear <= minimumYear}
                    onClick={() => setPickerYear((year) => Math.max(minimumYear, year - 1))}
                    className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    ‹
                  </button>

                  <div className="text-center">
                    <p className="text-xs font-medium uppercase tracking-[0.16em] text-slate-400">
                      Select {periodType}
                    </p>
                    <p className="mt-0.5 text-sm font-semibold text-slate-800">{pickerYear}</p>
                  </div>

                  <button
                    type="button"
                    disabled={pickerYear >= currentYear}
                    onClick={() => setPickerYear((year) => Math.min(currentYear, year + 1))}
                    className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    ›
                  </button>
                </div>
              ) : (
                <div className="mb-3">
                  <p className="text-xs font-medium uppercase tracking-[0.16em] text-slate-400">
                    Select year
                  </p>
                </div>
              )}

              <button
                type="button"
                onClick={() => selectRange(getCurrentRangeKey(periodType))}
                className="mb-3 w-full rounded-xl bg-[#365561]/5 px-3 py-2 text-xs font-semibold text-[#365561] transition hover:bg-[#365561]/10"
              >
                Current {periodType}
              </button>

              <div
                className={[
                  "max-h-[310px] overflow-y-auto pr-1",
                  periodType === "week"
                    ? "grid grid-cols-4 gap-2"
                    : periodType === "year"
                      ? "grid grid-cols-3 gap-2"
                      : "grid grid-cols-4 gap-2",
                ].join(" ")}
              >
                {rangeItems.map((item) => {
                  const active = item.key === rangeKey;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => selectRange(item.key)}
                      className={[
                        "rounded-xl border px-2 py-2 text-xs font-semibold transition",
                        active
                          ? "border-[#365561] bg-[#365561] text-white"
                          : "border-slate-200 bg-white text-slate-600 hover:border-[#365561]/25 hover:bg-[#365561]/5 hover:text-[#365561]",
                      ].join(" ")}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function DashboardStickyRangeHeader({
  visible,
  periodLabel,
  periodType,
  rangeKey,
  loading,
  onPeriodTypeChange,
  onRangeChange,
}) {
  return (
    <AnimatePresence>
      {visible ? (
        <motion.div
          key="dashboard-sticky-range-header"
          initial={{ y: -70, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -70, opacity: 0 }}
          transition={{ duration: 0.32, ease: "easeInOut" }}
          className="fixed left-0 right-0 top-[78px] z-[90] hidden border-b border-slate-200/90 bg-white/95 shadow-[0_10px_30px_rgba(15,23,42,0.10)] backdrop-blur-xl md:block"
        >
          <div className="mx-auto flex h-[62px] max-w-[1600px] items-center justify-between gap-6 px-4 sm:px-6 lg:px-8">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#365561]/10 text-[#365561]">
                <BarChart3 className="h-4 w-4" />
              </div>

              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-900">
                  Business overview
                </p>
                <p className="truncate text-[11px] font-medium text-slate-400">
                  {periodLabel}
                </p>
              </div>
            </div>

            <DashboardRangeControls
              compact
              periodType={periodType}
              rangeKey={rangeKey}
              loading={loading}
              onPeriodTypeChange={onPeriodTypeChange}
              onRangeChange={onRangeChange}
            />
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

function LoadingSpinner({ label = "Loading..." }) {
  return (
    <div className="flex h-full min-h-[220px] items-center justify-center">
      <div className="flex flex-col items-center gap-3 rounded-3xl border border-slate-200 bg-white/70 px-8 py-6 shadow-[0_18px_45px_rgba(15,23,42,0.08)] backdrop-blur">
        <RefreshCw className="h-7 w-7 animate-spin text-[#365561]" />
        <p className="text-sm font-semibold text-slate-600">{label}</p>
      </div>
    </div>
  );
}

function EmptyState({ title = "No data found", description }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center">
      <p className="text-sm font-semibold text-slate-700">{title}</p>
      {description ? (
        <p className="mt-1 text-xs text-slate-500">{description}</p>
      ) : null}
    </div>
  );
}

function MetricCard({
  title,
  value,
  change,
  icon: Icon,
  accentClass = "bg-[#365561]/10 text-[#365561]",
  footer,
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="group relative overflow-hidden rounded-3xl border border-slate-200/80 bg-white p-5 shadow-[0_10px_30px_rgba(15,23,42,0.06)] transition hover:-translate-y-0.5 hover:shadow-[0_18px_42px_rgba(15,23,42,0.10)]"
    >
      <div className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full bg-[#365561]/5 blur-2xl transition group-hover:bg-[#365561]/10" />

      <div className="relative flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <p className="mt-2 truncate text-3xl font-semibold tracking-tight text-slate-900">
            {value}
          </p>
          <p className="mt-2 text-xs font-medium text-slate-500">{change}</p>
        </div>

        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${accentClass}`}
        >
          <Icon className="h-5 w-5" />
        </div>
      </div>

      {footer ? <div className="relative mt-4">{footer}</div> : null}
    </motion.div>
  );
}

function SectionCard({
  title,
  subtitle,
  info,
  right,
  children,
  className = "",
}) {
  const infoText =
    info ||
    subtitle ||
    `Dashboard section: ${String(title || "Overview")}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`rounded-3xl border border-slate-200/80 bg-white p-5 shadow-[0_10px_30px_rgba(15,23,42,0.06)] ${className}`}
    >
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <h3 className="min-w-0 text-base font-semibold leading-6 text-slate-900">{title}</h3>
            <div className="group/info relative z-40 flex h-6 w-5 shrink-0 items-center justify-center">
              <button
                type="button"
                aria-label={`About ${String(title || "this section")}: ${infoText}`}
                className="flex h-5 w-5 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-[#365561] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#365561]/40"
              >
                <Info className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
              <div role="tooltip" className="pointer-events-none absolute left-1/2 top-full mt-1.5 hidden w-[300px] -translate-x-1/2 rounded-2xl border border-[#365561]/25 bg-[#557784] px-3 py-2.5 text-[11px] font-normal leading-4 text-white shadow-[0_16px_36px_rgba(15,23,42,0.22)] group-hover/info:block group-focus-within/info:block">
                {infoText}
              </div>
            </div>
          </div>
          {subtitle ? (
            <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
          ) : null}
        </div>
        {right}
      </div>

      {children}
    </motion.div>
  );
}


function MiniProgress({ value, className = "bg-[#365561]" }) {
  const safeValue = Math.max(0, Math.min(Number(value || 0), 100));

  return (
    <div className="h-2 overflow-hidden rounded-full bg-slate-100">
      <div
        className={`h-full rounded-full ${className}`}
        style={{ width: `${safeValue}%` }}
      />
    </div>
  );
}

function InsightPill({ icon: Icon, label, value, tone = "brand" }) {
  const styles = {
    brand: "border-[#365561]/15 bg-[#365561]/5 text-[#365561]",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    sky: "border-sky-200 bg-sky-50 text-sky-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    violet: "border-violet-200 bg-violet-50 text-violet-700",
    rose: "border-rose-200 bg-rose-50 text-rose-700",
  };

  return (
    <div className={`rounded-2xl border px-4 py-3 ${styles[tone]}`}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4" />
        <p className="text-xs font-semibold">{label}</p>
      </div>
      <p className="mt-1 text-lg font-semibold">{value}</p>
    </div>
  );
}

function PipelineTrend({
  current = 0,
  previous = 0,
  periodType = "week",
  meaning = "positive_up",
}) {
  const currentValue = Number(current || 0);
  const previousValue = Number(previous || 0);
  const periodName = {
    week: "week",
    month: "month",
    quarter: "quarter",
    year: "year",
  }[periodType] || "period";

  let direction = "flat";
  let label = `No change vs previous ${periodName}`;

  if (previousValue === 0 && currentValue > 0) {
    direction = "up";
    label = `New vs previous ${periodName}`;
  } else if (previousValue > 0) {
    const change = ((currentValue - previousValue) / previousValue) * 100;

    if (Math.abs(change) >= 0.05) {
      direction = change > 0 ? "up" : "down";
      label = `${Math.abs(change).toLocaleString(undefined, {
        maximumFractionDigits: 1,
      })}% ${change > 0 ? "more" : "less"} vs previous ${periodName}`;
    }
  }

  const Icon = direction === "down" ? TrendingDown : TrendingUp;

  let className = "text-slate-500";
  if (direction !== "flat") {
    if (meaning === "neutral") {
      className = "text-sky-700";
    } else {
      const isPositive =
        meaning === "negative_up" ? direction === "down" : direction === "up";
      className = isPositive ? "text-emerald-700" : "text-rose-700";
    }
  }

  return (
    <div className={`flex items-center gap-1 text-[11px] font-semibold ${className}`}>
      {direction !== "flat" ? <Icon className="h-3.5 w-3.5 shrink-0" /> : null}
      <span>{label}</span>
    </div>
  );
}

function PipelineInsightCard({
  icon: Icon,
  label,
  value,
  tone = "brand",
  details,
  trend,
}) {
  const styles = {
    brand: "border-[#365561]/15 bg-[#365561]/5 text-[#365561]",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    sky: "border-sky-200 bg-sky-50 text-sky-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
  };

  return (
    <div
      className={`flex min-h-[148px] flex-col rounded-2xl border px-4 py-3 ${
        styles[tone] || styles.brand
      }`}
    >
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4" />
        <p className="text-xs font-semibold">{label}</p>
      </div>

      <p className="mt-1 text-lg font-semibold">{value}</p>

      {details ? (
        <div className="mt-1 space-y-0.5 text-[11px] leading-4 text-slate-600">
          {details}
        </div>
      ) : null}

      {trend ? <div className="mt-auto pt-2">{trend}</div> : null}
    </div>
  );
}

function ExecutiveSnapshotCard({
  periodLabel,
  kpis,
  financeSplit,
}) {
  const income = Number(kpis.income_this_month || 0);
  const margin = Number(kpis.margin_this_month || 0);
  const expenses = Number(kpis.expenses_this_month || 0);
  const baseExpenses = Number(financeSplit.base_expenses || 0);
  const extensionExpenses = Number(financeSplit.extension_expenses || 0);
  const marginRate = income > 0 ? (margin / income) * 100 : 0;

  return (
    <div className="relative overflow-hidden rounded-[2rem] border border-white/40 bg-gradient-to-br from-[#365561] via-[#446b78] to-[#1f323a] p-5 text-white shadow-[0_24px_70px_rgba(15,23,42,0.20)]">
      <div className="pointer-events-none absolute -right-16 -top-16 h-52 w-52 rounded-full bg-cyan-200/10 blur-3xl" />
      <div className="pointer-events-none absolute -left-12 bottom-0 h-40 w-40 rounded-full bg-white/5 blur-3xl" />

      <div className="relative">
        <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-semibold text-slate-100">
          <Sparkles className="h-3.5 w-3.5" />
          Executive snapshot
        </div>

        <h2 className="mt-4 max-w-xl text-2xl font-semibold leading-tight tracking-tight">
          {formatCurrency(income)} generated with {formatCurrency(margin)} net margin {periodLabel}.
        </h2>

        <p className="mt-2 max-w-xl text-sm leading-6 text-slate-200">
          Compact view of financial performance, margin quality, and operating cost split.
        </p>

        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <div className="rounded-3xl border border-white/15 bg-white/10 p-4 backdrop-blur">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-slate-300">Margin rate</p>
              <TrendingUp className="h-4 w-4 text-slate-200" />
            </div>

            <p className="mt-1 text-2xl font-semibold">
              {formatPercent(marginRate)}
            </p>

            <div className="mt-3 h-2 rounded-full bg-white/15">
              <div
                className="h-full rounded-full bg-white"
                style={{
                  width: `${Math.max(0, Math.min(marginRate, 100))}%`,
                }}
              />
            </div>
          </div>

          <div className="rounded-3xl border border-white/15 bg-white/10 p-4 backdrop-blur">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-slate-300">Expenses</p>
              <BadgeEuro className="h-4 w-4 text-slate-200" />
            </div>

            <p className="mt-1 text-2xl font-semibold">
              {formatCurrency(expenses)}
            </p>

            <p className="mt-2 text-xs leading-5 text-slate-300">
              Base: {formatCurrency(baseExpenses)} <br />
              Extensions: {formatCurrency(extensionExpenses)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ExecutiveFinanceSummary({ financeSplit = {} }) {
  const baseIncome = Number(financeSplit.base_income || 0);
  const extensionIncome = Number(financeSplit.extension_income || 0);
  const participatedCount = Number(financeSplit.participated_count || 0);
  const extensionCount = Number(financeSplit.extension_count || 0);

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <InsightPill
        icon={CircleDollarSign}
        label="Base income"
        value={formatCurrency(baseIncome)}
        tone="brand"
      />

      <InsightPill
        icon={Rocket}
        label="Extension income"
        value={formatCurrency(extensionIncome)}
        tone="violet"
      />

      <InsightPill
        icon={UsersRound}
        label="Participated"
        value={formatNumber(participatedCount)}
        tone="sky"
      />

      <InsightPill
        icon={ShieldCheck}
        label="Extensions"
        value={formatNumber(extensionCount)}
        tone="emerald"
      />
    </div>
  );
}

function OperationPulseCard({
  callsThisWeek = 0,
  longTermThisWeek = 0,
  upcomingCalls = 0,
  extensions = 0,
}) {
  return (
    <SectionCard
      title="Today’s operation pulse"
      subtitle="Fast indicators from the current dashboard payload"
      className="h-full"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <InsightPill
          icon={Video}
          label="Calls this week"
          value={formatNumber(callsThisWeek)}
          tone="brand"
        />

        <InsightPill
          icon={Rocket}
          label="LT wins this week"
          value={formatNumber(longTermThisWeek)}
          tone="emerald"
        />

        <InsightPill
          icon={CalendarClock}
          label="Upcoming calls"
          value={formatNumber(upcomingCalls)}
          tone="sky"
        />

        <InsightPill
          icon={ShieldCheck}
          label="Extensions"
          value={formatNumber(extensions)}
          tone="violet"
        />
      </div>
    </SectionCard>
  );
}

function DashboardHeroStats({
  periodLabel,
  periodType,
  bdPerformance,
  pipelineComparison,
  pipelineInsights,
  projectCategoryBreakdown,
  clientPerformance,
}) {
  return (
    <div className="mb-4">
      <PipelineHealthSection
        bdPerformance={bdPerformance}
        periodLabel={periodLabel}
        periodType={periodType}
        pipelineComparison={pipelineComparison}
        pipelineInsights={pipelineInsights}
        projectCategoryBreakdown={projectCategoryBreakdown}
        clientPerformance={clientPerformance}
      />
    </div>
  );
}

function ExecutiveDashboardData({
  periodLabel,
  kpis,
  financeSplit,
}) {
  return(
    <div className="grid gap-4 xl:grid-cols-[0.9fr_1.1fr] xl:items-stretch">
      <div className="grid gap-4 xl:auto-rows-min">
        <ExecutiveSnapshotCard
          periodLabel={periodLabel}
          kpis={kpis}
          financeSplit={financeSplit}
        />

        <ExecutiveFinanceSummary financeSplit={financeSplit} />
      </div>

      <FinanceDetailSection
        financeSplit={financeSplit}
        periodLabel={periodLabel}
      />
    </div>
  );
}

function PipelineHealthSection({
  bdPerformance = [],
  periodLabel,
  periodType = "week",
  pipelineComparison = {},
  pipelineInsights = {},
  projectCategoryBreakdown = [],
  clientPerformance = [],
}) {
  const totals = useMemo(() => {
    return bdPerformance.reduce(
      (acc, item) => {
        acc.not_started += Number(item.not_started || 0);
        acc.profiling += Number(item.profiling || 0);
        acc.waiting_input += Number(item.waiting_input || 0);
        acc.contracting += Number(item.contracting || 0);
        acc.closed_won += Number(item.closed_won || 0);
        acc.closed_lost += Number(item.closed_lost || 0);
        acc.other += Number(item.other || 0);
        acc.total_wins += Number(item.total_wins || 0);
        acc.total_projects += Number(item.total_projects || 0);
        return acc;
      },
      {
        not_started: 0,
        profiling: 0,
        waiting_input: 0,
        contracting: 0,
        closed_won: 0,
        closed_lost: 0,
        other: 0,
        total_wins: 0,
        total_projects: 0,
      }
    );
  }, [bdPerformance]);

  const activePipeline =
    totals.not_started +
    totals.profiling +
    totals.waiting_input +
    totals.contracting;

  const winRate =
    totals.total_projects > 0
      ? (totals.closed_won / totals.total_projects) * 100
      : 0;

  const previousPipeline = pipelineComparison?.previous || {};
  const currentPipeline = pipelineComparison?.current || {};

  const topCategories = useMemo(() => {
    return [...(projectCategoryBreakdown || [])]
      .filter((item) => Number(item.current_count || 0) > 0)
      .sort((a, b) => Number(b.current_count || 0) - Number(a.current_count || 0))
      .slice(0, 2);
  }, [projectCategoryBreakdown]);

  const lowConversionClients = useMemo(() => {
    return (clientPerformance || [])
      .map((item) => {
        const totalProjects = Number(item.total_projects || 0);
        const totalWins = Number(item.total_wins ?? item.closed_won ?? 0);
        const lostProjects = Number(item.closed_lost || 0);
        const conversionRate =
          totalProjects > 0 ? (totalWins / totalProjects) * 100 : 0;

        return {
          clientName: item.client_name || "Unknown client",
          totalProjects,
          lostProjects,
          conversionRate,
        };
      })
      .filter((item) => item.lostProjects > 0 && item.totalProjects > 0)
      .sort((a, b) => {
        // Worst conversion comes first. When two clients have the same win
        // percentage, the client with more requests ranks as the worse client.
        if (a.conversionRate !== b.conversionRate) {
          return a.conversionRate - b.conversionRate;
        }

        if (a.totalProjects !== b.totalProjects) {
          return b.totalProjects - a.totalProjects;
        }

        return a.clientName.localeCompare(b.clientName, undefined, {
          sensitivity: "base",
        });
      })
      .slice(0, 2);
  }, [clientPerformance]);

  const expertCallsFromWins = Number(
    pipelineInsights?.expert_calls_from_wins || 0
  );
  const longTermConsultantsFromWins = Number(
    pipelineInsights?.long_term_consultants_from_wins || 0
  );

  const pipelineData = [
    { name: "Not Started", value: totals.not_started, color: "#e83131" },
    { name: "Profiling", value: totals.profiling, color: "rgb(255, 192, 0)" },
    { name: "Waiting Input", value: totals.waiting_input, color: "rgb(0, 176, 80)" },
    { name: "Contracting", value: totals.contracting, color: "#0ea5e9" },
    { name: "Won", value: totals.closed_won, color: "rgb(198, 224, 180)" },
    { name: "Lost", value: totals.closed_lost, color: "rgb(250, 218, 196)" },
  ].filter((item) => item.value > 0);

  return (
    <SectionCard
      title="Pipeline health"
      subtitle={`Project status mix for ${periodLabel}`}
      right={
        <div className="rounded-2xl bg-[#365561]/5 px-3 py-2 text-xs font-semibold text-[#365561]">
          {formatPercent(winRate)} win rate
        </div>
      }
    >
      <div className="grid gap-5 lg:grid-cols-[0.95fr_1.05fr]">
        <div className="grid gap-3 sm:grid-cols-2">
          <PipelineInsightCard
            icon={BriefcaseBusiness}
            label="Total projects"
            value={formatNumber(totals.total_projects)}
            tone="brand"
            details={
              topCategories.length > 0 ? (
                <>
                  {topCategories.map((item) => (
                    <p key={item.category} className="truncate">
                      <span className="font-semibold text-slate-700">
                        {item.category}
                      </span>{" "}
                      · {formatNumber(item.current_count)}
                    </p>
                  ))}
                </>
              ) : null
            }
            trend={
              <PipelineTrend
                current={currentPipeline.total_projects ?? totals.total_projects}
                previous={previousPipeline.total_projects}
                periodType={periodType}
              />
            }
          />

          <PipelineInsightCard
            icon={Activity}
            label="Active pipeline"
            value={formatNumber(activePipeline)}
            tone="sky"
            trend={
              <PipelineTrend
                current={currentPipeline.active_pipeline ?? activePipeline}
                previous={previousPipeline.active_pipeline}
                periodType={periodType}
                meaning="neutral"
              />
            }
          />

          <PipelineInsightCard
            icon={Trophy}
            label="Won projects"
            value={formatNumber(totals.closed_won)}
            tone="emerald"
            details={
              <>
                <p>
                  <span className="font-semibold text-slate-700">
                    {formatNumber(expertCallsFromWins)}
                  </span>{" "}
                  Expert Calls
                </p>
                <p>
                  <span className="font-semibold text-slate-700">
                    {formatNumber(longTermConsultantsFromWins)}
                  </span>{" "}
                  Long-term consultants
                </p>
              </>
            }
            trend={
              <PipelineTrend
                current={currentPipeline.won ?? totals.closed_won}
                previous={previousPipeline.won}
                periodType={periodType}
              />
            }
          />

          <PipelineInsightCard
            icon={TrendingDown}
            label="Lost projects"
            value={formatNumber(totals.closed_lost)}
            tone="amber"
            details={
              lowConversionClients.length > 0 ? (
                <>
                  {lowConversionClients.map((client) => (
                    <p key={client.clientName} className="truncate">
                      <span className="font-semibold text-slate-700">
                        {client.clientName}
                      </span>{" "}
                      · {formatNumber(client.totalProjects)} requests ·{" "}
                      {formatPercent(client.conversionRate)} win
                    </p>
                  ))}
                </>
              ) : null
            }
            trend={
              <PipelineTrend
                current={currentPipeline.lost ?? totals.closed_lost}
                previous={previousPipeline.lost}
                periodType={periodType}
                meaning="negative_up"
              />
            }
          />
        </div>

        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
          {pipelineData.length === 0 ? (
            <EmptyState title="No pipeline data" />
          ) : (
            <div className="space-y-3">
              {pipelineData.map((item) => {
                const percent =
                  totals.total_projects > 0
                    ? (item.value / totals.total_projects) * 100
                    : 0;

                return (
                  <div key={item.name}>
                    <div className="mb-1.5 flex items-center justify-between text-xs">
                      <span className="font-semibold text-slate-700">
                        {item.name}
                      </span>
                      <span className="text-slate-500">
                        {item.value} · {formatPercent(percent)}
                      </span>
                    </div>

                    <div className="h-2 overflow-hidden rounded-full bg-white">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${percent}%`,
                          backgroundColor: item.color,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </SectionCard>
  );
}

function FinanceDetailSection({ financeSplit = {}, periodLabel }) {
  const income = Number(financeSplit.income || 0);
  const expenses = Number(financeSplit.expenses || 0);
  const margin = Number(financeSplit.margin || income - expenses);
  const marginRate = income > 0 ? (margin / income) * 100 : 0;

  const financeData = [
    { name: "Income", value: income },
    { name: "Expenses", value: expenses },
  ];

  const financeBars = [
    {
      name: "Base",
      income: Number(financeSplit.base_income || 0),
      expenses: Number(financeSplit.base_expenses || 0),
    },
    {
      name: "Extensions",
      income: Number(financeSplit.extension_income || 0),
      expenses: Number(financeSplit.extension_expenses || 0),
    },
  ];

  return (
    <SectionCard
      title="Finance intelligence"
      subtitle={`Income, expenses, margin, and base vs extension split for ${periodLabel}`}
      right={
        <div className="rounded-2xl bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">
          {formatPercent(marginRate)} margin
        </div>
      }
    >
      <div className="grid gap-5 xl:grid-cols-[0.85fr_1.15fr]">
        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
          <div className="h-[220px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Tooltip
                  formatter={(value) => [formatCurrency(value), "Amount"]}
                  contentStyle={{
                    borderRadius: 16,
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 12px 24px rgba(15,23,42,0.08)",
                  }}
                />
                <Pie
                  data={financeData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={66}
                  outerRadius={96}
                  paddingAngle={4}
                >
                  {financeData.map((entry, index) => (
                    <Cell
                      key={entry.name}
                      fill={pieColors[index % pieColors.length]}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl bg-white p-3">
              <p className="text-xs text-slate-500">Income</p>
              <p className="mt-1 text-lg font-semibold text-slate-900">
                {formatCurrency(income)}
              </p>
            </div>
            <div className="rounded-2xl bg-white p-3">
              <p className="text-xs text-slate-500">Expenses</p>
              <p className="mt-1 text-lg font-semibold text-slate-900">
                {formatCurrency(expenses)}
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-4">
          <div className="grid gap-3 md:grid-cols-3">
            <InsightPill
              icon={BadgeEuro}
              label="Net margin"
              value={formatCurrency(margin)}
              tone="emerald"
            />
            <InsightPill
              icon={UsersRound}
              label="Participated"
              value={formatNumber(financeSplit.participated_count)}
              tone="brand"
            />
            <InsightPill
              icon={ShieldCheck}
              label="Extensions"
              value={formatNumber(financeSplit.extension_count)}
              tone="violet"
            />
          </div>

          <div className="h-[245px] rounded-3xl border border-slate-200 bg-white p-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={financeBars} barGap={8}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="name"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: "#64748b", fontSize: 12 }}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: "#64748b", fontSize: 12 }}
                  tickFormatter={(value) => `€${Number(value) / 1000}k`}
                />
                <Tooltip
                  formatter={(value) => [formatCurrency(value), "Amount"]}
                  contentStyle={{
                    borderRadius: 16,
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 12px 24px rgba(15,23,42,0.08)",
                  }}
                />
                <Legend />
                <Bar dataKey="income" fill="#365561" radius={[8, 8, 0, 0]} />
                <Bar dataKey="expenses" fill="#94a3b8" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

function CustomBDTooltip({ active, payload }) {
  if (!active || !payload || payload.length === 0) return null;

  const data = payload[0]?.payload;
  if (!data) return null;

  const decided =
    Number(data.period_wins || 0) + Number(data.period_losses || 0);
  const decisionWinRate =
    decided > 0 ? (Number(data.period_wins || 0) / decided) * 100 : null;

  return (
    <div className="min-w-[220px] rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-[0_18px_40px_rgba(15,23,42,0.14)]">
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-slate-900">
          {data.business_developer}
        </p>

        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-600">
          {decisionWinRate === null ? "No decisions" : `${formatPercent(decisionWinRate)} decision win`}
        </span>
      </div>

      <div className="mb-2 h-px bg-slate-200" />

      <div className="space-y-[2px] text-xs leading-4 text-slate-600">
        <p>Not Started: {data.not_started}</p>
        <p>Profiling: {data.profiling}</p>
        <p>Waiting Input: {data.waiting_input}</p>
        <p>Contracting: {data.contracting}</p>
        <p className="text-green-600">Won: {data.closed_won}</p>
        <p className="text-red-500">Lost: {data.closed_lost}</p>
      </div>

      <div className="my-2 h-px bg-slate-200" />

      <div className="space-y-1 text-xs font-medium text-slate-700">
        <div className="flex justify-between gap-4">
          <span>Projects worked on</span>
          <span>{data.total_projects}</span>
        </div>
        <div className="flex justify-between gap-4 text-slate-500">
          <span>New · carried over</span>
          <span>{formatNumber(data.new_projects)} · {formatNumber(data.carried_over_projects)}</span>
        </div>
        <div className="flex justify-between gap-4 text-slate-500">
          <span>Wins decided in period</span>
          <span>{formatNumber(data.period_wins)}</span>
        </div>
      </div>

    </div>
  );
}

function UserActivityAxisTick({
  x,
  y,
  payload,
  items = [],
  nameKey,
  showInactiveLabel = true,
  italicInactive = true,
}) {
  const value = String(payload?.value || "");
  const item = (items || []).find(
    (entry) => String(entry?.[nameKey] || "") === value
  );
  const isActive = item?.is_active !== false;

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="middle"
        fill="#64748b"
        fontSize={12}
        fontStyle={!isActive && italicInactive ? "italic" : "normal"}
        opacity={isActive ? 1 : 0.5}
      >
        {!isActive && showInactiveLabel ? `${value} (inactive)` : value}
      </text>
    </g>
  );
}

function BusinessDeveloperPerformance({ bdPerformance = [], periodLabel }) {
  return (
    <SectionCard
      title="Business Developer Project Activity"
      subtitle={`Projects worked on during ${periodLabel}, including carry-over workload`}
      info="Shows projects owned by each Business Developer that were active during the selected period, including carry-over projects. Bars show each project's current status. 'Wins in period' counts projects marked Closed Won with a closure date inside the selected period (one win per project). Expert Call wins are consultant-level participation outcomes: a single won project can have several participating consultants, including participation long after the project's closure. Therefore, Expert Call wins can occur in a period with no new project wins."
      // right={
      //   <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-slate-500">
      //     <span className="inline-flex items-center gap-1.5">
      //       <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
      //       Not Started
      //     </span>
      //     <span className="inline-flex items-center gap-1.5">
      //       <span className="h-2.5 w-2.5 rounded-full bg-sky-400" />
      //       Profiling
      //     </span>
      //     <span className="inline-flex items-center gap-1.5">
      //       <span className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
      //       Waiting Input
      //     </span>
      //     <span className="inline-flex items-center gap-1.5">
      //       <span className="h-2.5 w-2.5 rounded-full bg-violet-400" />
      //       Contracting
      //     </span>
      //     <span className="inline-flex items-center gap-1.5">
      //       <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
      //       Won
      //     </span>
      //     <span className="inline-flex items-center gap-1.5">
      //       <span className="h-2.5 w-2.5 rounded-full bg-orange-400" />
      //       Lost
      //     </span>
      //   </div>
      // }
    >
      {bdPerformance.length === 0 ? (
        <EmptyState title="No BD performance data" />
      ) : (
        <div className="h-[380px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={bdPerformance} barGap={10}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis
                dataKey="business_developer"
                tickLine={false}
                axisLine={false}
                tick={(props) => (
                  <UserActivityAxisTick
                    {...props}
                    items={bdPerformance}
                    nameKey="business_developer"
                    showInactiveLabel={false}
                    italicInactive={false}
                  />
                )}
              />
              <YAxis
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                tick={{ fill: "#64748b", fontSize: 12 }}
              />
              <Tooltip content={<CustomBDTooltip />} />
              <Legend 
                wrapperStyle={{
                  fontSize: "10px",
                  color: "#64748b",
                  fontWeight: 500,
                  paddingTop: "12px"
                }}
              />
              <Bar
                dataKey="not_started"
                stackId="projects"
                fill="#e83131"
                name="Not Started"
              />
              <Bar
                dataKey="profiling"
                stackId="projects"
                fill="rgb(255, 192, 0)"
                name="Profiling"
              />
              <Bar
                dataKey="waiting_input"
                stackId="projects"
                fill="rgb(0, 176, 80)"
                name="Waiting Input"
              />
              <Bar
                dataKey="contracting"
                stackId="projects"
                fill="#0ea5e9"
                name="Contracting"
              />
              <Bar
                dataKey="closed_won"
                stackId="projects"
                fill="rgb(198, 224, 180)"
                name="Won"
              />
              <Bar
                dataKey="closed_lost"
                stackId="projects"
                fill="rgb(250, 218, 196)"
                name="Lost"
              />
              <Line
                type="monotone"
                dataKey="total_wins"
                stroke="#365561"
                strokeWidth={3}
                dot={{ r: 4, fill: "#365561" }}
                name="Wins in period"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </SectionCard>
  );
}

function ProjectCategoryTooltip({
  active,
  payload,
  currentLabel,
  previousLabel,
}) {
  if (!active || !payload || payload.length === 0) return null;

  const data = payload[0]?.payload;
  if (!data) return null;

  const currentCount = Number(data.current_count || 0);
  const previousCount = Number(data.previous_count || 0);
  const deltaCount = currentCount - previousCount;
  const changePercent =
    previousCount > 0 ? (deltaCount / previousCount) * 100 : null;

  const comparisonText = (() => {
    if (previousCount === 0 && currentCount > 0) {
      return `New vs ${previousLabel}`;
    }

    if (previousCount === 0 && currentCount === 0) {
      return `No change vs ${previousLabel}`;
    }

    if (deltaCount === 0) {
      return `0% change vs ${previousLabel}`;
    }

    return `${formatPercent(Math.abs(changePercent))} ${
      deltaCount > 0 ? "more" : "less"
    } than ${previousLabel}`;
  })();

  return (
    <div className="min-w-[220px] rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-[0_18px_40px_rgba(15,23,42,0.14)]">
      <p className="truncate text-sm font-semibold text-slate-900">
        {data.category}
      </p>

      <div className="mt-2 space-y-1 text-xs text-slate-600">
        <div className="flex justify-between gap-4">
          <span>{currentLabel}</span>
          <span className="font-semibold text-[#365561]">
            {formatNumber(currentCount)}
          </span>
        </div>

        <div className="flex justify-between gap-4">
          <span>{previousLabel}</span>
          <span className="font-semibold text-slate-600">
            {formatNumber(previousCount)}
          </span>
        </div>

        <div className="flex justify-between gap-4 border-t border-slate-100 pt-1">
          <span>Difference</span>
          <span
            className={[
              "font-semibold",
              deltaCount >= 0 ? "text-emerald-600" : "text-rose-600",
            ].join(" ")}
          >
            {deltaCount >= 0 ? "+" : ""}
            {formatNumber(deltaCount)}
          </span>
        </div>
      </div>

      <div className="my-2 h-px bg-slate-200" />

      <p
        className={[
          "text-xs font-semibold",
          deltaCount > 0
            ? "text-emerald-600"
            : deltaCount < 0
              ? "text-rose-600"
              : "text-slate-500",
        ].join(" ")}
      >
        {comparisonText}
      </p>
    </div>
  );
}

function CategoryMomentumAxisTick({ x, y, payload, slotWidth = 140 }) {
  const value = String(payload?.value || "");
  const maxChars = Math.max(8, Math.floor(Number(slotWidth || 140) / 7));
  const label =
    value.length > maxChars
      ? `${value.slice(0, Math.max(4, maxChars - 1))}…`
      : value;

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="middle"
        fill="#64748b"
        fontSize={12}
      >
        {label}
      </text>
    </g>
  );
}

function CategoryPerformanceMetric({
  label,
  value,
  detail,
  tone = "default",
  detailTone = "default",
  badge = null,
}) {
  const toneClass = {
    default: "text-slate-900",
    brand: "text-[#365561]",
    positive: "text-emerald-600",
    negative: "text-rose-600",
    info: "text-sky-600",
  }[tone] || "text-slate-900";

  const detailToneClass = {
    default: "text-slate-500",
    positive: "text-emerald-600",
    negative: "text-rose-600",
    info: "text-sky-600",
    brand: "text-[#365561]",
  }[detailTone] || "text-slate-500";

  return (
    <div className="relative rounded-2xl border border-slate-100 bg-slate-50/80 p-3">
      <div className="flex items-start justify-between gap-2">
        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
          {label}
        </p>
        {badge}
      </div>
      <p className={`mt-1 text-lg font-bold leading-6 ${toneClass}`}>
        {value}
      </p>
      {detail ? (
        <p className={`mt-1 text-[11px] leading-4 ${detailToneClass}`}>{detail}</p>
      ) : null}
    </div>
  );
}

function CategoryStageRow({ label, value, total, color }) {
  const count = Number(value || 0);
  const denominator = Number(total || 0);
  const percentage = denominator > 0 ? (count / denominator) * 100 : 0;

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-3 text-xs">
        <span className="inline-flex items-center gap-2 font-medium text-slate-600">
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: color }}
          />
          {label}
        </span>
        <span className="font-semibold text-slate-900">
          {formatNumber(count)}
          <span className="ml-1 font-normal text-slate-400">
            · {formatPercent(percentage)}
          </span>
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{ width: `${Math.max(0, Math.min(100, percentage))}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

function CategoryPerformanceDrawer({
  category,
  periodLabel,
  onClose,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
}) {
  useEffect(() => {
    if (!category) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [category]);

  if (!category) return null;

  const requests = Number(category.project_count || 0);
  const totalProfiles = Number(category.total_profiles || 0);
  const wonProjects = Number(category.closed_won || 0);
  const lostProjects = Number(category.closed_lost || 0);

  const roundedAverage = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return "—";
    }
    return formatNumber(Math.round(Number(value)));
  };

  const closeAverage =
    category.average_close_days === null || category.average_close_days === undefined
      ? "—"
      : roundedAverage(category.average_close_days);
  const deliveryAverage =
    category.average_delivery_days === null || category.average_delivery_days === undefined
      ? "—"
      : roundedAverage(category.average_delivery_days);
  const wonProfileAverage = roundedAverage(category.average_profiles_won_project);
  const lostProfileAverage = roundedAverage(category.average_profiles_lost_project);
  const globalWonProfileAverage = roundedAverage(
    category.global_average_profiles_won_project
  );

  const wonVsGlobal =
    category.won_profiles_vs_global_percentage === null ||
    category.won_profiles_vs_global_percentage === undefined
      ? null
      : Number(category.won_profiles_vs_global_percentage);
  const lostVsWon =
    category.lost_profiles_vs_won_percentage === null ||
    category.lost_profiles_vs_won_percentage === undefined
      ? null
      : Number(category.lost_profiles_vs_won_percentage);

  const effortRank = Number(category.won_profile_effort_rank || 0);
  const effortRankTotal = Number(category.won_profile_effort_rank_total || 0);
  const ordinal = (value) => {
    const mod100 = value % 100;
    if (mod100 >= 11 && mod100 <= 13) return `${value}th`;
    const mod10 = value % 10;
    if (mod10 === 1) return `${value}st`;
    if (mod10 === 2) return `${value}nd`;
    if (mod10 === 3) return `${value}rd`;
    return `${value}th`;
  };

  const effortRankBadge = effortRank > 0 ? (
    <span
      className={[
        "shrink-0 rounded-full px-2 py-1 text-[9px] font-bold uppercase tracking-[0.08em]",
        effortRank === 1
          ? "bg-rose-100 text-rose-700"
          : effortRank === 2
            ? "bg-orange-100 text-orange-700"
            : effortRank === 3
              ? "bg-amber-100 text-amber-700"
              : "bg-slate-200/70 text-slate-600",
      ].join(" ")}
    >
      {effortRank === 1 ? "Highest effort" : `${ordinal(effortRank)} highest effort`}
    </span>
  ) : null;

  const stageRows = [
    { key: "not_started", label: "Not Started", color: "#e83131" },
    { key: "profiling", label: "Profiling", color: "rgb(255, 192, 0)" },
    { key: "waiting_input", label: "Waiting Input", color: "rgb(0, 176, 80)" },
    { key: "contracting", label: "Contracting", color: "#0ea5e9" },
    { key: "closed_won", label: "Won", color: "rgb(198, 224, 180)" },
    { key: "closed_lost", label: "Lost", color: "rgb(250, 218, 196)" },
  ];

  return (
    <AnimatePresence>
      <>
        <motion.button
          type="button"
          aria-label="Close category KPI drawer"
          className="fixed bottom-0 left-0 right-0 top-[78px] z-[120] bg-slate-950/20 backdrop-blur-[1px]"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />

        <motion.aside
          className="fixed bottom-0 right-0 top-[78px] z-[130] flex w-full flex-col border-l border-slate-200 bg-slate-50 shadow-[-24px_0_70px_rgba(15,23,42,0.18)] sm:w-[620px] xl:w-[720px]"
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ duration: 0.32, ease: "easeInOut" }}
        >
          <div className="border-b border-slate-200 bg-white px-5 py-4 sm:px-6">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-lg font-semibold leading-6 text-slate-900">
                  {category.category || "Uncategorized"}
                </p>
                <p className="mt-0.5 text-xs font-medium text-[#365561]">
                  Category Insights
                </p>
                <p className="mt-1 text-xs text-slate-400">{periodLabel}</p>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-4 flex items-center justify-between gap-3">
              <button
                type="button"
                disabled={!hasPrevious}
                onClick={onPrevious}
                className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition hover:border-[#365561]/20 hover:text-[#365561] disabled:cursor-not-allowed disabled:opacity-35"
              >
                <ChevronLeft className="h-4 w-4" /> Previous
              </button>
              <span className="text-[11px] font-medium text-slate-400">Browse categories</span>
              <button
                type="button"
                disabled={!hasNext}
                onClick={onNext}
                className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition hover:border-[#365561]/20 hover:text-[#365561] disabled:cursor-not-allowed disabled:opacity-35"
              >
                Next <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
              <CategoryPerformanceMetric
                label="Project requests"
                value={formatNumber(requests)}
                detail={`${formatPercent(category.portfolio_share_percentage)} of selected-period requests`}
                tone="brand"
              />
              <CategoryPerformanceMetric
                label="Win rate"
                value={formatPercent(category.win_rate)}
                detail={`${formatNumber(wonProjects)} won · ${formatNumber(requests)} total requests`}
                tone="positive"
              />
              <CategoryPerformanceMetric
                label="Avg. Profiles"
                value={
                  <>
                    {roundedAverage(category.average_profiles_per_project)}
                    <span className="ml-1.5 text-[10px] font-medium text-slate-400">
                      per request
                    </span>
                  </>
                }
                detail={`${formatNumber(totalProfiles)} profiles total`}
              />
              <CategoryPerformanceMetric
                label="Participation yield"
                value={formatPercent(category.participation_yield)}
                detail={`${formatNumber(category.participated_profiles)} / ${formatNumber(totalProfiles)} profiles participated`}
                tone="info"
              />
              <CategoryPerformanceMetric
                label="Average close time"
                value={
                  <>
                    {closeAverage}
                    {closeAverage !== "—" ? (
                      <span className="ml-1.5 text-[10px] font-medium text-slate-400">days</span>
                    ) : null}
                  </>
                }
                detail={`${formatNumber(category.close_samples)} closed · ${formatNumber(category.close_expert_call_requests)} Expert Calls · ${formatNumber(category.close_long_term_requests)} Long-Term`}
              />
              <CategoryPerformanceMetric
                label="Average first delivery"
                value={
                  <>
                    {deliveryAverage}
                    {deliveryAverage !== "—" ? (
                      <span className="ml-1.5 text-[10px] font-medium text-slate-400">days</span>
                    ) : null}
                  </>
                }
                detail={`${formatNumber(category.delivery_signal_projects)} / ${formatNumber(requests)} requests · ${formatPercent(category.delivery_coverage)} coverage`}
                tone="info"
              />
            </div>

            <div className="mt-5 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Project stage distribution</p>
                  <p className="mt-0.5 text-xs text-slate-400">
                    {formatNumber(requests)} requests · current stage mix
                  </p>
                </div>
                <BarChart3 className="h-5 w-5 text-[#365561]" />
              </div>

              <div className="space-y-3.5">
                {stageRows.map((stage) => (
                  <CategoryStageRow
                    key={stage.key}
                    label={stage.label}
                    value={category[stage.key]}
                    total={requests}
                    color={stage.color}
                  />
                ))}
                {Number(category.other || 0) > 0 ? (
                  <CategoryStageRow
                    label="Other"
                    value={category.other}
                    total={requests}
                    color="#94a3b8"
                  />
                ) : null}
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-slate-50 px-3 py-2 text-[11px]">
                <span className="font-medium text-slate-500">Closed requests</span>
                <span className="font-semibold text-slate-800">
                  {formatNumber(category.decided_projects)} / {formatNumber(requests)} · {formatPercent(category.decision_rate)}
                </span>
              </div>
            </div>

            <div className="mt-4 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex items-center gap-2">
                <UsersRound className="h-4 w-4 text-[#365561]" />
                <div>
                  <p className="text-sm font-semibold text-slate-900">Profile effort and outcome</p>
                  <p className="mt-0.5 text-xs text-slate-400">Sourcing intensity and profile conversion by outcome</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2.5">
                <CategoryPerformanceMetric
                  label="All requests"
                  value={
                    <>
                      {roundedAverage(category.average_profiles_per_project)}
                      <span className="ml-1.5 text-[10px] font-medium text-slate-400">
                        profiles per request
                      </span>
                    </>
                  }
                  detail={`${formatNumber(totalProfiles)} profiles total`}
                  tone="brand"
                />

                <CategoryPerformanceMetric
                  label="Won requests"
                  value={
                    <>
                      {wonProfileAverage}
                      <span className="ml-1.5 text-[10px] font-medium text-slate-400">
                        profiles per request
                      </span>
                    </>
                  }
                  badge={effortRankBadge}
                  detail={
                    wonVsGlobal === null
                      ? `Global average: ${globalWonProfileAverage} profiles per Won request`
                      : `${formatPercent(Math.abs(wonVsGlobal))} ${wonVsGlobal >= 0 ? "higher" : "lower"} than global average · global ${globalWonProfileAverage} / request`
                  }
                  detailTone={
                    wonVsGlobal === null
                      ? "default"
                      : wonVsGlobal > 0
                        ? "negative"
                        : wonVsGlobal < 0
                          ? "positive"
                          : "default"
                  }
                />

                <CategoryPerformanceMetric
                  label="Lost requests"
                  value={
                    <>
                      {lostProfileAverage}
                      <span className="ml-1.5 text-[10px] font-medium text-slate-400">
                        profiles per request
                      </span>
                    </>
                  }
                  detail={
                    lostVsWon === null
                      ? "No Won-request benchmark available for this category"
                      : `${formatPercent(Math.abs(lostVsWon))} ${lostVsWon >= 0 ? "more" : "less"} than this category's Won-request average`
                  }
                  detailTone={
                    lostVsWon === null
                      ? "default"
                      : lostVsWon < 0
                        ? "negative"
                        : lostVsWon > 0
                          ? "positive"
                          : "default"
                  }
                />

                <CategoryPerformanceMetric
                  label="Participation yield"
                  value={formatPercent(category.participation_yield)}
                  detail={`${formatNumber(category.participated_profiles)} / ${formatNumber(totalProfiles)} profiles reached Participated`}
                  tone="info"
                />
              </div>
            </div>

            <div className="mt-4 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex items-center gap-2">
                <Mail className="h-4 w-4 text-[#365561]" />
                <div>
                  <p className="text-sm font-semibold text-slate-900">Communication and staffing</p>
                  <p className="mt-0.5 text-xs text-slate-400">Average workload required to run requests in this category</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2.5">
                <CategoryPerformanceMetric
                  label="Emails / request"
                  value={roundedAverage(category.average_outgoing_emails_per_project)}
                  detail={`${formatNumber(category.total_outgoing_emails)} emails total · ${formatPercent(category.email_coverage)} request coverage`}
                  tone="brand"
                />

                <CategoryPerformanceMetric
                  label="Supporters / request"
                  value={roundedAverage(category.average_supporters_per_project)}
                  detail={`${formatNumber(category.supporter_above_average_projects)} requests above average · max ${formatNumber(category.max_supporters_per_project)} supporters in ${formatNumber(category.max_supporters_project_count)} request${Number(category.max_supporters_project_count || 0) === 1 ? "" : "s"}`}
                />

                <CategoryPerformanceMetric
                  label="Requests with email activity"
                  value={formatPercent(category.email_coverage)}
                  detail={`${formatNumber(category.email_projects)} / ${formatNumber(requests)} requests`}
                  tone="info"
                />

                <CategoryPerformanceMetric
                  label="Requests with supporters"
                  value={formatPercent(category.supporter_coverage)}
                  detail={`${formatNumber(category.staffed_projects)} / ${formatNumber(requests)} requests`}
                />
              </div>
            </div>

            <div className="mt-4 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex items-center gap-2">
                <Clock3 className="h-4 w-4 text-[#365561]" />
                <div>
                  <p className="text-sm font-semibold text-slate-900">Speed and delivery</p>
                  <p className="mt-0.5 text-xs text-slate-400">How quickly category requests move from creation to output and closure</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2.5">
                <CategoryPerformanceMetric
                  label="Time to close"
                  value={
                    <>
                      {closeAverage}
                      {closeAverage !== "—" ? (
                        <span className="ml-1.5 text-[10px] font-medium text-slate-400">days</span>
                      ) : null}
                    </>
                  }
                  detail={`${formatNumber(category.close_samples)} closed · ${formatNumber(category.close_expert_call_requests)} Expert Calls · ${formatNumber(category.close_long_term_requests)} Long-Term`}
                />

                <CategoryPerformanceMetric
                  label="First delivery"
                  value={
                    <>
                      {deliveryAverage}
                      {deliveryAverage !== "—" ? (
                        <span className="ml-1.5 text-[10px] font-medium text-slate-400">days</span>
                      ) : null}
                    </>
                  }
                  detail={`${formatNumber(category.delivery_signal_projects)} / ${formatNumber(requests)} requests · ${formatPercent(category.delivery_coverage)} coverage`}
                  tone="info"
                />
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-slate-100 bg-white px-4 py-3 text-[10px] leading-4 text-slate-400 shadow-sm">
              Profiles = ProjectExpert · Emails = matched outgoing ContactLog email activity · Multi-category requests count in each assigned category.
            </div>
          </div>
        </motion.aside>
      </>
    </AnimatePresence>
  );
}

function ProjectCategorySection({
  categories = [],
  categoryInsights = [],
  periodLabel,
  periodType,
  rangeKey,
}) {
  const [selectedCategoryKey, setSelectedCategoryKey] = useState("");

  const chartData = useMemo(() => {
    return (categories || [])
      .map((item, index) => ({
        category: item.category || "Uncategorized",
        current_count: Number(item.current_count || item.project_count || 0),
        previous_count: Number(item.previous_count || 0),
        current_percentage: Number(item.current_percentage || item.percentage || 0),
        previous_percentage: Number(item.previous_percentage || 0),
        delta_count: Number(item.delta_count || 0),
        color: categoryColors[index % categoryColors.length],
      }))
      .filter((item) => item.current_count > 0 || item.previous_count > 0)
      .sort((a, b) =>
        a.category.localeCompare(b.category, undefined, { sensitivity: "base" })
      );
  }, [categories]);

  const performanceData = useMemo(() => {
    return (categoryInsights || []).map((item) => ({
      ...item,
      category: item.category || "Uncategorized",
      project_count: Number(item.project_count || 0),
      portfolio_share_percentage: Number(item.portfolio_share_percentage || 0),
      not_started: Number(item.not_started || 0),
      profiling: Number(item.profiling || 0),
      waiting_input: Number(item.waiting_input || 0),
      contracting: Number(item.contracting || 0),
      closed_won: Number(item.closed_won || 0),
      closed_lost: Number(item.closed_lost || 0),
      other: Number(item.other || 0),
      decided_projects: Number(item.decided_projects || 0),
      decision_rate: Number(item.decision_rate || 0),
      win_rate: Number(item.win_rate || 0),
      total_profiles: Number(item.total_profiles || 0),
      average_profiles_per_project: Number(item.average_profiles_per_project || 0),
      won_profiles_total: Number(item.won_profiles_total || 0),
      lost_profiles_total: Number(item.lost_profiles_total || 0),
      won_profiles_percentage: Number(item.won_profiles_percentage || 0),
      lost_profiles_percentage: Number(item.lost_profiles_percentage || 0),
      participated_profiles: Number(item.participated_profiles || 0),
      participation_yield: Number(item.participation_yield || 0),
      close_samples: Number(item.close_samples || 0),
      close_expert_call_requests: Number(item.close_expert_call_requests || 0),
      close_long_term_requests: Number(item.close_long_term_requests || 0),
      global_won_project_count: Number(item.global_won_project_count || 0),
      global_won_profiles_total: Number(item.global_won_profiles_total || 0),
      won_profile_effort_rank: Number(item.won_profile_effort_rank || 0),
      won_profile_effort_rank_total: Number(item.won_profile_effort_rank_total || 0),
      supporter_above_average_projects: Number(item.supporter_above_average_projects || 0),
      max_supporters_per_project: Number(item.max_supporters_per_project || 0),
      max_supporters_project_count: Number(item.max_supporters_project_count || 0),
      total_outgoing_emails: Number(item.total_outgoing_emails || 0),
      average_outgoing_emails_per_project: Number(item.average_outgoing_emails_per_project || 0),
      email_projects: Number(item.email_projects || 0),
      email_coverage: Number(item.email_coverage || 0),
      supporter_assignments: Number(item.supporter_assignments || 0),
      average_supporters_per_project: Number(item.average_supporters_per_project || 0),
      staffed_projects: Number(item.staffed_projects || 0),
      supporter_coverage: Number(item.supporter_coverage || 0),
      delivery_signal_projects: Number(item.delivery_signal_projects || 0),
      delivery_coverage: Number(item.delivery_coverage || 0),
    }));
  }, [categoryInsights]);

  const selectedIndex = performanceData.findIndex(
    (item) => item.category === selectedCategoryKey
  );
  const selectedCategory = selectedIndex >= 0 ? performanceData[selectedIndex] : null;
  const globalWonProfilesAverage = useMemo(() => {
    const firstAvailable = performanceData.find(
      (item) =>
        item.global_average_profiles_won_project !== null &&
        item.global_average_profiles_won_project !== undefined
    );
    return firstAvailable?.global_average_profiles_won_project ?? null;
  }, [performanceData]);

  useEffect(() => {
    if (
      selectedCategoryKey &&
      !performanceData.some((item) => item.category === selectedCategoryKey)
    ) {
      setSelectedCategoryKey("");
    }
  }, [performanceData, selectedCategoryKey]);

  const previousRangeKey = useMemo(
    () => getPreviousRangeKey(periodType, rangeKey),
    [periodType, rangeKey]
  );

  const currentComparisonLabel = useMemo(
    () => getCompactRangeLabel(periodType, rangeKey),
    [periodType, rangeKey]
  );

  const previousComparisonLabel = useMemo(
    () => getCompactRangeLabel(periodType, previousRangeKey),
    [periodType, previousRangeKey]
  );

  const renderPeriodLabel = (label) => ({ x, y, width, value }) => {
    if (!Number(value)) return null;

    return (
      <text
        x={Number(x || 0) + Number(width || 0) / 2}
        y={Math.max(13, Number(y || 0) - 7)}
        textAnchor="middle"
        fill="#64748b"
        fontSize={10}
        fontWeight={700}
      >
        {label}
      </text>
    );
  };

  const compactStageItems = [
    { key: "not_started", label: "Not Started", color: "#e83131" },
    { key: "profiling", label: "Profiling", color: "rgb(255, 192, 0)" },
    { key: "waiting_input", label: "Waiting", color: "rgb(0, 176, 80)" },
    { key: "contracting", label: "Contracting", color: "#0ea5e9" },
    { key: "closed_won", label: "Won", color: "rgb(198, 224, 180)" },
    { key: "closed_lost", label: "Lost", color: "rgb(250, 218, 196)" },
  ];

  return (
    <div className="grid gap-4">
      <SectionCard
        title="Category momentum"
        subtitle={`${previousComparisonLabel} vs ${currentComparisonLabel} category comparison`}
      >
        {chartData.length === 0 ? (
          <EmptyState title="No category comparison data" />
        ) : (
          <div className="h-[380px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                barGap={18}
                barCategoryGap="24%"
                margin={{ top: 28, right: 8, left: 0, bottom: 24 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#e2e8f0"
                  vertical={false}
                />

                <XAxis
                  dataKey="category"
                  interval={0}
                  tickLine={false}
                  axisLine={false}
                  tick={(props) => (
                    <CategoryMomentumAxisTick {...props} slotWidth={140} />
                  )}
                />

                <YAxis
                  allowDecimals={false}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: "#64748b", fontSize: 12 }}
                />

                <Tooltip
                  cursor={{ fill: "rgba(54,85,97,0.025)" }}
                  content={
                    <ProjectCategoryTooltip
                      currentLabel={currentComparisonLabel}
                      previousLabel={previousComparisonLabel}
                    />
                  }
                />

                <Legend
                  wrapperStyle={{
                    fontSize: "10px",
                    color: "#64748b",
                    fontWeight: 500,
                    paddingTop: "12px",
                  }}
                />

                <Bar
                  dataKey="previous_count"
                  name={previousComparisonLabel}
                  fill="#94a3b8"
                  barSize={36}
                  label={renderPeriodLabel(previousComparisonLabel)}
                />

                <Bar
                  dataKey="current_count"
                  name={currentComparisonLabel}
                  fill="#365561"
                  barSize={36}
                  label={renderPeriodLabel(currentComparisonLabel)}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </SectionCard>

      <SectionCard
        title="Category insights"
        subtitle={`Key category KPIs for ${periodLabel}`}
        right={
          <div className="grid gap-1.5 text-right">
            <div className="rounded-2xl bg-[#365561]/5 px-3 py-2 text-xs font-semibold text-[#365561]">
              {formatNumber(performanceData.length)} categories · click a card for details
            </div>
            {globalWonProfilesAverage !== null ? (
              <div className="text-[10px] font-medium text-slate-400">
                Global Won average · {formatNumber(Math.round(Number(globalWonProfilesAverage)))} profiles / request
              </div>
            ) : null}
          </div>
        }
      >
        {performanceData.length === 0 ? (
          <EmptyState
            title="No category KPI data"
            description="Add project_category_insights to the dashboard overview payload."
          />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {performanceData.map((item) => {
              const isSelected = selectedCategoryKey === item.category;
              const closeAverage =
                item.average_close_days === null || item.average_close_days === undefined
                  ? "—"
                  : formatNumber(Math.round(Number(item.average_close_days)));
              const roundedProfilesAverage = formatNumber(
                Math.round(Number(item.average_profiles_per_project || 0))
              );

              return (
                <button
                  key={item.category}
                  type="button"
                  onClick={() => setSelectedCategoryKey(item.category)}
                  className={[
                    "group relative overflow-hidden rounded-3xl border bg-white p-4 text-left shadow-sm transition duration-200 focus:outline-none",
                    isSelected
                      ? "border-[#365561]/40 bg-[#365561]/5 shadow-[0_14px_35px_rgba(54,85,97,0.12)]"
                      : "border-slate-200 hover:-translate-y-0.5 hover:border-[#365561]/25 hover:shadow-[0_14px_32px_rgba(15,23,42,0.08)]",
                  ].join(" ")}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="line-clamp-2 text-sm font-semibold leading-5 text-slate-900">
                        {item.category}
                      </p>
                      <p className="mt-1 text-[11px] text-slate-400">
                        {formatNumber(item.project_count)} requests · {formatPercent(item.portfolio_share_percentage)} share
                      </p>
                    </div>
                    <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-[#365561]" />
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <div className="rounded-2xl bg-slate-50 px-3 py-2.5">
                      <p className="text-[10px] uppercase tracking-[0.1em] text-slate-400">Requests</p>
                      <p className="mt-1 text-base font-semibold text-slate-900">{formatNumber(item.project_count)}</p>
                    </div>
                    <div className="rounded-2xl bg-emerald-50/70 px-3 py-2.5">
                      <p className="text-[10px] uppercase tracking-[0.1em] text-slate-400">Win rate</p>
                      <p className="mt-1 text-base font-semibold text-emerald-700">{formatPercent(item.win_rate)}</p>
                    </div>
                    <div className="rounded-2xl bg-[#365561]/5 px-3 py-2.5">
                      <p className="text-[10px] uppercase tracking-[0.1em] text-slate-400">Avg. Profiles</p>
                      <p className="mt-1 text-sm font-semibold text-[#365561]">
                        {roundedProfilesAverage}
                        <span className="ml-1 text-[9px] font-medium text-slate-400">per request</span>
                      </p>
                    </div>
                    <div className="rounded-2xl bg-slate-50 px-3 py-2.5">
                      <p className="text-[10px] uppercase tracking-[0.1em] text-slate-400">Avg close</p>
                      <p className="mt-1 text-sm font-semibold text-slate-800">
                        {closeAverage}
                        {closeAverage !== "—" ? (
                          <span className="ml-1 text-[9px] font-medium text-slate-400">days</span>
                        ) : null}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 border-t border-slate-100 pt-3">
                    <p className="mb-2 text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-400">Stage mix</p>
                    <div className="flex flex-wrap gap-1.5">
                      {compactStageItems.map((stage) => (
                        <span
                          key={stage.key}
                          className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2 py-1 text-[9px] font-semibold text-slate-600"
                        >
                          <span
                            className="h-1.5 w-1.5 rounded-full"
                            style={{ backgroundColor: stage.color }}
                          />
                          {stage.label} {formatNumber(item[stage.key])}
                        </span>
                      ))}
                      {Number(item.other || 0) > 0 ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2 py-1 text-[9px] font-semibold text-slate-600">
                          <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                          Other {formatNumber(item.other)}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-3 text-[10px] text-slate-400">
                    <span>Participation {formatPercent(item.participation_yield)}</span>
                    <span>Delivery {formatPercent(item.delivery_coverage)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </SectionCard>

      <CategoryPerformanceDrawer
        category={selectedCategory}
        periodLabel={periodLabel}
        onClose={() => setSelectedCategoryKey("")}
        onPrevious={() => {
          if (selectedIndex > 0) {
            setSelectedCategoryKey(performanceData[selectedIndex - 1].category);
          }
        }}
        onNext={() => {
          if (selectedIndex >= 0 && selectedIndex < performanceData.length - 1) {
            setSelectedCategoryKey(performanceData[selectedIndex + 1].category);
          }
        }}
        hasPrevious={selectedIndex > 0}
        hasNext={selectedIndex >= 0 && selectedIndex < performanceData.length - 1}
      />
    </div>
  );
}

function ProjectCategoryDistributionTooltip({ active, payload }) {
  if (!active || !payload || payload.length === 0) return null;

  const data = payload[0]?.payload;
  if (!data) return null;

  return (
    <div className="min-w-[200px] rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-[0_18px_40px_rgba(15,23,42,0.14)]">
      <p className="truncate text-sm font-semibold text-slate-900">
        {data.category}
      </p>

      <div className="mt-2 space-y-1 text-xs text-slate-600">
        <div className="flex justify-between gap-4">
          <span>Projects</span>
          <span className="font-semibold text-slate-900">
            {formatNumber(data.current_count)}
          </span>
        </div>

        <div className="flex justify-between gap-4">
          <span>Share</span>
          <span className="font-semibold text-[#365561]">
            {formatPercent(data.current_percentage)}
          </span>
        </div>
      </div>
    </div>
  );
}


function ClientLeaderboardTooltip({ active, payload }) {
  if (!active || !payload || payload.length === 0) return null;

  const data = payload[0]?.payload;
  if (!data) return null;

  const percentOfTotal = (value) =>
    data.total_projects > 0
      ? (Number(value || 0) / Number(data.total_projects || 0)) * 100
      : 0;

  return (
    <div className="min-w-[200px] max-w-[320px] rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-[0_18px_40px_rgba(15,23,42,0.14)]">
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="truncate text-sm font-semibold text-slate-900">
          {data.client_name}
        </p>

        <span className="shrink-0 rounded-full bg-[#365561]/10 px-2 py-0.5 text-[11px] font-semibold text-[#365561]">
          {formatNumber(data.total_projects)} projects
        </span>
      </div>

      <div className="mb-2 h-px bg-slate-200" />

      <div className="space-y-[2px] text-xs leading-4 text-slate-600">
        <p>
          Not Started: {formatNumber(data.not_started)} · {formatPercent(percentOfTotal(data.not_started))}
        </p>
        <p>
          Profiling: {formatNumber(data.profiling)} · {formatPercent(percentOfTotal(data.profiling))}
        </p>
        <p>
          Waiting Input: {formatNumber(data.waiting_input)} · {formatPercent(percentOfTotal(data.waiting_input))}
        </p>
        <p>
          Contracting: {formatNumber(data.contracting)} · {formatPercent(percentOfTotal(data.contracting))}
        </p>
        <p className="text-green-600">
          Won: {formatNumber(data.closed_won)} · {formatPercent(percentOfTotal(data.closed_won))}
        </p>
        <p className="text-red-500">
          Lost: {formatNumber(data.closed_lost)} · {formatPercent(percentOfTotal(data.closed_lost))}
        </p>
      </div>

      <div className="my-2 h-px bg-slate-200" />
    </div>
  );
}

function ClientProjectStatusAxisTick({ x, y, payload, slotWidth = 100 }) {
  const value = String(payload?.value || "");
  const maxChars = Math.max(8, Math.floor(Number(slotWidth || 100) / 7));
  const label =
    value.length > maxChars
      ? `${value.slice(0, Math.max(4, maxChars - 1))}…`
      : value;

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="middle"
        fill="#64748b"
        fontSize={12}
      >
        {label}
      </text>
    </g>
  );
}

function ClientLeaderboardSection({ clientPerformance = [], periodLabel }) {
  const chartData = useMemo(() => {
    return (clientPerformance || [])
      .map((item) => ({
        client_name: item.client_name || "Unknown client",
        not_started: Number(item.not_started || 0),
        profiling: Number(item.profiling || 0),
        waiting_input: Number(item.waiting_input || 0),
        contracting: Number(item.contracting || 0),
        closed_won: Number(item.closed_won || item.total_wins || 0),
        closed_lost: Number(item.closed_lost || 0),
        other: Number(item.other || 0),
        total_projects: Number(item.total_projects || 0),
      }))
      .filter((item) => item.total_projects > 0)
      .sort((a, b) =>
        a.client_name.localeCompare(b.client_name, undefined, {
          sensitivity: "base",
        })
      );
  }, [clientPerformance]);

  const chartViewportRef = useRef(null);
  const scrollFrameRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  useEffect(() => {
    const node = chartViewportRef.current;
    if (!node) return undefined;

    const updateViewportWidth = () => {
      setViewportWidth(node.clientWidth);
    };

    updateViewportWidth();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateViewportWidth);
      observer.observe(node);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateViewportWidth);
    return () => window.removeEventListener("resize", updateViewportWidth);
  }, [chartData.length]);

  useEffect(() => {
    return () => {
      if (scrollFrameRef.current) {
        cancelAnimationFrame(scrollFrameRef.current);
      }
    };
  }, []);

  const effectiveViewportWidth = viewportWidth || 1024;

  // Match the visual spacing of the Project Supporter Workload chart.
  // Wider windows may show more clients, but never more than 25 at once.
  const preferredClientSlotWidth = 100;
  const visibleClientCapacity = Math.min(
    25,
    Math.max(4, Math.floor(effectiveViewportWidth / preferredClientSlotWidth))
  );

  // If there are only a few clients, the chart fills the whole card exactly
  // like Project Supporter Workload. Extra clients extend the chart on X.
  const clientSlotWidth =
    chartData.length > visibleClientCapacity
      ? effectiveViewportWidth / visibleClientCapacity
      : effectiveViewportWidth / Math.max(chartData.length, 1);

  const chartWidth =
    chartData.length > visibleClientCapacity
      ? chartData.length * clientSlotWidth
      : effectiveViewportWidth;

  const visibleStartIndex = Math.max(
    0,
    Math.min(
      Math.max(chartData.length - 1, 0),
      Math.floor(scrollLeft / Math.max(clientSlotWidth, 1))
    )
  );

  const visibleEndIndex = Math.min(
    chartData.length,
    Math.max(
      visibleStartIndex + 1,
      Math.ceil(
        (scrollLeft + effectiveViewportWidth) / Math.max(clientSlotWidth, 1)
      )
    )
  );

  const visibleClients = useMemo(
    () => chartData.slice(visibleStartIndex, visibleEndIndex),
    [chartData, visibleStartIndex, visibleEndIndex]
  );

  const visibleScale = useMemo(() => {
    const visibleMax = Math.max(
      0,
      ...visibleClients.map(
        (item) =>
          Number(item.not_started || 0) +
          Number(item.profiling || 0) +
          Number(item.waiting_input || 0) +
          Number(item.contracting || 0) +
          Number(item.closed_won || 0) +
          Number(item.closed_lost || 0)
      )
    );

    const paddedTarget = Math.max(1, visibleMax * 1.12);
    const roughStep = paddedTarget / 5;
    const magnitude =
      10 ** Math.floor(Math.log10(Math.max(roughStep, 1)));
    const normalized = roughStep / magnitude;
    const niceMultiplier =
      normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
    const step = Math.max(1, niceMultiplier * magnitude);
    const max = Math.max(step, Math.ceil(paddedTarget / step) * step);
    const ticks = [];

    for (let value = 0; value <= max + step / 2; value += step) {
      ticks.push(value);
    }

    return { max, ticks };
  }, [visibleClients]);

  const handleClientChartScroll = (event) => {
    const nextScrollLeft = event.currentTarget.scrollLeft;

    if (scrollFrameRef.current) {
      cancelAnimationFrame(scrollFrameRef.current);
    }

    scrollFrameRef.current = requestAnimationFrame(() => {
      setScrollLeft(nextScrollLeft);
      scrollFrameRef.current = null;
    });
  };

  return (
    <SectionCard
      title="Client Portfolio Overview"
      subtitle={`Project status distribution by client for ${periodLabel}`}
      right={
        <div className="rounded-2xl bg-[#365561]/5 px-3 py-2 text-xs font-semibold text-[#365561]">
          {formatNumber(chartData.length)} clients
        </div>
      }
    >
      {chartData.length === 0 ? (
        <EmptyState title="No client project data" />
      ) : (
        <div>
          <div className="flex h-[380px] w-full">
            <div className="h-full w-[48px] shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={[{ value: 0 }]}
                  margin={{ top: 8, right: 0, left: 0, bottom: 24 }}
                >
                  <XAxis
                    dataKey="value"
                    height={30}
                    tick={false}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    allowDecimals={false}
                    allowDataOverflow
                    domain={[0, visibleScale.max]}
                    ticks={visibleScale.ticks}
                    tickLine={false}
                    axisLine={false}
                    width={46}
                    tick={{ fill: "#64748b", fontSize: 12 }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div
              ref={chartViewportRef}
              className="h-full min-w-0 flex-1 overflow-x-auto overflow-y-hidden"
              onScroll={handleClientChartScroll}
            >
              <div style={{ width: `${chartWidth}px` }} className="h-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    barGap={10}
                    margin={{ top: 8, right: 8, left: 0, bottom: 24 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#e2e8f0"
                      vertical={false}
                    />

                    <XAxis
                      dataKey="client_name"
                      interval={0}
                      height={30}
                      tickLine={false}
                      axisLine={false}
                      tick={(props) => (
                        <ClientProjectStatusAxisTick
                          {...props}
                          slotWidth={clientSlotWidth}
                        />
                      )}
                    />

                    <YAxis
                      hide
                      width={0}
                      allowDecimals={false}
                      allowDataOverflow
                      domain={[0, visibleScale.max]}
                      ticks={visibleScale.ticks}
                    />

                    <Tooltip
                      cursor={{ fill: "rgba(54,85,97,0.015)" }}
                      content={<ClientLeaderboardTooltip />}
                    />

                    <Bar
                      dataKey="not_started"
                      stackId="projects"
                      fill="#e83131"
                      name="Not Started"
                      isAnimationActive
                      animationDuration={240}
                      animationEasing="ease-out"
                    />
                    <Bar
                      dataKey="profiling"
                      stackId="projects"
                      fill="rgb(255, 192, 0)"
                      name="Profiling"
                      isAnimationActive
                      animationDuration={240}
                      animationEasing="ease-out"
                    />
                    <Bar
                      dataKey="waiting_input"
                      stackId="projects"
                      fill="rgb(0, 176, 80)"
                      name="Waiting Input"
                      isAnimationActive
                      animationDuration={240}
                      animationEasing="ease-out"
                    />
                    <Bar
                      dataKey="contracting"
                      stackId="projects"
                      fill="#0ea5e9"
                      name="Contracting"
                      isAnimationActive
                      animationDuration={240}
                      animationEasing="ease-out"
                    />
                    <Bar
                      dataKey="closed_won"
                      stackId="projects"
                      fill="rgb(198, 224, 180)"
                      name="Won"
                      isAnimationActive
                      animationDuration={240}
                      animationEasing="ease-out"
                    />
                    <Bar
                      dataKey="closed_lost"
                      stackId="projects"
                      fill="rgb(250, 218, 196)"
                      name="Lost"
                      isAnimationActive
                      animationDuration={240}
                      animationEasing="ease-out"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="mt-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[10px] font-medium text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-[#e83131]" />
              Not Started
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-[rgb(255,192,0)]" />
              Profiling
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-[rgb(0,176,80)]" />
              Waiting Input
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-[#0ea5e9]" />
              Contracting
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: "rgb(198, 224, 180)" }}
              />
              Won
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: "rgb(250, 218, 196)" }}
              />
              Lost
            </span>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

function formatDeliveryTimeLabel(hours) {
  if (hours === null || hours === undefined || Number.isNaN(Number(hours))) {
    return "—";
  }

  const roundedHours = Math.max(0, Math.round(Number(hours)));
  const days = Math.floor(roundedHours / 24);
  const remainingHours = roundedHours % 24;

  if (days === 0) {
    return `${roundedHours} ${roundedHours === 1 ? "hour" : "hours"}`;
  }

  if (remainingHours === 0) {
    return `${days} ${days === 1 ? "day" : "days"}`;
  }

  return `${days} ${days === 1 ? "day" : "days"} ${remainingHours} ${remainingHours === 1 ? "hour" : "hours"}`;
}

function getTimelineStageMeta(status) {
  const normalized = String(status || "").toLowerCase();

  if (normalized.includes("won")) return { dotClass: "bg-emerald-500", borderClass: "border-emerald-200", textClass: "text-emerald-700" };
  if (normalized.includes("lost")) return { dotClass: "bg-orange-400", borderClass: "border-orange-200", textClass: "text-orange-600" };
  if (normalized.includes("contract")) return { dotClass: "bg-sky-500", borderClass: "border-sky-200", textClass: "text-sky-700" };
  if (normalized.includes("waiting")) return { dotClass: "bg-green-500", borderClass: "border-green-200", textClass: "text-green-700" };
  if (normalized.includes("profil")) return { dotClass: "bg-yellow-400", borderClass: "border-yellow-200", textClass: "text-yellow-700" };
  if (normalized.includes("delivered")) return { dotClass: "bg-[#365561]", borderClass: "border-[#365561]/20", textClass: "text-[#365561]" };

  return { dotClass: "bg-slate-400", borderClass: "border-slate-200", textClass: "text-slate-600" };
}

function ProjectDeliveryTimeline({ project }) {
  const timeline = project.timeline || [];

  if (timeline.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500">
        No staging timeline available for this project.
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="absolute left-[9px] top-2 h-[calc(100%-16px)] w-px bg-slate-200" />

      <div className="space-y-3">
        {timeline.map((stage, index) => {
          const meta = getTimelineStageMeta(stage.stage);
          const isDeliverySignal =
            stage.stage === "Delivered!" || stage.stage === "Waiting Input";

          return (
            <div
              key={`${project.project_id}-${stage.stage}-${index}`}
              className="relative flex gap-3"
            >
              <span
                className={[
                  "relative z-10 mt-1 h-[18px] w-[18px] rounded-full border-4 border-white shadow-sm",
                  meta.dotClass,
                  isDeliverySignal ? "ring-4 ring-[#365561]/10" : "",
                ].join(" ")}
              />

              <div
                className={[
                  "flex-1 rounded-2xl border bg-white px-3 py-2",
                  meta.borderClass,
                ].join(" ")}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className={`text-xs font-semibold ${meta.textClass}`}>
                    {stage.stage}
                  </p>

                  <p className="text-[11px] font-medium text-slate-400">
                    {formatDateTime(stage.timestamp)}
                  </p>
                </div>

                {stage.note ? (
                  <p className="mt-1 text-[11px] leading-4 text-slate-500">
                    {stage.note}
                  </p>
                ) : null}

                {stage.source ? (
                  <p className="mt-1 text-[11px] leading-4 text-slate-400">
                    Source: {stage.source}
                  </p>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DeliveryProjectCard({ project }) {
  const deliveryTimeHours = project.delivery_time_hours ?? (project.delivery_time_days != null ? Number(project.delivery_time_days) * 24 : null);
  const deliverySignal = project.delivery_signal || "No delivery signal";

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <button
            type="button"
            onClick={() =>
              window.open(
                `/app/ProjectDetails/${project.project_id}`,
                "_blank",
                "noopener,noreferrer"
              )
            }
            className="truncate text-left text-sm font-semibold text-slate-900 transition hover:text-[#365561]"
          >
            {project.project_name || "Untitled project"}
          </button>

          <p className="mt-1 text-xs text-slate-500">
            Client: <span className="font-semibold text-slate-700">{project.client_name || "Unknown client"}</span>
          </p>

          <p className="mt-0.5 text-xs text-slate-500">
            Status: {project.project_status || "Unknown"}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2 text-right">
          <div className="rounded-2xl bg-[#365561]/5 px-3 py-2">
            <p className="text-[11px] text-slate-500">Delivery time</p>
            <p className="text-sm font-semibold text-[#365561]">
              {formatDeliveryTimeLabel(deliveryTimeHours)}
            </p>
          </div>

          <div className="rounded-2xl bg-slate-50 px-3 py-2">
            <p className="text-[11px] text-slate-500">Signal</p>
            <p className="text-xs font-semibold text-slate-700">
              {deliverySignal}
            </p>
          </div>
        </div>
      </div>

      <ProjectDeliveryTimeline project={project} />
    </div>
  );
}

function BusinessDeveloperDeliveryLeaderboard({ bdDelivery = [], loading, periodLabel }) {
  const leaderboard = useMemo(() => {
    return (bdDelivery || [])
      .map((item) => {
        const delivered = Number(item.delivered_projects || 0);
        const manualDelivery = Number(item.manual_delivery_projects || 0);
        const total = Number(item.total_projects || 0);
        const won = Number(item.closed_won || 0);
        const lost = Number(item.closed_lost || 0);
        const ongoing = Number(item.ongoing || 0);
        const averageDeliveryTimeHours =
          item.average_delivery_time_hours !== null &&
          item.average_delivery_time_hours !== undefined
            ? Number(item.average_delivery_time_hours)
            : item.average_delivery_time_days !== null && item.average_delivery_time_days !== undefined
              ? Number(item.average_delivery_time_days) * 24
              : null;
        const winRate = total > 0 ? (won / total) * 100 : 0;

        return {
          ...item,
          total,
          delivered,
          manualDelivery,
          won,
          lost,
          ongoing,
          averageDeliveryTimeHours,
          winRate,
          deliverySignalProjects: Number(item.delivery_signal_projects || 0),
          projects: item.projects || [],
        };
      })
      .filter((item) => item.total > 0)
      .sort((a, b) => {
        const aTime = a.averageDeliveryTimeHours ?? Number.POSITIVE_INFINITY;
        const bTime = b.averageDeliveryTimeHours ?? Number.POSITIVE_INFINITY;
        return aTime - bTime || b.won - a.won || b.total - a.total;
      });
  }, [bdDelivery]);

  const [selectedBD, setSelectedBD] = useState("");

  useEffect(() => {
    if (!selectedBD && leaderboard.length > 0) {
      setSelectedBD(leaderboard[0].business_developer);
    }

    if (
      selectedBD &&
      leaderboard.length > 0 &&
      !leaderboard.some((item) => item.business_developer === selectedBD)
    ) {
      setSelectedBD(leaderboard[0].business_developer);
    }
  }, [leaderboard, selectedBD]);

  const activeBD =
    leaderboard.find((item) => item.business_developer === selectedBD) || leaderboard[0];

  const totals = useMemo(() => {
    const base = leaderboard.reduce(
      (acc, item) => {
        acc.total += item.total;
        acc.delivered += item.delivered;
        acc.manualDelivery += item.manualDelivery;
        acc.won += item.won;
        acc.lost += item.lost;
        acc.ongoing += item.ongoing;

        if (item.averageDeliveryTimeHours !== null && item.averageDeliveryTimeHours !== undefined) {
          const weight = item.deliverySignalProjects || 0;
          acc.deliveryTimeSum += item.averageDeliveryTimeHours * weight;
          acc.deliveryTimeCount += weight;
        }

        return acc;
      },
      {
        total: 0,
        delivered: 0,
        manualDelivery: 0,
        won: 0,
        lost: 0,
        ongoing: 0,
        deliveryTimeSum: 0,
        deliveryTimeCount: 0,
      }
    );

    return {
      ...base,
      averageDeliveryTimeHours:
        base.deliveryTimeCount > 0
          ? base.deliveryTimeSum / base.deliveryTimeCount
          : null,
    };
  }, [leaderboard]);

  return (
    <SectionCard
      title="BD Delivery Time"
      subtitle={`Average delivery time from project creation to the first valid delivery signal for ${periodLabel}`}
      right={
        <div className="rounded-2xl bg-[#365561]/5 px-3 py-2 text-xs font-semibold text-[#365561]">
          Avg. delivery: {formatDeliveryTimeLabel(totals.averageDeliveryTimeHours)}
        </div>
      }
    >
      {loading ? (
        <LoadingSpinner label="Loading BD delivery..." />
      ) : leaderboard.length === 0 ? (
        <EmptyState title="No BD delivery data" />
      ) : (
        <div className="grid gap-5 xl:grid-cols-[0.68fr_1.32fr]">
          <div className="max-h-[600px] space-y-3 overflow-y-auto pr-1">
            {leaderboard.map((bd, index) => {
              const isActive = activeBD?.business_developer === bd.business_developer;

              return (
                <button
                  key={bd.business_developer}
                  type="button"
                  onClick={() => setSelectedBD(bd.business_developer)}
                  className={[
                    "w-full rounded-2xl border p-4 text-left transition",
                    isActive
                      ? "border-[#365561]/25 bg-[#365561]/5 shadow-sm"
                      : "border-slate-200 bg-white hover:border-[#365561]/20 hover:bg-slate-50",
                  ].join(" ")}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className={[
                        "flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-sm font-bold",
                        index === 0 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600",
                      ].join(" ")}>{index + 1}</div>

                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900">{bd.business_developer}</p>
                        <p className="text-xs text-slate-400">
                          <span className="text-emerald-600">{formatNumber(bd.won)} won</span>
                          <span> · {formatNumber(bd.total)} requests</span>
                          <span> · {formatPercent(bd.winRate)} win rate</span>
                        </p>
                      </div>
                    </div>

                    <div className="text-right">
                      <p>
                        <span className="text-[11px] text-slate-400">Avg. </span>
                        <span className="text-sm font-semibold text-[#365561]">
                          {formatDeliveryTimeLabel(bd.averageDeliveryTimeHours)}
                        </span>
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatNumber(bd.deliverySignalProjects)} valid delivery signals
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {activeBD?.business_developer || "Business developer"}
                </p>
                <p className="mt-1 text-xs text-slate-500">Projects with delivery timeline</p>
              </div>

              <div className="grid grid-cols-3 gap-2 text-right">
                <div className="rounded-2xl bg-white px-3 py-2">
                  <p className="text-[11px] text-slate-400">Avg. delivery</p>
                  <p className="text-sm font-semibold text-[#365561]">
                    {formatDeliveryTimeLabel(activeBD?.averageDeliveryTimeHours)}
                  </p>
                </div>
                <div className="rounded-2xl bg-white px-3 py-2">
                  <p className="text-[11px] text-slate-400">Win rate</p>
                  <p className="text-sm font-semibold text-emerald-600">
                    {formatPercent(activeBD?.winRate || 0)}
                  </p>
                </div>
                <div className="rounded-2xl bg-white px-3 py-2">
                  <p className="text-[11px] text-slate-400">Requests</p>
                  <p className="text-sm font-semibold text-slate-900">
                    {formatNumber(activeBD?.total || 0)}
                  </p>
                </div>
              </div>
            </div>

            {activeBD?.projects?.length > 0 ? (
              <div className="max-h-[470px] space-y-4 overflow-y-auto pr-1">
                {activeBD.projects
                  .slice()
                  .sort((a, b) => {
                    const aHours = a.delivery_time_hours ?? Number.POSITIVE_INFINITY;
                    const bHours = b.delivery_time_hours ?? Number.POSITIVE_INFINITY;
                    return aHours - bHours;
                  })
                  .map((project) => (
                    <DeliveryProjectCard
                      key={`${activeBD.business_developer}-${project.project_id}`}
                      project={project}
                    />
                  ))}
              </div>
            ) : (
              <EmptyState title="No project timeline data for this BD" />
            )}
          </div>
        </div>
      )}
    </SectionCard>
  );
}

function ProjectSupporterDeliveryLeaderboard({ supporterDelivery = [], loading, periodLabel }) {
  const leaderboard = useMemo(() => {
    return (supporterDelivery || [])
      .map((item) => {
        const delivered = Number(item.delivered_projects || 0);
        const manualDelivery = Number(item.manual_delivery_projects || 0);
        const total = Number(item.total_projects || 0);
        const won = Number(item.closed_won || 0);
        const lost = Number(item.closed_lost || 0);
        const ongoing = Number(item.ongoing || 0);
        const averageDeliveryTimeHours =
          item.average_delivery_time_hours !== null &&
          item.average_delivery_time_hours !== undefined
            ? Number(item.average_delivery_time_hours)
            : item.average_delivery_time_days !== null && item.average_delivery_time_days !== undefined
              ? Number(item.average_delivery_time_days) * 24
              : null;
        const winRate = total > 0 ? (won / total) * 100 : 0;

        return {
          ...item,
          total,
          delivered,
          manualDelivery,
          won,
          lost,
          ongoing,
          averageDeliveryTimeHours,
          winRate,
          deliverySignalProjects: Number(item.delivery_signal_projects || 0),
          projects: item.projects || [],
        };
      })
      .filter((item) => item.total > 0)
      .sort((a, b) => {
        const aTime = a.averageDeliveryTimeHours ?? Number.POSITIVE_INFINITY;
        const bTime = b.averageDeliveryTimeHours ?? Number.POSITIVE_INFINITY;
        return aTime - bTime || b.won - a.won || b.total - a.total;
      });
  }, [supporterDelivery]);

  const [selectedSupporter, setSelectedSupporter] = useState("");

  useEffect(() => {
    if (!selectedSupporter && leaderboard.length > 0) {
      setSelectedSupporter(leaderboard[0].supporter_name);
    }

    if (
      selectedSupporter &&
      leaderboard.length > 0 &&
      !leaderboard.some((item) => item.supporter_name === selectedSupporter)
    ) {
      setSelectedSupporter(leaderboard[0].supporter_name);
    }
  }, [leaderboard, selectedSupporter]);

  const activeSupporter =
    leaderboard.find((item) => item.supporter_name === selectedSupporter) || leaderboard[0];

  const totals = useMemo(() => {
    const base = leaderboard.reduce(
      (acc, item) => {
        acc.total += item.total;
        acc.won += item.won;
        if (item.averageDeliveryTimeHours !== null && item.averageDeliveryTimeHours !== undefined) {
          const weight = item.deliverySignalProjects || 0;
          acc.deliveryTimeSum += item.averageDeliveryTimeHours * weight;
          acc.deliveryTimeCount += weight;
        }
        return acc;
      },
      { total: 0, won: 0, deliveryTimeSum: 0, deliveryTimeCount: 0 }
    );

    return {
      ...base,
      averageDeliveryTimeHours:
        base.deliveryTimeCount > 0
          ? base.deliveryTimeSum / base.deliveryTimeCount
          : null,
    };
  }, [leaderboard]);

  return (
    <SectionCard
      title="Project Supporter Delivery Time"
      subtitle={`Average delivery time from project creation to the first valid delivery signal for ${periodLabel}`}
      right={
        <div className="rounded-2xl bg-[#365561]/5 px-3 py-2 text-xs font-semibold text-[#365561]">
          Avg. delivery: {formatDeliveryTimeLabel(totals.averageDeliveryTimeHours)}
        </div>
      }
    >
      {loading ? (
        <LoadingSpinner label="Loading supporter delivery..." />
      ) : leaderboard.length === 0 ? (
        <EmptyState title="No supporter delivery data" />
      ) : (
        <div className="grid gap-5 xl:grid-cols-[0.68fr_1.32fr]">
          <div className="max-h-[600px] space-y-3 overflow-y-auto pr-1">
            {leaderboard.map((supporter, index) => {
              const isActive = activeSupporter?.supporter_name === supporter.supporter_name;

              return (
                <button
                  key={supporter.supporter_name}
                  type="button"
                  onClick={() => setSelectedSupporter(supporter.supporter_name)}
                  className={[
                    "w-full rounded-2xl border p-4 text-left transition",
                    isActive
                      ? "border-[#365561]/25 bg-[#365561]/5 shadow-sm"
                      : "border-slate-200 bg-white hover:border-[#365561]/20 hover:bg-slate-50",
                  ].join(" ")}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className={[
                        "flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-sm font-bold",
                        index === 0 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600",
                      ].join(" ")}>{index + 1}</div>

                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900">{supporter.supporter_name}</p>
                        <p className="text-xs text-slate-400">
                          <span className="text-emerald-600">{formatNumber(supporter.won)} won</span>
                          <span> · {formatNumber(supporter.total)} requests</span>
                          <span> · {formatPercent(supporter.winRate)} win rate</span>
                        </p>
                      </div>
                    </div>

                    <div className="text-right">
                      <p>
                        <span className="text-[11px] text-slate-400">Avg. </span>
                        <span className="text-sm font-semibold text-[#365561]">
                          {formatDeliveryTimeLabel(supporter.averageDeliveryTimeHours)}
                        </span>
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatNumber(supporter.deliverySignalProjects)} valid delivery signals
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {activeSupporter?.supporter_name || "Project supporter"}
                </p>
                <p className="mt-1 text-xs text-slate-500">Projects with delivery timeline</p>
              </div>

              <div className="grid grid-cols-3 gap-2 text-right">
                <div className="rounded-2xl bg-white px-3 py-2">
                  <p className="text-[11px] text-slate-400">Avg. delivery</p>
                  <p className="text-sm font-semibold text-[#365561]">
                    {formatDeliveryTimeLabel(activeSupporter?.averageDeliveryTimeHours)}
                  </p>
                </div>
                <div className="rounded-2xl bg-white px-3 py-2">
                  <p className="text-[11px] text-slate-400">Win rate</p>
                  <p className="text-sm font-semibold text-emerald-600">
                    {formatPercent(activeSupporter?.winRate || 0)}
                  </p>
                </div>
                <div className="rounded-2xl bg-white px-3 py-2">
                  <p className="text-[11px] text-slate-400">Requests</p>
                  <p className="text-sm font-semibold text-slate-900">
                    {formatNumber(activeSupporter?.total || 0)}
                  </p>
                </div>
              </div>
            </div>

            {activeSupporter?.projects?.length > 0 ? (
              <div className="max-h-[470px] space-y-4 overflow-y-auto pr-1">
                {activeSupporter.projects
                  .slice()
                  .sort((a, b) => {
                    const aHours = a.delivery_time_hours ?? Number.POSITIVE_INFINITY;
                    const bHours = b.delivery_time_hours ?? Number.POSITIVE_INFINITY;
                    return aHours - bHours;
                  })
                  .map((project) => (
                    <DeliveryProjectCard
                      key={`${activeSupporter.supporter_name}-${project.project_id}`}
                      project={project}
                    />
                  ))}
              </div>
            ) : (
              <EmptyState title="No project timeline data for this supporter" />
            )}
          </div>
        </div>
      )}
    </SectionCard>
  );
}


function formatCompactDuration(hours) {
  if (hours === null || hours === undefined || Number.isNaN(Number(hours))) {
    return "—";
  }

  const totalMinutes = Math.max(0, Math.round(Number(hours) * 60));
  if (totalMinutes < 60) return `${totalMinutes}m`;

  const wholeHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (wholeHours < 24) {
    return minutes > 0 ? `${wholeHours}h ${minutes}m` : `${wholeHours}h`;
  }

  const days = Math.floor(wholeHours / 24);
  const hoursLeft = wholeHours % 24;
  return hoursLeft > 0 ? `${days}d ${hoursLeft}h` : `${days}d`;
}

function getDashboardUserAliases(user) {
  return [
    user?.display_name,
    user?.fullname,
    user?.username,
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);
}

function mergeDashboardUserPresentation(person, userById, userByAlias) {
  if (!person) return person;

  let user = null;
  if (person.user_id !== null && person.user_id !== undefined) {
    user = userById.get(String(person.user_id)) || null;
  }

  if (!user) {
    const aliases = [
      person.display_name,
      person.fullname,
      person.username,
      person.business_developer,
      person.supporter_name,
      person.supporter,
    ]
      .map((value) => String(value || "").trim().toLowerCase())
      .filter(Boolean);

    for (const alias of aliases) {
      user = userByAlias.get(alias) || null;
      if (user) break;
    }
  }

  if (!user) return person;

  return {
    ...person,
    user_id: person.user_id ?? user.user_id,
    fullname: person.fullname || user.fullname,
    username: person.username || user.username,
    display_name: person.display_name || user.display_name,
    is_active:
      person.is_active === undefined ? user.is_active : person.is_active,
    profile_picture: user.profile_picture || "",
  };
}

function resolveDashboardProfilePicture(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^(https?:|data:|blob:)/i.test(raw)) return raw;

  return `https://images.expertpowerhouse.com${raw.startsWith("/") ? "" : "/"}${raw}`;
}

function DashboardUserAvatar({ person, size = "md" }) {
  const name = person?.fullname || person?.display_name || person?.username || "?";
  const initials = String(name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";
  const src = resolveDashboardProfilePicture(person?.profile_picture);
  const sizeClass = size === "lg" ? "h-16 w-16 text-lg" : "h-12 w-12 text-sm";

  return (
    <div
      className={`${sizeClass} relative flex shrink-0 items-center justify-center overflow-hidden rounded-full border border-white bg-[#365561]/10 font-bold text-[#365561] shadow-sm`}
    >
      <span>{initials}</span>
      {src ? (
        <img
          src={src}
          alt={name}
          className="absolute inset-0 h-full w-full object-cover"
          onError={(event) => {
            event.currentTarget.style.display = "none";
          }}
        />
      ) : null}
    </div>
  );
}

function ExecutionMetric({
  label,
  value,
  helper,
  tone = "default",
  indicatorIcon: IndicatorIcon,
  indicatorLabel,
  info,
  infoPlacement = "right",
}) {
  const toneClass = {
    default: "text-slate-900",
    brand: "text-[#365561]",
    emerald: "text-emerald-600",
    sky: "text-sky-600",
    amber: "text-amber-600",
  }[tone] || "text-slate-900";

  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3 shadow-sm">
      <div className="flex items-start justify-between gap-1.5">
        <p className="min-w-0 text-[10px] font-semibold uppercase leading-4 tracking-[0.12em] text-slate-400">
          {label}
        </p>
        {IndicatorIcon || info ? (
          <div className="group/metric-help relative z-30 flex h-4 w-4 shrink-0 items-center justify-center">
            {info ? (
              <button
                type="button"
                aria-label={`${indicatorLabel || `About ${label}`}: ${info}`}
                className="flex h-4 w-4 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-[#365561] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#365561]/40"
              >
                {IndicatorIcon ? (
                  <IndicatorIcon className="h-3.5 w-3.5 text-[#365561]" aria-hidden="true" />
                ) : (
                  <Info className="h-3.5 w-3.5" aria-hidden="true" />
                )}
              </button>
            ) : IndicatorIcon ? (
              <IndicatorIcon className="h-3.5 w-3.5 text-[#365561]" aria-label={indicatorLabel || label} />
            ) : null}
            {info ? (
              <div
                role="tooltip"
                className={`pointer-events-none absolute top-full mt-2 hidden w-64 max-w-[calc(100vw-3rem)] rounded-2xl border border-[#365561]/25 bg-[#557784] px-3 py-2.5 text-left text-[11px] font-normal normal-case leading-4 tracking-normal text-white shadow-[0_16px_36px_rgba(15,23,42,0.22)] group-hover/metric-help:block group-focus-within/metric-help:block ${infoPlacement === "left" ? "left-0 sm:right-0 sm:left-auto" : "right-0"}`}
              >
                <span className="mb-1 block font-semibold">{label}</span>
                {info}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      <p className={`mt-1 text-lg font-semibold ${toneClass}`}>{value}</p>
      {helper ? <p className="mt-0.5 text-[11px] text-slate-400">{helper}</p> : null}
    </div>
  );
}

function ExecutionFunnelRow({ label, value, maxValue, meta }) {
  const width = maxValue > 0 ? Math.max(4, Math.min(100, (Number(value || 0) / maxValue) * 100)) : 0;

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-3 text-xs">
        <span className="font-medium text-slate-600">{label}</span>
        <span className="font-semibold text-slate-900">
          {formatNumber(value)}{meta ? <span className="ml-1 font-normal text-slate-400">{meta}</span> : null}
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full bg-[#365561] transition-all duration-300"
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}

function ExecutionKpiDrawer({
  person,
  roleType,
  periodLabel,
  onClose,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
  detailLoading = false,
  detailError = "",
}) {
  useEffect(() => {
    if (!person) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [person]);

  if (!person) return null;

  const isBD = roleType === "business_developer";
  const roleLabel = isBD ? "Business Developer" : "Project Supporter";
  const profilesSourced = Number(person.profiles_sourced || 0);
  const contactedProfiles = Number(person.contacted_profiles || 0);
  const digitalProfiles = Number(person.digital_profiles || 0);
  const participatedProfiles = Number(person.participated_profiles || 0);
  const categories = person.category_mix || [];
  const projects = person.projects || [];
  const methods = person.outreach_methods || {};
  const portfolioYear = Number(person.portfolio_year || new Date().getFullYear());
  const decisionCount = Number(person.decision_count || 0);
  const portfolioTotal = Number(person.portfolio_total_projects || 0);

  return (
    <AnimatePresence>
      <>
        <motion.button
          type="button"
          aria-label="Close KPI drawer"
          className="fixed bottom-0 left-0 right-0 top-[78px] z-[120] bg-slate-950/20 backdrop-blur-[1px]"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />

        <motion.aside
          className="fixed bottom-0 right-0 top-[78px] z-[130] flex w-full flex-col border-l border-slate-200 bg-slate-50 shadow-[-24px_0_70px_rgba(15,23,42,0.18)] sm:w-[620px] xl:w-[720px]"
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ duration: 0.32, ease: "easeInOut" }}
        >
          <div className="border-b border-slate-200 bg-white px-5 py-4 sm:px-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex min-w-0 items-center gap-3">
                <DashboardUserAvatar person={person} size="lg" />
                <div className="min-w-0">
                  <p className="truncate text-lg font-semibold text-slate-900">
                    {person.fullname || person.display_name || person.username || "Unknown user"}
                  </p>
                  <p className="mt-0.5 text-xs font-medium text-[#365561]">{roleLabel}</p>
                  <p className="mt-1 text-xs text-slate-400">{periodLabel}</p>
                </div>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-4 flex items-center justify-between gap-3">
              <button
                type="button"
                disabled={!hasPrevious}
                onClick={onPrevious}
                className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition hover:border-[#365561]/20 hover:text-[#365561] disabled:cursor-not-allowed disabled:opacity-35"
              >
                <ChevronLeft className="h-4 w-4" /> Previous
              </button>
              <span className="text-[11px] font-medium text-slate-400">Browse {roleLabel.toLowerCase()}s</span>
              <button
                type="button"
                disabled={!hasNext}
                onClick={onNext}
                className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition hover:border-[#365561]/20 hover:text-[#365561] disabled:cursor-not-allowed disabled:opacity-35"
              >
                Next <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
              <ExecutionMetric
                label="Projects worked on"
                value={formatNumber(person.total_projects)}
                helper={`${formatNumber(person.new_projects)} new · ${formatNumber(person.carried_over_projects)} carried`}
                tone="brand"
              />
              <ExecutionMetric
                label="Decision win rate"
                value={formatRateOrDash(person.decision_win_rate, decisionCount)}
                helper={`${formatNumber(person.closed_won)} of ${formatNumber(decisionCount)} decisions won`}
                tone="emerald"
              />
              <ExecutionMetric
                label={`${portfolioYear} Portfolio win rate`}
                value={formatRateOrDash(person.portfolio_win_rate, portfolioTotal)}
                helper={`${formatNumber(person.portfolio_won_projects)} / ${formatNumber(portfolioTotal)} projects · ${formatPercentagePointDelta(person.portfolio_vs_company_pp)} vs company`}
                tone="brand"
              />
              <ExecutionMetric
                label="Avg. delivery"
                value={formatCompactDuration(person.average_delivery_time_hours)}
                helper={`${formatNumber(person.delivery_signal_projects)} first deliveries in period`}
                tone="sky"
              />
              <ExecutionMetric
                label="Profiles sourced"
                value={formatNumber(person.profiles_sourced)}
                helper={`${Number(person.average_profiles_per_project || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} / project worked on`}
              />
              <ExecutionMetric
                label="Median outreach"
                value={formatCompactDuration(person.median_first_outreach_hours)}
                helper={`${formatNumber(person.first_outreach_samples)} first-outreach events in period`}
                tone="brand"
                info="Typical time from adding a consultant to a project until this employee's first contact (Outlook email or LinkedIn InMail). The median is the middle observed time, so unusually fast or slow cases have less impact than on an average."
              />
            </div>

            {detailLoading ? (
              <div className="mt-4 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <LoadingSpinner label="Loading detailed employee breakdown..." />
              </div>
            ) : null}

            {detailError ? (
              <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs font-medium text-rose-700">
                {detailError}
              </div>
            ) : null}

            <div className="mt-5 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Sourcing funnel</p>
                  <p className="mt-0.5 text-xs text-slate-400">Directly attributed through ProjectExpert.expertAddedBy</p>
                </div>
                <UsersRound className="h-5 w-5 text-[#365561]" />
              </div>

              <div className="space-y-3.5">
                <ExecutionFunnelRow label="Profiles sourced" value={profilesSourced} maxValue={profilesSourced} />
                <ExecutionFunnelRow
                  label="Contacted by user"
                  value={contactedProfiles}
                  maxValue={profilesSourced}
                  meta={profilesSourced > 0 ? `· ${formatPercent(person.contact_coverage)}` : ""}
                />
                <ExecutionFunnelRow
                  label="Digital profile"
                  value={digitalProfiles}
                  maxValue={profilesSourced}
                  meta={profilesSourced > 0 ? `· ${formatPercent(person.digital_profile_rate)}` : ""}
                />
                <ExecutionFunnelRow
                  label="Participated"
                  value={participatedProfiles}
                  maxValue={profilesSourced}
                  meta={digitalProfiles > 0 ? `· ${formatPercent(person.participation_yield)} of digital` : ""}
                />
              </div>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center gap-2">
                  <Database className="h-4 w-4 text-[#365561]" />
                  <p className="text-sm font-semibold text-slate-900">Sourcing origin</p>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-2xl bg-slate-50 p-3">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Database</p>
                    <p className="mt-1 text-lg font-semibold text-slate-900">{formatNumber(person.database_profiles)}</p>
                    <p className="text-[11px] text-slate-400">
                      {profilesSourced > 0 ? formatPercent((Number(person.database_profiles || 0) / profilesSourced) * 100) : "0%"}
                    </p>
                  </div>
                  <div className="rounded-2xl bg-[#365561]/5 p-3">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">LinkedIn</p>
                    <p className="mt-1 text-lg font-semibold text-[#365561]">{formatNumber(person.linkedin_profiles)}</p>
                    <p className="text-[11px] text-slate-400">
                      {profilesSourced > 0 ? formatPercent((Number(person.linkedin_profiles || 0) / profilesSourced) * 100) : "0%"}
                    </p>
                  </div>
                </div>

                {Number(person.unknown_source_profiles || 0) > 0 ? (
                  <p className="mt-2 text-[11px] text-slate-400">
                    {formatNumber(person.unknown_source_profiles)} profile(s) could not be classified.
                  </p>
                ) : null}
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center gap-2">
                  <Mail className="h-4 w-4 text-[#365561]" />
                  <p className="text-sm font-semibold text-slate-900">First outreach</p>
                </div>

                <div className="space-y-2.5 text-xs">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-slate-500">Median</span>
                    <span className="font-semibold text-slate-900">{formatCompactDuration(person.median_first_outreach_hours)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-slate-500">Average</span>
                    <span className="font-semibold text-slate-900">{formatCompactDuration(person.average_first_outreach_hours)}</span>
                  </div>
                  <div className="h-px bg-slate-100" />
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-slate-500">Email</span>
                    <span className="font-semibold text-slate-900">{formatNumber(methods.Email)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-slate-500">InMail</span>
                    <span className="font-semibold text-slate-900">{formatNumber(methods.InMail)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-[#365561]" />
                <div>
                  <p className="text-sm font-semibold text-slate-900">Response performance</p>
                  <p className="mt-0.5 text-xs text-slate-400">ContactLog incoming/outgoing communication turns</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                <ExecutionMetric
                  label="Consultant response"
                  value={formatPercent(person.consultant_response_rate)}
                  helper={`${formatNumber(person.consultant_responded_profiles)} / ${formatNumber(person.profiles_contacted_by_user)} profiles`}
                  tone="emerald"
                />
                <ExecutionMetric
                  label="Median consultant reply"
                  value={formatCompactDuration(person.median_consultant_response_hours)}
                  helper={`${formatNumber(person.consultant_response_samples)} turns`}
                  indicatorIcon={Inbox}
                  indicatorLabel="Incoming consultant reply"
                  info="Incoming reply: Typical time a consultant takes to respond after an outgoing message from the internal employee. The median is the middle observed reply time, making it less sensitive to unusually long or short delays."
                />
                <ExecutionMetric
                  label="Median internal reply"
                  value={formatCompactDuration(person.median_internal_reply_hours)}
                  helper={`${formatNumber(person.internal_reply_samples)} turns`}
                  tone="brand"
                  indicatorIcon={Send}
                  indicatorLabel="Outgoing internal reply"
                  infoPlacement="left"
                  info="Outgoing reply: Typical time the internal employee takes to respond after an incoming consultant message. The median is the middle observed reply time, making it less sensitive to unusually long or short delays."
                />
                <ExecutionMetric
                  label="Avg. internal reply"
                  value={formatCompactDuration(person.average_internal_reply_hours)}
                  helper="Elapsed time"
                />
              </div>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-sm font-semibold text-slate-900">Project categories</p>
                <p className="mt-0.5 text-xs text-slate-400">Categories across projects worked on in {periodLabel}</p>

                {categories.length > 0 ? (
                  <div className="mt-4 space-y-3">
                    {categories.slice(0, 8).map((category) => (
                      <div key={category.category}>
                        <div className="mb-1 flex items-center justify-between gap-3 text-xs">
                          <span className="truncate font-medium text-slate-600">{category.category}</span>
                          <span className="shrink-0 font-semibold text-slate-900">{formatNumber(category.project_count)}</span>
                        </div>
                        <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                          <div
                            className="h-full rounded-full bg-[#365561]"
                            style={{ width: `${Math.min(100, Number(category.percentage || 0))}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-4 text-xs text-slate-400">No category data for this period.</p>
                )}
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-sm font-semibold text-slate-900">Delivery performance</p>
                <p className="mt-0.5 text-xs text-slate-400">First delivery events that occurred inside the selected period</p>

                <div className="mt-4 space-y-2.5 text-xs">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-slate-500">Average delivery</span>
                    <span className="font-semibold text-slate-900">{formatCompactDuration(person.average_delivery_time_hours)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-slate-500">Fastest</span>
                    <span className="font-semibold text-emerald-600">{formatCompactDuration(person.fastest_delivery_time_hours)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-slate-500">Slowest</span>
                    <span className="font-semibold text-amber-600">{formatCompactDuration(person.slowest_delivery_time_hours)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-slate-500">Valid delivery signals</span>
                    <span className="font-semibold text-slate-900">
                      {formatNumber(person.delivery_signal_projects)} / {formatNumber(person.total_projects)}
                    </span>
                  </div>
                  {isBD ? (
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-slate-500">Self-supported projects</span>
                      <span className="font-semibold text-[#365561]">
                        {formatNumber(person.self_supported_projects)} · {formatPercent(person.self_supported_share)}
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="mt-4 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex items-end justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Projects</p>
                  <p className="mt-0.5 text-xs text-slate-400">Per-project sourcing, outreach, response and delivery</p>
                </div>
                <span className="text-xs font-semibold text-[#365561]">{formatNumber(projects.length)} total</span>
              </div>

              {projects.length > 0 ? (
                <div className="space-y-2.5">
                  {projects.map((project) => (
                    <button
                      key={`${person.user_id || person.display_name}-${project.project_id}`}
                      type="button"
                      onClick={() =>
                        window.open(
                          `/app/ProjectDetails/${project.project_id}`,
                          "_blank",
                          "noopener,noreferrer"
                        )
                      }
                      className="group w-full rounded-2xl border border-slate-200 bg-slate-50 p-3 text-left transition hover:border-[#365561]/25 hover:bg-[#365561]/5 focus:outline-none"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-900 group-hover:text-[#365561]">
                            {project.project_name || "Untitled project"}
                          </p>
                          <p className="mt-0.5 truncate text-[11px] text-slate-400">
                            {(project.categories || []).join(" · ") || "Uncategorized"} · {project.project_status || "Unknown"}
                          </p>
                        </div>
                        <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-[#365561]" />
                      </div>

                      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-[11px] sm:grid-cols-4">
                        <div>
                          <p className="text-slate-400">Profiles</p>
                          <p className="font-semibold text-slate-700">
                            {formatNumber(project.profiles_added)} · DB {formatNumber(project.database_profiles)} / LI {formatNumber(project.linkedin_profiles)}
                          </p>
                        </div>
                        <div>
                          <p className="text-slate-400">Contact → digital</p>
                          <p className="font-semibold text-slate-700">
                            {formatNumber(project.contacted_profiles)} → {formatNumber(project.digital_profiles)}
                          </p>
                        </div>
                        <div>
                          <p className="text-slate-400">Median outreach</p>
                          <p className="font-semibold text-slate-700">{formatCompactDuration(project.median_first_outreach_hours)}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Delivery</p>
                          <p className="font-semibold text-slate-700">{formatCompactDuration(project.delivery_time_hours)}</p>
                        </div>
                      </div>

                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-slate-400">
                        <span>Participated: {formatNumber(project.participated_profiles)}</span>
                        <span>Consultant response: {formatPercent(project.consultant_response_rate)}</span>
                        <span>Internal reply: {formatCompactDuration(project.median_internal_reply_hours)}</span>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-400">
                  No projects were worked on during this selected period.
                </p>
              )}
            </div>
          </div>
        </motion.aside>
      </>
    </AnimatePresence>
  );
}

function ExecutionKpiSection({
  items = [],
  loading,
  periodLabel,
  periodKey,
  roleType,
  API_URL,
  dashboardUsers = [],
}) {
  const [selectedKey, setSelectedKey] = useState("");
  const [detailCache, setDetailCache] = useState({});
  const [detailLoadingKey, setDetailLoadingKey] = useState("");
  const [detailErrors, setDetailErrors] = useState({});

  const isBD = roleType === "business_developer";
  const title = isBD
    ? "Business Developer KPIs"
    : "Project Supporter KPIs";
  const subtitle = isBD
    ? `Workload, period outcomes and activity for ${periodLabel}`
    : `Workload, sourcing and operational activity for ${periodLabel}`;
  const sectionInfo = isBD
    ? "Workload includes owned projects that overlap the selected period, including carry-over projects. Wins and losses use closedDate inside the selected period. Decision win rate is period wins divided by period decisions. Portfolio win rate uses the current calendar-year project cohort. Sourcing, outreach and delivery metrics use their own timestamps. Detailed project breakdown is loaded only when a card is opened."
    : "Workload includes supported projects that overlap the selected period, including carry-over projects. Decision win rate uses wins/losses decided inside the selected period. Portfolio win rate uses the current calendar-year supported-project cohort. Sourcing, outreach and delivery metrics use their own timestamps. Detailed project breakdown is loaded only when a card is opened.";

  const userMaps = useMemo(() => {
    const byId = new Map();
    const byAlias = new Map();

    (dashboardUsers || []).forEach((user) => {
      if (user?.user_id !== null && user?.user_id !== undefined) {
        byId.set(String(user.user_id), user);
      }

      getDashboardUserAliases(user).forEach((alias) => {
        if (!byAlias.has(alias)) {
          byAlias.set(alias, user);
        }
      });
    });

    return { byId, byAlias };
  }, [dashboardUsers]);

  const people = useMemo(() => {
    return (items || [])
      .map((person) =>
        mergeDashboardUserPresentation(
          person,
          userMaps.byId,
          userMaps.byAlias
        )
      )
      .slice()
      .sort((a, b) => {
        if ((a.is_active !== false) !== (b.is_active !== false)) {
          return a.is_active !== false ? -1 : 1;
        }
        return Number(b.total_projects || 0) - Number(a.total_projects || 0) ||
          String(a.display_name || a.fullname || "").localeCompare(
            String(b.display_name || b.fullname || ""),
            undefined,
            { sensitivity: "base" }
          );
      });
  }, [items, userMaps]);

  const getKey = (person) =>
    String(
      person?.user_id ??
        `${person?.display_name || "unknown"}-${person?.username || ""}`
    );

  const selectedIndex = people.findIndex(
    (person) => getKey(person) === selectedKey
  );
  const selectedPerson = selectedIndex >= 0 ? people[selectedIndex] : null;

  const getDetailCacheKey = (person) => {
    if (!person) return "";
    return [
      periodKey || periodLabel || "",
      roleType,
      getKey(person),
    ].join("::");
  };

  const selectedDetailKey = getDetailCacheKey(selectedPerson);
  const cachedDetail = selectedDetailKey
    ? detailCache[selectedDetailKey]
    : null;

  const selectedDrawerPerson = useMemo(() => {
    if (!selectedPerson) return null;

    const detail = cachedDetail
      ? mergeDashboardUserPresentation(
          cachedDetail,
          userMaps.byId,
          userMaps.byAlias
        )
      : null;

    return detail
      ? { ...selectedPerson, ...detail }
      : selectedPerson;
  }, [selectedPerson, cachedDetail, userMaps]);

  useEffect(() => {
    if (
      selectedKey &&
      !people.some((person) => getKey(person) === selectedKey)
    ) {
      setSelectedKey("");
    }
  }, [people, selectedKey]);

  useEffect(() => {
    if (!selectedPerson || !selectedDetailKey || cachedDetail) {
      return undefined;
    }

    const controller = new AbortController();

    const fetchDetail = async () => {
      setDetailLoadingKey(selectedDetailKey);
      setDetailErrors((current) => ({
        ...current,
        [selectedDetailKey]: "",
      }));

      try {
        const params = new URLSearchParams({
          period: periodKey || "",
          role_type: roleType,
          identity:
            selectedPerson.display_name ||
            selectedPerson.fullname ||
            selectedPerson.username ||
            "",
        });

        if (
          selectedPerson.user_id !== null &&
          selectedPerson.user_id !== undefined
        ) {
          params.set("user_id", String(selectedPerson.user_id));
        }

        const response = await fetch(
          `${API_URL}fetch-dashboard-execution-detail?${params.toString()}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            signal: controller.signal,
          }
        );

        const data = await response.json();

        if (!response.ok || data.status !== "success") {
          throw new Error(
            data.message || "Failed to load employee detail"
          );
        }

        setDetailCache((current) => ({
          ...current,
          [selectedDetailKey]: data.executionDetail || {},
        }));
      } catch (error) {
        if (error?.name === "AbortError") return;

        console.error("Error fetching dashboard execution detail:", error);
        setDetailErrors((current) => ({
          ...current,
          [selectedDetailKey]:
            error?.message || "Failed to load employee detail",
        }));
      } finally {
        setDetailLoadingKey((current) =>
          current === selectedDetailKey ? "" : current
        );
      }
    };

    fetchDetail();
    return () => controller.abort();
  }, [
    API_URL,
    cachedDetail,
    periodKey,
    roleType,
    selectedDetailKey,
    selectedPerson,
  ]);

  const highlightStats = useMemo(() => {
    const withProjects = people.filter(
      (person) => Number(person.total_projects || 0) > 0
    );
    const fastestDelivery = Math.min(
      ...withProjects
        .map((person) => Number(person.average_delivery_time_hours))
        .filter(Number.isFinite)
    );
    const fastestOutreach = Math.min(
      ...withProjects
        .map((person) => Number(person.median_first_outreach_hours))
        .filter(Number.isFinite)
    );
    const bestWinRate = Math.max(
      0,
      ...withProjects.map((person) =>
        Number(person.portfolio_win_rate || 0)
      )
    );
    const topSourcing = Math.max(
      0,
      ...withProjects.map((person) =>
        Number(person.profiles_sourced || 0)
      )
    );

    return {
      fastestDelivery,
      fastestOutreach,
      bestWinRate,
      topSourcing,
    };
  }, [people]);

  const getHighlight = (person) => {
    if (Number(person.total_projects || 0) <= 0) return "";

    if (isBD) {
      if (
        highlightStats.bestWinRate > 0 &&
        Number(person.portfolio_win_rate || 0) ===
          highlightStats.bestWinRate
      ) {
        return "Top portfolio";
      }
      if (
        Number.isFinite(highlightStats.fastestDelivery) &&
        Number(person.average_delivery_time_hours) ===
          highlightStats.fastestDelivery
      ) {
        return "Fast delivery";
      }
      if (
        highlightStats.topSourcing > 0 &&
        Number(person.profiles_sourced || 0) ===
          highlightStats.topSourcing
      ) {
        return "Top sourcing";
      }
      return "";
    }

    if (
      highlightStats.topSourcing > 0 &&
      Number(person.profiles_sourced || 0) ===
        highlightStats.topSourcing
    ) {
      return "Top sourcing";
    }
    if (
      Number.isFinite(highlightStats.fastestOutreach) &&
      Number(person.median_first_outreach_hours) ===
        highlightStats.fastestOutreach
    ) {
      return "Fast outreach";
    }
    return "";
  };

  return (
    <>
      <SectionCard
        title={title}
        subtitle={subtitle}
        info={sectionInfo}
        right={
          <div className="rounded-2xl bg-[#365561]/5 px-3 py-2 text-xs font-semibold text-[#365561]">
            {people.length} {isBD ? "BDs" : "supporters"} · click a card for details
          </div>
        }
      >
        {loading ? (
          <LoadingSpinner
            label={`Loading ${isBD ? "BD" : "supporter"} KPIs...`}
          />
        ) : people.length === 0 ? (
          <EmptyState
            title={`No ${isBD ? "BD" : "supporter"} KPI data`}
          />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {people.map((person) => {
              const personKey = getKey(person);
              const isSelected = selectedKey === personKey;
              const isActive = person.is_active !== false;
              const highlight = getHighlight(person);

              return (
                <button
                  key={personKey}
                  type="button"
                  onClick={() => setSelectedKey(personKey)}
                  className={[
                    "group relative overflow-hidden rounded-3xl border bg-white p-4 text-left shadow-sm transition duration-200 focus:outline-none",
                    isSelected
                      ? "border-[#365561]/40 bg-[#365561]/5 shadow-[0_14px_35px_rgba(54,85,97,0.12)]"
                      : "border-slate-200 hover:-translate-y-0.5 hover:border-[#365561]/25 hover:shadow-[0_14px_32px_rgba(15,23,42,0.08)]",
                    isActive ? "opacity-100" : "opacity-[0.55]",
                  ].join(" ")}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <DashboardUserAvatar person={person} />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900">
                          {person.fullname ||
                            person.display_name ||
                            person.username ||
                            "Unknown user"}
                        </p>
                        <p className="mt-0.5 truncate text-[11px] font-medium text-slate-400">
                          {isBD
                            ? "Business Developer"
                            : person.username || "Project Supporter"}
                        </p>
                      </div>
                    </div>

                    {highlight ? (
                      <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-1 text-[9px] font-bold uppercase tracking-[0.08em] text-emerald-700">
                        {highlight}
                      </span>
                    ) : null}
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <div className="rounded-2xl bg-slate-50 px-3 py-2.5">
                      <p className="text-[10px] uppercase tracking-[0.1em] text-slate-400">
                        Worked on
                      </p>
                      <p className="mt-1 text-base font-semibold text-slate-900">
                        {formatNumber(person.total_projects)}
                      </p>
                      <p className="mt-0.5 text-[10px] text-slate-400">
                        {formatNumber(person.new_projects)} new ·{" "}
                        {formatNumber(person.carried_over_projects)} carried
                      </p>
                    </div>

                    <div className="rounded-2xl bg-[#365561]/5 px-3 py-2.5">
                      <p className="text-[10px] uppercase tracking-[0.1em] text-slate-400">
                        Decision win
                      </p>
                      <p className="mt-1 text-base font-semibold text-[#365561]">
                        {formatRateOrDash(
                          person.decision_win_rate,
                          person.decision_count
                        )}
                      </p>
                      <p className="mt-0.5 text-[10px] text-slate-400">
                        {formatNumber(person.closed_won)} /{" "}
                        {formatNumber(person.decision_count)} decisions
                      </p>
                    </div>

                    <div className="rounded-2xl bg-slate-50 px-3 py-2.5">
                      <p className="text-[10px] uppercase tracking-[0.1em] text-slate-400">
                        {person.portfolio_year ||
                          new Date().getFullYear()}{" "}
                        Portfolio
                      </p>
                      <p className="mt-1 text-sm font-semibold text-slate-800">
                        {formatRateOrDash(
                          person.portfolio_win_rate,
                          person.portfolio_total_projects
                        )}
                      </p>
                      <p className="mt-0.5 text-[10px] text-slate-400">
                        {formatPercentagePointDelta(
                          person.portfolio_vs_company_pp
                        )}{" "}
                        vs company
                      </p>
                    </div>

                    <div className="rounded-2xl bg-slate-50 px-3 py-2.5">
                      <p className="text-[10px] uppercase tracking-[0.1em] text-slate-400">
                        {isBD ? "Avg. delivery" : "Profiles sourced"}
                      </p>
                      <p className="mt-1 text-sm font-semibold text-slate-800">
                        {isBD
                          ? formatCompactDuration(
                              person.average_delivery_time_hours
                            )
                          : formatNumber(person.profiles_sourced)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-100 pt-3 text-[10px] text-slate-400">
                    {isBD ? (
                      <>
                        <span>
                          {formatNumber(person.profiles_sourced)} profiles sourced
                        </span>
                        <span>
                          Company{" "}
                          {formatPercent(
                            person.company_portfolio_win_rate
                          )}
                        </span>
                      </>
                    ) : (
                      <>
                        <span>
                          Response{" "}
                          {formatPercent(
                            person.consultant_response_rate
                          )}
                        </span>
                        <span>
                          Company{" "}
                          {formatPercent(
                            person.company_portfolio_win_rate
                          )}
                        </span>
                      </>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </SectionCard>

      <ExecutionKpiDrawer
        person={selectedDrawerPerson}
        roleType={roleType}
        periodLabel={periodLabel}
        onClose={() => setSelectedKey("")}
        onPrevious={() => {
          if (selectedIndex > 0) {
            setSelectedKey(getKey(people[selectedIndex - 1]));
          }
        }}
        onNext={() => {
          if (
            selectedIndex >= 0 &&
            selectedIndex < people.length - 1
          ) {
            setSelectedKey(getKey(people[selectedIndex + 1]));
          }
        }}
        hasPrevious={selectedIndex > 0}
        hasNext={
          selectedIndex >= 0 &&
          selectedIndex < people.length - 1
        }
        detailLoading={
          Boolean(selectedDetailKey) &&
          detailLoadingKey === selectedDetailKey
        }
        detailError={
          selectedDetailKey
            ? detailErrors[selectedDetailKey] || ""
            : ""
        }
      />
    </>
  );
}


export function WorldMapCard({
  consultantCountries = [],
  profilesAddedInPeriod = 0,
  periodLabel,
}) {
  const mapContainerRef = useRef(null);
  const mapAnimationRef = useRef(null);
  const [mapWidth, setMapWidth] = useState(0);
  const [activeCountry, setActiveCountry] = useState(null);
  const [activeRegionKey, setActiveRegionKey] = useState("world");
  const [hoveredCountry, setHoveredCountry] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const [mapPosition, setMapPosition] = useState({
    coordinates: [0, 6],
    zoom: 1,
  });

  const sortedCountries = useMemo(() => {
    return (consultantCountries || [])
      .slice()
      .sort(
        (a, b) =>
          Number(b.consultant_count || 0) - Number(a.consultant_count || 0) ||
          String(a.country || "").localeCompare(String(b.country || ""), undefined, {
            sensitivity: "base",
          })
      );
  }, [consultantCountries]);

  const regionConfig = useMemo(
    () =>
      consultantMapRegionConfig.find((region) => region.key === activeRegionKey) ||
      consultantMapRegionConfig[0],
    [activeRegionKey]
  );

  const countriesInActiveRegion = useMemo(() => {
    if (activeRegionKey === "world") return sortedCountries;

    return sortedCountries.filter(
      (item) => getConsultantMapRegionKey(item) === activeRegionKey
    );
  }, [activeRegionKey, sortedCountries]);

  useEffect(() => {
    setActiveCountry((current) => {
      if (countriesInActiveRegion.length === 0) return null;

      const matchedCurrent = countriesInActiveRegion.find(
        (item) => item.country === current?.country
      );

      return matchedCurrent || countriesInActiveRegion[0];
    });
  }, [countriesInActiveRegion]);

  useEffect(() => {
    const node = mapContainerRef.current;
    if (!node) return undefined;

    const updateWidth = () => {
      setMapWidth(node.clientWidth);
    };

    updateWidth();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateWidth);
      observer.observe(node);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  useEffect(() => {
    return () => {
      if (mapAnimationRef.current && typeof cancelAnimationFrame !== "undefined") {
        cancelAnimationFrame(mapAnimationRef.current);
      }
    };
  }, []);

  const totalConsultants = useMemo(
    () =>
      sortedCountries.reduce(
        (sum, item) => sum + Number(item.consultant_count || 0),
        0
      ),
    [sortedCountries]
  );

  const activeRegionConsultants = useMemo(
    () =>
      countriesInActiveRegion.reduce(
        (sum, item) => sum + Number(item.consultant_count || 0),
        0
      ),
    [countriesInActiveRegion]
  );

  const activeRegionMaxCountryCount = useMemo(
    () =>
      Math.max(
        0,
        ...countriesInActiveRegion.map((item) => Number(item.consultant_count || 0))
      ),
    [countriesInActiveRegion]
  );

  const consultantByMapName = useMemo(() => {
    const lookup = new Map();

    sortedCountries.forEach((item) => {
      lookup.set(normalizeMapCountryName(item.country), item);

      if (item.map_name) {
        lookup.set(normalizeMapCountryName(item.map_name), item);
      }
    });

    return lookup;
  }, [sortedCountries]);

  const consultantByNumericCode = useMemo(() => {
    const lookup = new Map();

    sortedCountries.forEach((item) => {
      if (!item.country_numeric_code) return;
      lookup.set(String(item.country_numeric_code).padStart(3, "0"), item);
    });

    return lookup;
  }, [sortedCountries]);

  const mappedCountries = useMemo(() => {
    return sortedCountries
      .map((item) => {
        const backendLongitude = Number(item.longitude);
        const backendLatitude = Number(item.latitude);
        const hasBackendCoordinates =
          Number.isFinite(backendLongitude) && Number.isFinite(backendLatitude);
        const directMatch = countryCoordinates[item.country];

        if (!hasBackendCoordinates && !directMatch) return null;

        return {
          ...item,
          coordinates: hasBackendCoordinates
            ? [backendLongitude, backendLatitude]
            : directMatch.coordinates,
          region_key: getConsultantMapRegionKey(item),
        };
      })
      .filter(Boolean);
  }, [sortedCountries]);

  const visibleMappedCountries = useMemo(() => {
    if (activeRegionKey === "world") return mappedCountries;

    return mappedCountries.filter(
      (item) => item.region_key === activeRegionKey
    );
  }, [activeRegionKey, mappedCountries]);

  const effectiveMapWidth = Math.max(320, mapWidth || 1100);
  const mapHeight = Math.max(
    360,
    Math.min(620, Math.round(effectiveMapWidth * 0.5))
  );
  const projectionScale = Math.min(
    effectiveMapWidth / 4.85,
    mapHeight / 2.45
  );

  const activeCountryRank = activeCountry
    ? countriesInActiveRegion.findIndex(
        (item) => item.country === activeCountry.country
      ) + 1
    : 0;

  const activeCountryShare =
    activeRegionConsultants > 0
      ? (Number(activeCountry?.consultant_count || 0) / activeRegionConsultants) * 100
      : 0;

  const activeScopeLabel =
    activeRegionKey === "world" ? "global network" : `${regionConfig.label} network`;

  const setTooltipPosition = (event, country) => {
    const node = mapContainerRef.current;
    if (!node || !country) return;

    const rect = node.getBoundingClientRect();
    const tooltipWidth = 190;
    const tooltipHeight = 72;
    const rawX = event.clientX - rect.left + 14;
    const rawY = event.clientY - rect.top + 14;

    setHoveredCountry(country);
    setHoverPosition({
      x: Math.max(12, Math.min(rawX, rect.width - tooltipWidth - 12)),
      y: Math.max(12, Math.min(rawY, rect.height - tooltipHeight - 12)),
    });
  };

  const cancelMapAnimation = () => {
    if (
      mapAnimationRef.current &&
      typeof cancelAnimationFrame !== "undefined"
    ) {
      cancelAnimationFrame(mapAnimationRef.current);
      mapAnimationRef.current = null;
    }
  };

  const animateMapTo = (targetPosition) => {
    cancelMapAnimation();

    if (typeof requestAnimationFrame === "undefined") {
      setMapPosition(targetPosition);
      return;
    }

    const fromCoordinates = Array.isArray(mapPosition.coordinates)
      ? mapPosition.coordinates
      : [0, 6];
    const fromZoom = Number(mapPosition.zoom || 1);
    const toCoordinates = targetPosition.coordinates;
    const toZoom = Number(targetPosition.zoom || 1);
    const duration = 460;
    let startTime = null;

    const easeInOut = (value) =>
      value < 0.5
        ? 4 * value * value * value
        : 1 - Math.pow(-2 * value + 2, 3) / 2;

    const animateFrame = (timestamp) => {
      if (startTime === null) startTime = timestamp;

      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = easeInOut(progress);

      setMapPosition({
        coordinates: [
          fromCoordinates[0] +
            (toCoordinates[0] - fromCoordinates[0]) * eased,
          fromCoordinates[1] +
            (toCoordinates[1] - fromCoordinates[1]) * eased,
        ],
        zoom: fromZoom + (toZoom - fromZoom) * eased,
      });

      if (progress < 1) {
        mapAnimationRef.current = requestAnimationFrame(animateFrame);
      } else {
        mapAnimationRef.current = null;
      }
    };

    mapAnimationRef.current = requestAnimationFrame(animateFrame);
  };

  const selectRegion = (regionKey) => {
    const nextRegion =
      consultantMapRegionConfig.find((region) => region.key === regionKey) ||
      consultantMapRegionConfig[0];

    setActiveRegionKey(nextRegion.key);
    setHoveredCountry(null);

    const nextRegionCountries =
      nextRegion.key === "world"
        ? sortedCountries
        : sortedCountries.filter(
            (item) => getConsultantMapRegionKey(item) === nextRegion.key
          );

    setActiveCountry(nextRegionCountries[0] || null);

    animateMapTo({
      coordinates: nextRegion.center,
      zoom: nextRegion.zoom,
    });
  };

  const resetMap = () => {
    selectRegion("world");
  };

  const zoomMap = (amount) => {
    cancelMapAnimation();

    setMapPosition((current) => ({
      ...current,
      zoom: Math.max(1, Math.min(4, Number(current.zoom || 1) + amount)),
    }));
  };

  return (
    <SectionCard
      title="Consultant distribution"
      subtitle="Global and regional consultant coverage by country"
      right={
        <div className="grid gap-2 text-right">
          <div className="rounded-2xl bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600">
            {formatNumber(activeRegionConsultants)} consultants ·{" "}
            {formatNumber(countriesInActiveRegion.length)} countries ·{" "}
            {regionConfig.label}
          </div>

          <div className="rounded-2xl bg-[#365561]/5 px-3 py-2 text-xs font-semibold text-[#365561]">
            +{formatNumber(profilesAddedInPeriod)} profiles added · {periodLabel}
          </div>
        </div>
      }
      className="h-full"
    >
      {sortedCountries.length === 0 ? (
        <EmptyState title="No consultant map data" />
      ) : (
        <div>
          <div
            ref={mapContainerRef}
            className="relative w-full overflow-hidden rounded-3xl border border-slate-200 bg-[linear-gradient(to_bottom,#f8fafc,#edf2f5)]"
            style={{ height: `${mapHeight}px` }}
            onMouseLeave={() => setHoveredCountry(null)}
          >
            <ComposableMap
              width={effectiveMapWidth}
              height={mapHeight}
              projection="geoEqualEarth"
              projectionConfig={{
                scale: projectionScale,
              }}
              style={{
                width: "100%",
                height: "100%",
                display: "block",
              }}
            >
              <ZoomableGroup
                center={mapPosition.coordinates}
                zoom={mapPosition.zoom}
                minZoom={1}
                maxZoom={4}
                onMoveEnd={(position) => setMapPosition(position)}
              >
                <Geographies geography={geoUrl}>
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      const geographyName = geo.properties?.name || "";
                      const geographyNumericCode =
                        geo.id === null || geo.id === undefined
                          ? ""
                          : String(geo.id).padStart(3, "0");
                      const country =
                        consultantByNumericCode.get(geographyNumericCode) ||
                        consultantByMapName.get(
                          normalizeMapCountryName(geographyName)
                        );
                      const countryRegionKey = country
                        ? getConsultantMapRegionKey(country)
                        : null;
                      const isInActiveRegion =
                        activeRegionKey === "world" ||
                        countryRegionKey === activeRegionKey;
                      const consultantCount = isInActiveRegion
                        ? Number(country?.consultant_count || 0)
                        : 0;
                      const isActive =
                        isInActiveRegion &&
                        normalizeMapCountryName(activeCountry?.country) ===
                          normalizeMapCountryName(geographyName);

                      const fill = isInActiveRegion
                        ? getConsultantCountryFill(
                            consultantCount,
                            activeRegionMaxCountryCount
                          )
                        : "#e7edf0";

                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill={fill}
                          stroke="#ffffff"
                          strokeWidth={isActive ? 1.25 : 0.65}
                          opacity={isInActiveRegion ? 1 : 0.62}
                          onMouseEnter={(event) => {
                            if (country && isInActiveRegion) {
                              setTooltipPosition(event, country);
                            }
                          }}
                          onMouseMove={(event) => {
                            if (country && isInActiveRegion) {
                              setTooltipPosition(event, country);
                            }
                          }}
                          onMouseLeave={() => {
                            if (country && isInActiveRegion) {
                              setHoveredCountry(null);
                            }
                          }}
                          onClick={() => {
                            if (country && isInActiveRegion) {
                              setActiveCountry(country);
                            }
                          }}
                          style={{
                            default: {
                              outline: "none",
                              cursor:
                                country && isInActiveRegion
                                  ? "pointer"
                                  : "default",
                            },
                            hover: {
                              fill:
                                country && isInActiveRegion
                                  ? "#496873"
                                  : isInActiveRegion
                                    ? "#cbd5e1"
                                    : "#e7edf0",
                              outline: "none",
                              cursor:
                                country && isInActiveRegion
                                  ? "pointer"
                                  : "default",
                            },
                            pressed: {
                              outline: "none",
                            },
                          }}
                        />
                      );
                    })
                  }
                </Geographies>

                {visibleMappedCountries.map((item) => {
                  const bubbleSize = getResponsiveBubbleSize(
                    item.consultant_count,
                    activeRegionMaxCountryCount
                  );
                  const isActive = activeCountry?.country === item.country;

                  return (
                    <Marker
                      key={item.country}
                      coordinates={item.coordinates}
                      onMouseEnter={(event) => setTooltipPosition(event, item)}
                      onMouseMove={(event) => setTooltipPosition(event, item)}
                      onMouseLeave={() => setHoveredCountry(null)}
                      onClick={() => setActiveCountry(item)}
                    >
                      {isActive ? (
                        <circle
                          r={bubbleSize + 4}
                          fill="none"
                          stroke="#365561"
                          strokeWidth={1.5}
                          strokeOpacity={0.45}
                        />
                      ) : null}

                      <circle
                        r={bubbleSize}
                        fill="#365561"
                        fillOpacity={isActive ? 0.95 : 0.78}
                        stroke="#ffffff"
                        strokeWidth={2}
                        style={{ cursor: "pointer" }}
                      />
                    </Marker>
                  );
                })}
              </ZoomableGroup>
            </ComposableMap>

            <div className="absolute left-3 top-3 z-20 flex w-[108px] flex-col gap-1 rounded-2xl border border-slate-200/80 bg-white/95 p-1.5 shadow-sm backdrop-blur sm:w-[126px]">
              {consultantMapRegionConfig.map((region) => {
                const isSelected = activeRegionKey === region.key;

                return (
                  <button
                    key={region.key}
                    type="button"
                    onClick={() => selectRegion(region.key)}
                    className={[
                      "w-full rounded-xl px-2.5 py-2 text-left text-[10px] font-semibold transition sm:text-[11px]",
                      isSelected
                        ? "bg-[#365561] text-white shadow-sm"
                        : "text-slate-600 hover:bg-slate-100 hover:text-[#365561]",
                    ].join(" ")}
                  >
                    {region.label}
                  </button>
                );
              })}
            </div>

            <div className="absolute right-3 top-3 z-20 flex items-center gap-1 rounded-2xl border border-slate-200/80 bg-white/95 p-1.5 shadow-sm backdrop-blur">
              <button
                type="button"
                onClick={() => zoomMap(0.5)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-xl text-base font-semibold text-slate-600 transition hover:bg-slate-100 hover:text-[#365561]"
                aria-label="Zoom map in"
              >
                +
              </button>
              <button
                type="button"
                onClick={() => zoomMap(-0.5)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-xl text-base font-semibold text-slate-600 transition hover:bg-slate-100 hover:text-[#365561]"
                aria-label="Zoom map out"
              >
                −
              </button>
              <button
                type="button"
                onClick={resetMap}
                className="rounded-xl px-2.5 py-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500 transition hover:bg-slate-100 hover:text-[#365561]"
              >
                World
              </button>
            </div>

            {activeCountry ? (
              <div className="absolute bottom-4 left-4 z-20 min-w-[210px] rounded-2xl border border-white/70 bg-white/95 px-4 py-3 shadow-[0_14px_34px_rgba(15,23,42,0.12)] backdrop-blur">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-900">
                      {activeCountry.country}
                    </p>
                    <p className="mt-1 text-2xl font-semibold text-[#365561]">
                      {formatNumber(activeCountry.consultant_count)}
                    </p>
                    <p className="text-[11px] text-slate-400">
                      consultants · {formatPercent(activeCountryShare)} of {activeScopeLabel}
                    </p>
                  </div>

                  {activeCountryRank > 0 ? (
                    <span className="shrink-0 rounded-full bg-[#365561]/8 px-2 py-1 text-[10px] font-semibold text-[#365561]">
                      #{activeCountryRank}
                    </span>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="absolute bottom-4 left-4 z-20 rounded-2xl border border-white/70 bg-white/95 px-4 py-3 text-xs text-slate-500 shadow-sm backdrop-blur">
                No consultant data in {regionConfig.label}
              </div>
            )}

            <div className="absolute bottom-4 right-4 z-20 rounded-2xl border border-white/70 bg-white/90 px-3 py-2 text-[10px] text-slate-500 shadow-sm backdrop-blur">
              <div className="mb-1.5 flex items-center justify-between gap-4">
                <span>{regionConfig.label} consultant density</span>
                <span className="font-semibold text-slate-700">High</span>
              </div>
              <div className="flex gap-1">
                {["#dbe4ea", "#bdcdd3", "#91a9b1", "#67838d", "#365561"].map(
                  (color) => (
                    <span
                      key={color}
                      className="h-2.5 w-7 rounded-full"
                      style={{ backgroundColor: color }}
                    />
                  )
                )}
              </div>
            </div>

            {hoveredCountry ? (
              <div
                className="pointer-events-none absolute z-30 min-w-[175px] rounded-2xl border border-slate-200 bg-white px-3 py-2.5 shadow-[0_14px_30px_rgba(15,23,42,0.14)]"
                style={{
                  left: `${hoverPosition.x}px`,
                  top: `${hoverPosition.y}px`,
                }}
              >
                <p className="text-xs font-semibold text-slate-900">
                  {hoveredCountry.country}
                </p>
                <p className="mt-0.5 text-[11px] text-slate-500">
                  {formatNumber(hoveredCountry.consultant_count)} consultants
                </p>
              </div>
            ) : null}
          </div>

          <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
            {countriesInActiveRegion.length > 0 ? (
              countriesInActiveRegion.map((item, index) => {
                const isActive = activeCountry?.country === item.country;

                return (
                  <button
                    key={item.country}
                    type="button"
                    onClick={() => setActiveCountry(item)}
                    className={[
                      "flex shrink-0 items-center gap-2 rounded-2xl border px-3 py-2 text-xs transition",
                      isActive
                        ? "border-[#365561]/25 bg-[#365561]/5 text-[#365561]"
                        : "border-slate-200 bg-white text-slate-600 hover:border-[#365561]/20 hover:bg-slate-50",
                    ].join(" ")}
                  >
                    <span className="text-[10px] font-semibold text-slate-400">
                      #{index + 1}
                    </span>
                    <span className="font-semibold">{item.country}</span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-700">
                      {formatNumber(item.consultant_count)}
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-400">
                No consultant data in {regionConfig.label}
              </div>
            )}
          </div>
        </div>
      )}
    </SectionCard>
  );
}


const getDashboardFirstRowCount = (layout) => {
  if (typeof window === "undefined") {
    return layout === "ongoing" ? 6 : layout === "wins" ? 3 : 5;
  }

  const width = window.innerWidth;

  if (layout === "ongoing") {
    if (width >= 1536) return 6;
    if (width >= 1280) return 5;
    if (width >= 1024) return 4;
    if (width >= 768) return 2;
    return 1;
  }

  if (layout === "wins") {
    if (width >= 1280) return 3;
    if (width >= 768) return 2;
    return 1;
  }

  if (width >= 1536) return 5;
  if (width >= 1280) return 4;
  if (width >= 1024) return 3;
  if (width >= 768) return 2;
  return 1;
};

function useDashboardFirstRowCount(layout) {
  const [count, setCount] = useState(() => getDashboardFirstRowCount(layout));

  useEffect(() => {
    const update = () => setCount(getDashboardFirstRowCount(layout));

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [layout]);

  return count;
}

const getDashboardCallStatus = (call, nowValue) => {
  const now = new Date(nowValue || Date.now());
  const start = new Date(call?.start_time);
  const parsedEnd = call?.end_time ? new Date(call.end_time) : null;
  const end =
    parsedEnd && !Number.isNaN(parsedEnd.getTime()) ? parsedEnd : start;

  if (Number.isNaN(start.getTime())) {
    return {
      status: "unknown",
      badge: "Unknown",
      label: "No start time",
    };
  }

  if (now >= start && now <= end) {
    return {
      status: "live",
      badge: "Live",
      label: "Ongoing call",
    };
  }

  if (now > end) {
    return {
      status: "conducted",
      badge: "Conducted",
      label: "Conducted",
    };
  }

  const diff = Math.max(start - now, 0);
  const totalMinutes = Math.floor(diff / (1000 * 60));
  const totalHours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const minutes = totalMinutes % 60;

  let label = "";

  if (days > 0) {
    label = `${days} ${days === 1 ? "day" : "days"} ${String(hours).padStart(
      2,
      "0"
    )}:${String(minutes).padStart(2, "0")}h`;
  } else if (totalHours > 0) {
    label = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )} hours`;
  } else {
    label = `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
  }

  return {
    status: "upcoming",
    badge: "Upcoming",
    label,
  };
};

function DashboardCallCard({ call, nowValue }) {
  const callStatus = getDashboardCallStatus(call, nowValue);
  const startDate = new Date(call.start_time);

  const month = Number.isNaN(startDate.getTime())
    ? "-"
    : startDate.toLocaleString("en-US", { month: "short" });
  const day = Number.isNaN(startDate.getTime()) ? "-" : startDate.getDate();
  const weekday = Number.isNaN(startDate.getTime())
    ? "-"
    : startDate.toLocaleString("en-US", { weekday: "short" });
  const time = Number.isNaN(startDate.getTime())
    ? "-"
    : startDate.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });

  const badgeClass =
    callStatus.status === "live"
      ? "bg-emerald-50 text-emerald-700"
      : callStatus.status === "conducted"
        ? "bg-slate-100 text-slate-600"
        : callStatus.status === "upcoming"
          ? "bg-sky-50 text-sky-700"
          : "bg-slate-100 text-slate-500";

  const statusTextClass =
    callStatus.status === "live"
      ? "text-emerald-600"
      : callStatus.status === "conducted"
        ? "text-slate-600"
        : "text-slate-900";

  return (
    <div
      className="group cursor-pointer rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-[#365561]/20 hover:shadow-[0_16px_36px_rgba(15,23,42,0.10)]"
      onClick={() =>
        window.open(
          `/app/ProjectDetails/${call.project_id}`,
          "_blank",
          "noopener,noreferrer"
        )
      }
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-[11px] font-medium text-slate-400 group-hover:text-[#365561]">
          Open project →
        </p>
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${badgeClass}`}
        >
          {callStatus.badge}
        </span>
      </div>

      <div className="mb-3">
        <p className="truncate text-sm font-semibold text-slate-900">
          {call.project_name}
        </p>
        <p className="truncate text-xs text-slate-500">
          Owner: {call.owner_name}
        </p>
        <p className="truncate text-xs text-slate-500">
          Expert: {call.expert_name}
        </p>
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="w-[100px] overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="bg-[#365561] px-3 py-1.5 text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white">
              {month}
            </p>
          </div>

          <div className="px-3 py-1 text-center">
            <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400">
              {weekday}
            </p>
            <p className="mt-1 text-4xl font-semibold leading-none text-slate-900">
              {day}
            </p>
            <p className="px-2 py-1 text-[11px] font-semibold text-slate-600">
              {time}
            </p>
          </div>
        </div>

        <div className="min-w-0 space-y-1 text-right">
          <p className="text-xs text-slate-500">
            {callStatus.status === "upcoming" ? "Starts in" : "Status"}
          </p>
          <p className={`text-sm font-semibold ${statusTextClass}`}>
            {callStatus.label}
          </p>

          <p className="mt-2 text-xs text-slate-500">Call type</p>
          <p className="text-sm font-medium text-slate-700">
            {call.call_type}
          </p>
        </div>
      </div>

      <div className="mt-3 flex justify-between text-[11px] text-slate-400">
        <span>{formatDateTime(call.start_time)}</span>
        <span>
          {call.end_time
            ? new Date(call.end_time).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })
            : ""}
        </span>
      </div>
    </div>
  );
}

function DashboardCallsSection({ title, subtitle, calls, loading, nowValue }) {
  const [showAll, setShowAll] = useState(false);
  const firstRowCount = useDashboardFirstRowCount("calls");

  useEffect(() => {
    setShowAll(false);
  }, [calls]);

  const callSummary = useMemo(() => {
    return (calls || []).reduce(
      (acc, call) => {
        const status = getDashboardCallStatus(call, nowValue).status;
        acc.total += 1;
        if (status === "conducted") acc.conducted += 1;
        if (status === "upcoming") acc.upcoming += 1;
        if (status === "live") acc.live += 1;
        return acc;
      },
      { total: 0, conducted: 0, upcoming: 0, live: 0 }
    );
  }, [calls, nowValue]);

  const visibleCalls = showAll ? calls : calls.slice(0, firstRowCount);
  const hasMoreThanOneRow = calls.length > firstRowCount;

  return (
    <SectionCard
      title={title}
      subtitle={subtitle}
      right={
        <div className="flex flex-wrap justify-end gap-2">
          <span className="rounded-full bg-[#365561]/8 px-3 py-1.5 text-xs font-semibold text-[#365561]">
            {formatNumber(callSummary.total)} total
          </span>
          <span className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600">
            {formatNumber(callSummary.conducted)} conducted
          </span>
          <span className="rounded-full bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-700">
            {formatNumber(callSummary.upcoming)} upcoming
          </span>
          {callSummary.live > 0 ? (
            <span className="rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
              {formatNumber(callSummary.live)} live
            </span>
          ) : null}
        </div>
      }
    >
      {loading ? (
        <LoadingSpinner label="Loading calls..." />
      ) : calls.length === 0 ? (
        <EmptyState title="No calls found for this range" />
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
            {visibleCalls.map((call) => (
              <DashboardCallCard
                key={call.invitation_id}
                call={call}
                nowValue={nowValue}
              />
            ))}
          </div>

          {hasMoreThanOneRow ? (
            <div className="mt-5 flex justify-center">
              <button
                type="button"
                onClick={() => setShowAll((current) => !current)}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-[#365561] shadow-sm transition hover:border-[#365561]/25 hover:bg-[#365561]/5 focus:outline-none"
              >
                {showAll ? "Show less" : "See more"}
              </button>
            </div>
          ) : null}
        </>
      )}
    </SectionCard>
  );
}

const buildWeeklyOwnerChartData = (items, type) => {
  const grouped = {};

  items.forEach((item) => {
    const owner = item.owner_name || "Unknown";

    if (!grouped[owner]) {
      grouped[owner] = {
        owner,
        count: 0,
        items: [],
      };
    }

    grouped[owner].count += 1;
    grouped[owner].items.push({
      ...item,
      type,
    });
  });

  return Object.values(grouped).sort((a, b) => b.count - a.count);
};


function WeeklySnapshotList({ title, subtitle, items = [], variant }) {
  const groupedItems = useMemo(
    () => buildWeeklyOwnerChartData(items, variant).slice(0, 4),
    [items, variant]
  );

  const allGroupedItems = useMemo(
    () => buildWeeklyOwnerChartData(items, variant),
    [items, variant]
  );

  const [activeOwner, setActiveOwner] = useState("");

  useEffect(() => {
    if (
      activeOwner &&
      groupedItems.length > 0 &&
      !groupedItems.some((group) => group.owner === activeOwner)
    ) {
      setActiveOwner("");
    }
  }, [groupedItems, activeOwner]);

  const total = items.length;

  const getProjectClientName = (item) =>
    item.client_name ||
    item.clientName ||
    item.account_name ||
    item.accountName ||
    item.client ||
    "Unknown client";

  const getProjectSupporterName = (item, fallbackOwner) =>
    item.supporter_name ||
    item.project_supporter ||
    item.Project_Supporter ||
    item.supporter ||
    fallbackOwner ||
    "Unknown supporter";

  const openProject = (projectId) => {
    if (!projectId) return;

    window.open(
      `/app/ProjectDetails/${projectId}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <div className="rounded-3xl border border-slate-200 bg-white pt-12 pl-12 pr-12 pb-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">{title}</p>
          <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>
        </div>

        <span className="shrink-0 rounded-2xl bg-[#365561]/10 px-3 py-1.5 text-xs font-semibold text-[#365561]">
          {formatNumber(total)}
        </span>
      </div>

      {groupedItems.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-3 py-6 text-center text-xs font-medium text-slate-500">
          No records this week
        </div>
      ) : (
        <div className="space-y-2">
          {groupedItems.map((group) => {
            const percent = total > 0 ? (group.count / total) * 100 : 0;
            const isActive = activeOwner === group.owner;

            return (
              <div
                key={`${variant}-${group.owner}`}
                className="relative pr-3"
                onMouseEnter={() => setActiveOwner(group.owner)}
                onMouseLeave={() => setActiveOwner((current) => current === group.owner ? "" : current)}
              >
                <button
                  type="button"
                  onClick={() =>
                    setActiveOwner((current) =>
                      current === group.owner ? "" : group.owner
                    )
                  }
                  className="w-full text-left focus:outline-none"
                >
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <p className="truncate text-xs font-semibold text-slate-800">
                      {group.owner}
                    </p>

                    <p className="text-xs font-semibold text-[#365561]">
                      {formatNumber(group.count)}
                    </p>
                  </div>

                  <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-[#365561]"
                      style={{ width: `${Math.max(percent, 6)}%` }}
                    />
                  </div>
                </button>

                {isActive ? (
                  <div
                    className="absolute left-0 -top-1 z-[80] w-[360px] rounded-3xl border border-slate-200 bg-white p-3 shadow-[0_24px_70px_rgba(15,23,42,0.22)]"
                    onMouseEnter={() => setActiveOwner(group.owner)}
                    onMouseLeave={() => setActiveOwner("")}
                  >
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900">
                          {group.owner}
                        </p>
                        <p className="text-[11px] text-slate-400">
                          {formatNumber(group.count)} projects · click a row to open
                        </p>
                      </div>

                      <span className="shrink-0 rounded-full bg-[#365561]/10 px-2.5 py-1 text-[11px] font-semibold text-[#365561]">
                        {variant === "call" ? "Calls" : "LT"}
                      </span>
                    </div>

                    <div className="h-px bg-slate-200" />

                    <div className="mt-2 max-h-[230px] space-y-1.5 overflow-y-auto pr-1">
                      {group.items.map((item, index) => (
                        <button
                          key={
                            variant === "call"
                              ? `snapshot-call-${item.invitation_id || index}`
                              : `snapshot-lt-${item.project_id || index}`
                          }
                          type="button"
                          onClick={() => openProject(item.project_id)}
                          className="group flex w-full items-start justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-left transition hover:border-[#365561]/30 hover:bg-[#365561]/5 focus:outline-none"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-xs font-semibold text-slate-900 group-hover:text-[#365561]">
                              {item.project_name || "Untitled project"}
                            </p>

                            <p className="mt-1 truncate text-[11px] leading-3 text-slate-500">
                              Client: {getProjectClientName(item)}
                            </p>

                            <p className="mt-1 truncate text-[11px] leading-3 text-slate-500">
                              Supporter: {getProjectSupporterName(item, group.owner)}
                            </p>
                          </div>

                          <span className="shrink-0 rounded-full bg-white px-2 py-1 text-[10px] font-semibold text-[#365561] shadow-sm">
                            Open
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}

          {allGroupedItems.length > 4 ? (
            <p className="pt-1 text-center text-[11px] font-medium text-slate-400">
              +{allGroupedItems.length - 4} more owners
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

function RangeWinsSection({
  expertCalls = [],
  longTermProjects = [],
  loading,
  periodLabel,
}) {
  const [showAll, setShowAll] = useState(false);
  const firstRowCount = useDashboardFirstRowCount("wins");

  const wins = useMemo(() => {
    const sortAlphabetically = (a, b) =>
      String(a?.project_name || "").localeCompare(
        String(b?.project_name || ""),
        undefined,
        { sensitivity: "base" }
      ) ||
      String(a?.expert_name || "").localeCompare(
        String(b?.expert_name || ""),
        undefined,
        { sensitivity: "base" }
      );

    const sortedExpertCalls = expertCalls
      .slice()
      .sort(sortAlphabetically)
      .map((item) => ({ ...item, dashboardWinType: "expert_call" }));

    const sortedLongTermProjects = longTermProjects
      .slice()
      .sort(sortAlphabetically)
      .map((item) => ({ ...item, dashboardWinType: "long_term" }));

    return [...sortedExpertCalls, ...sortedLongTermProjects];
  }, [expertCalls, longTermProjects]);

  useEffect(() => {
    setShowAll(false);
  }, [expertCalls, longTermProjects, periodLabel]);

  const visibleWins = showAll ? wins : wins.slice(0, firstRowCount);
  const hasMoreThanOneRow = wins.length > firstRowCount;

  const openProject = (projectID) => {
    if (!projectID) return;
    window.open(`/app/ProjectDetails/${projectID}`, "_blank", "noopener,noreferrer");
  };

  return (
    <SectionCard
      title={`Wins · ${periodLabel}`}
      subtitle="Participated Expert Calls first, followed by participated long-term consultants"
      right={
        <div className="flex flex-wrap gap-2">
          <span className="rounded-full bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-700">
            {expertCalls.length} Expert-Call
          </span>
          <span className="rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
            {longTermProjects.length} Long-term consultants
          </span>
        </div>
      }
    >
      {loading ? (
        <LoadingSpinner label="Loading wins..." />
      ) : wins.length === 0 ? (
        <EmptyState title="No participated consultants found for this range" />
      ) : (
        <>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {visibleWins.map((item, index) => {
              const isExpertCall = item.dashboardWinType === "expert_call";
              const utilizationIntensity = Number(item.utilization_intensity);
              const hasUtilizationIntensity =
                item.utilization_intensity !== null &&
                item.utilization_intensity !== undefined &&
                String(item.utilization_intensity).trim() !== "" &&
                Number.isFinite(utilizationIntensity);

              return (
                <button
                  key={`win-${item.project_expert_id || `${item.project_id || "project"}-${item.expert_id || index}`}`}
                  type="button"
                  onClick={() => openProject(item.project_id)}
                  className="rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-[#365561]/25 hover:bg-[#365561]/5 hover:shadow-[0_14px_32px_rgba(15,23,42,0.08)]"
                >
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900">
                        {item.project_name || "Untitled project"}
                      </p>
                    </div>

                    <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
                      {item.has_extension ? (
                        <span className="rounded-full bg-violet-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-violet-700">
                          Extension
                        </span>
                      ) : null}

                      <span
                        className={[
                          "rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.08em]",
                          isExpertCall
                            ? "bg-sky-50 text-sky-700"
                            : "bg-emerald-50 text-emerald-700",
                        ].join(" ")}
                      >
                        {isExpertCall ? "Expert-Call" : "Long-term"}
                      </span>
                    </div>
                  </div>

                  <div className="text-xs text-slate-500">
                    <div className="space-y-0.5">
                      <p>
                        Owner:{" "}
                        <span className="font-semibold text-slate-700">
                          {item.owner_name || "Unknown"}
                        </span>
                      </p>
                      <p>
                        Supporters:{" "}
                        <span className="font-semibold text-slate-700">
                          {item.supporter_name || "Unknown"}
                        </span>
                      </p>
                    </div>

                    <div className="mt-3 space-y-0.5">
                      <p>
                        Client:{" "}
                        <span className="font-semibold text-slate-700">
                          {item.client_name || "Unknown client"}
                        </span>
                      </p>
                      <p>
                        Expert:{" "}
                        <span className="font-semibold text-slate-700">
                          {item.expert_name || "Unknown expert"}
                        </span>
                      </p>
                    </div>

                    <div className="mt-3 space-y-0.5 text-slate-500">
                      <p>Project created: {formatDate(item.project_created_date)}</p>
                      <p>Participated: {formatDate(item.date_participated)}</p>
                      {isExpertCall ? (
                        <p>
                          Scheduled call: {formatScheduledCallRange(
                            item.scheduled_call_start || item.scheduled_call_date,
                            item.scheduled_call_end
                          )}
                        </p>
                      ) : (
                        <p>
                          Total days: {item.total_days || "0"}
                          {hasUtilizationIntensity
                            ? ` (${utilizationIntensity.toLocaleString(undefined, { maximumFractionDigits: 1 })}%)`
                            : ""}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {hasMoreThanOneRow ? (
            <div className="mt-5 flex justify-center">
              <button
                type="button"
                onClick={() => setShowAll((current) => !current)}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-[#365561] shadow-sm transition hover:border-[#365561]/25 hover:bg-[#365561]/5 focus:outline-none"
              >
                {showAll ? "Show less" : "See more"}
              </button>
            </div>
          ) : null}
        </>
      )}
    </SectionCard>
  );
}

function WeeklyBarTooltip({ active, payload, variant }) {
  if (!active || !payload || payload.length === 0) return null;

  const data = payload[0]?.payload;
  const items = data?.items || [];

  if (!items.length) return null;

  return (
    <div className="min-w-[280px] max-w-[360px] rounded-3xl border border-slate-200 bg-white p-3 shadow-[0_24px_70px_rgba(15,23,42,0.20)]">
      <div className="mb-2 flex items-center justify-between gap-3 px-1">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900">
            {data.owner}
          </p>
          <p className="text-[11px] text-slate-400">
            Click a project card to open details
          </p>
        </div>

        <span className="shrink-0 rounded-full bg-[#365561]/10 px-2.5 py-1 text-[11px] font-semibold text-[#365561]">
          {data.count} total
        </span>
      </div>

      <div className="mb-2 h-px bg-slate-200" />

      <div className="max-h-[230px] space-y-1.5 overflow-y-auto pr-1">
        {items.map((item) => (
          <a
            key={
              variant === "call"
                ? `call-tip-${item.invitation_id}`
                : `lt-tip-${item.project_id}`
            }
            href={`/app/ProjectDetails/${item.project_id}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="group block rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 transition hover:border-[#365561]/30 hover:bg-[#365561]/5"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-xs font-semibold leading-4 text-slate-900 group-hover:text-[#365561]">
                  {item.project_name}
                </p>

                <p className="mt-1 truncate text-[11px] leading-3 text-slate-500">
                  {variant === "call"
                    ? `Expert: ${item.expert_name}`
                    : `Supporter: ${item.supporter_name}`}
                </p>

                <p className="mt-1 text-[11px] leading-3 text-slate-400">
                  {variant === "call"
                    ? formatDateTime(item.start_time)
                    : `Total days: ${item.total_days}`}
                </p>
              </div>

              <span className="shrink-0 rounded-full bg-white px-2 py-1 text-[10px] font-semibold text-[#365561] shadow-sm">
                Open
              </span>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

function WeeklyOwnerBarSection({
  title,
  subtitle,
  items,
  loading,
  variant,
  compact = false,
  mini = false,
}) {
  const chartData = useMemo(
    () => buildWeeklyOwnerChartData(items, variant),
    [items, variant]
  );

  const totalItems = items.length;

  return (
    <SectionCard
      title={title}
      subtitle={subtitle}
      right={
        <div className="rounded-2xl bg-[#365561]/5 px-3 py-2 text-xs font-semibold text-[#365561]">
          {totalItems} this week
        </div>
      }
      className={[
        "h-full overflow-visible",
        mini ? "p-4" : "",
      ].join(" ")}
    >
      {loading ? (
        <LoadingSpinner label="Loading weekly data..." />
      ) : chartData.length === 0 ? (
        <EmptyState title="No records found this week" />
      ) : (
        <div className={mini ? "h-[205px] w-full" : compact ? "h-[285px] w-full" : "h-[320px] w-full"}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              barGap={8}
              barCategoryGap={compact ? "42%" : "35%"}
              margin={{ top: mini ? 2 : 8, right: 8, left: -10, bottom: mini ? 0 : compact ? 8 : 0 }}
            >
              <defs>
                <linearGradient id={`weeklyGradient-${variant}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#365561" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="#365561" stopOpacity={0.58} />
                </linearGradient>
              </defs>

              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />

              <XAxis
                dataKey="owner"
                tickLine={false}
                axisLine={false}
                tick={{ fill: "#64748b", fontSize: mini ? 9 : compact ? 10 : 12 }}
                interval={0}
              />

              <YAxis
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                tick={{ fill: "#64748b", fontSize: mini ? 9 : compact ? 10 : 12 }}
              />

              <Tooltip
                cursor={{ fill: "rgba(54,85,97,0.06)" }}
                allowEscapeViewBox={{ x: true, y: true }}
                wrapperStyle={{
                  pointerEvents: "auto",
                  zIndex: 9999,
                }}
                content={<WeeklyBarTooltip variant={variant} />}
              />

              <Bar
                dataKey="count"
                fill={`url(#weeklyGradient-${variant})`}
                radius={[10, 10, 0, 0]}
                name="Count"
                maxBarSize={mini ? 28 : compact ? 34 : 40}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </SectionCard>
  );
}

function ProjectSupporterLoadTooltip({ active, payload }) {
  if (!active || !payload || payload.length === 0) return null;

  const data = payload[0]?.payload;
  if (!data) return null;

  return (
    <div className="min-w-[200px] max-w-[320px] rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-[0_18px_40px_rgba(15,23,42,0.14)]">
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="truncate text-sm font-semibold text-slate-900">
          {data.supporter}
        </p>

        <span className="shrink-0 rounded-full bg-[#365561]/10 px-2 py-0.5 text-[11px] font-semibold text-[#365561]">
          {data.total_projects} projects
        </span>
      </div>

      <div className="mb-2 h-px bg-slate-200" />

      <div className="space-y-[2px] text-xs leading-4 text-slate-600">
        <p>Not Started: {data.not_started}</p>
        <p>Profiling: {data.profiling}</p>
        <p>Waiting Input: {data.waiting_input}</p>
        <p>Contracting: {data.contracting}</p>
        <p className="text-green-600">Won: {data.closed_won}</p>
        <p className="text-red-500">Lost: {data.closed_lost}</p>
        {data.other > 0 ? <p>Other: {data.other}</p> : null}
      </div>

      <div className="my-2 h-px bg-slate-200" />
      <div className="space-y-1 text-xs text-slate-500">
        <div className="flex justify-between gap-4">
          <span>New · carried over</span>
          <span>{formatNumber(data.new_projects)} · {formatNumber(data.carried_over_projects)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span>Active now</span>
          <span>{formatNumber(data.active_now_projects)}</span>
        </div>
      </div>
    </div>
  );
}

function ProjectSupporterLoadSection({ items = [], loading, periodLabel }) {
  const chartData = useMemo(() => {
    return (items || [])
      .map((item) => ({
        supporter: item.supporter_name || item.supporter || "Unknown",
        not_started: Number(item.not_started || 0),
        profiling: Number(item.profiling || 0),
        waiting_input: Number(item.waiting_input || 0),
        contracting: Number(item.contracting || 0),
        closed_won: Number(item.closed_won || 0),
        closed_lost: Number(item.closed_lost || 0),
        other: Number(item.other || 0),
        total_projects: Number(item.total_projects || item.project_count || item.count || 0),
        new_projects: Number(item.new_projects || 0),
        carried_over_projects: Number(item.carried_over_projects || 0),
        active_now_projects: Number(item.active_now_projects || 0),
        period_wins: Number(item.period_wins || 0),
        period_losses: Number(item.period_losses || 0),
        is_active: item.is_active !== false,
        user_id: item.user_id ?? null,
      }))
      .sort((a, b) =>
        a.supporter.localeCompare(b.supporter, undefined, {
          sensitivity: "base",
        })
      );
  }, [items]);

  const totalProjects = chartData.reduce(
    (sum, item) => sum + Number(item.total_projects || 0),
    0
  );

  return (
    <SectionCard
      title="Project Supporter Workload & Activity"
      subtitle={`Projects worked on during ${periodLabel}, including carry-over workload`}
      info="Shows projects supported at any point in the selected period, including projects created earlier that continued into the range. Bars show each project’s current status, so workload is not limited to newly created requests."
      // right={
      //   <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-slate-500">
      //     <span className="inline-flex items-center gap-1.5">
      //       <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
      //       Not Started
      //     </span>
      //     <span className="inline-flex items-center gap-1.5">
      //       <span className="h-2.5 w-2.5 rounded-full bg-sky-400" />
      //       Profiling
      //     </span>
      //     <span className="inline-flex items-center gap-1.5">
      //       <span className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
      //       Waiting Input
      //     </span>
      //     <span className="inline-flex items-center gap-1.5">
      //       <span className="h-2.5 w-2.5 rounded-full bg-violet-400" />
      //       Contracting
      //     </span>
      //     <span className="inline-flex items-center gap-1.5">
      //       <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
      //       Won
      //     </span>
      //     <span className="inline-flex items-center gap-1.5">
      //       <span className="h-2.5 w-2.5 rounded-full bg-orange-400" />
      //       Lost
      //     </span>
      //   </div>
      // }
    >
      {loading ? (
        <LoadingSpinner label="Loading supporter workload..." />
      ) : chartData.length === 0 ? (
        <EmptyState
          title="No supporter workload data"
          description="Add project_supporter_load to the dashboard overview payload."
        />
      ) : (
        <>
          <div className="h-[380px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} barGap={10}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />

                <XAxis
                  dataKey="supporter"
                  tickLine={false}
                  axisLine={false}
                  tick={(props) => (
                    <UserActivityAxisTick
                      {...props}
                      items={chartData}
                      nameKey="supporter"
                      showInactiveLabel={false}
                      italicInactive={false}
                    />
                  )}
                />

                <YAxis
                  allowDecimals={false}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: "#64748b", fontSize: 12 }}
                />

                <Tooltip content={<ProjectSupporterLoadTooltip />} />
                <Legend 
                  wrapperStyle={{
                    fontSize: "10px",
                    color: "#64748b",
                    fontWeight: 500,
                    paddingTop: "12px"
                  }}
                />

                <Bar
                  dataKey="not_started"
                  stackId="projects"
                  fill="#e83131"
                  name="Not Started"
                />
                <Bar
                  dataKey="profiling"
                  stackId="projects"
                  fill="rgb(255, 192, 0)"
                  name="Profiling"
                />
                <Bar
                  dataKey="waiting_input"
                  stackId="projects"
                  fill="rgb(0, 176, 80)"
                  name="Waiting Input"
                />
                <Bar
                  dataKey="contracting"
                  stackId="projects"
                  fill="#0ea5e9"
                  name="Contracting"
                />
                <Bar
                  dataKey="closed_won"
                  stackId="projects"
                  fill="rgb(198, 224, 180)"
                  name="Won"
                />
                <Bar
                  dataKey="closed_lost"
                  stackId="projects"
                  fill="rgb(250, 218, 196)"
                  name="Lost"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </SectionCard>
  );
}

function OngoingProjectCard({ proj }) {
  const progress = Number(proj.progress_percent || 0);
  const remainingDays = Number(proj.remaining_days || 0);

  return (
    <div
      className="group cursor-pointer rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-[#365561]/20 hover:shadow-[0_16px_36px_rgba(15,23,42,0.10)]"
      onClick={() =>
        window.open(
          `/app/ProjectDetails/${proj.project_id}`,
          "_blank",
          "noopener,noreferrer"
        )
      }
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-[11px] text-slate-400 group-hover:text-[#365561]">
          Open details →
        </p>

        {proj.has_extension ? (
          <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-700">
            Extended
          </span>
        ) : (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">
            Base
          </span>
        )}
      </div>

      <div className="mb-4">
        <p className="truncate text-sm font-semibold text-slate-900">
          {proj.project_name}
        </p>
        <p className="text-xs text-slate-500">Owner: {proj.owner_name}</p>
        <p className="mt-0.5 text-xs text-slate-500">Consultant: <span className="font-semibold text-slate-700">{proj.expert_name || "Unknown consultant"}</span></p>
      </div>

      <div className="rounded-2xl bg-slate-50 p-3">
        <div className="mb-2 flex items-center justify-between text-xs">
          <span className="font-medium text-slate-500">Progress</span>
          <span className="font-semibold text-slate-900">
            {formatPercent(progress)}
          </span>
        </div>

        <MiniProgress
          value={progress}
          className={
            progress >= 85
              ? "bg-rose-500"
              : progress >= 60
                ? "bg-amber-500"
                : "bg-[#365561]"
          }
        />

        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
          <div className="rounded-xl bg-white p-2">
            <p className="text-slate-400">Used</p>
            <p className="font-semibold text-slate-800">
              {formatNumber(proj.used_days)} days
            </p>
          </div>
          <div className="rounded-xl bg-white p-2">
            <p className="text-slate-400">Remaining</p>
            <p
              className={[
                "font-semibold",
                remainingDays <= 7 ? "text-rose-600" : "text-slate-800",
              ].join(" ")}
            >
              {formatNumber(remainingDays)} days
            </p>
          </div>
        </div>
      </div>

      <div className="mt-3 flex justify-between gap-2 text-[11px] text-slate-400">
        <span>{formatDate(proj.start_date)}</span>
        <span>{formatDate(proj.end_date)}</span>
      </div>
    </div>
  );
}

function OngoingProjectsSection({ projects = [], periodLabel }) {
  const [showAll, setShowAll] = useState(false);
  const firstRowCount = useDashboardFirstRowCount("ongoing");

  const sortedProjects = useMemo(() => {
    return projects
      .slice()
      .sort((a, b) => Number(a.remaining_days || 0) - Number(b.remaining_days || 0));
  }, [projects]);

  useEffect(() => {
    setShowAll(false);
  }, [projects, periodLabel]);

  const atRisk = sortedProjects.filter(
    (proj) => Number(proj.remaining_days || 0) <= 7
  ).length;

  const visibleProjects = showAll
    ? sortedProjects
    : sortedProjects.slice(0, firstRowCount);
  const hasMoreThanOneRow = sortedProjects.length > firstRowCount;

  return (
    <SectionCard
      title="Ongoing long-term projects"
      subtitle="Consultant-level long-term engagements active today"
      right={
        <div className="flex flex-wrap gap-2">
          <div className="rounded-2xl bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600">
            {projects.length} consultant engagements
          </div>

          <div
            className={[
              "rounded-2xl px-3 py-2 text-xs font-medium",
              atRisk > 0
                ? "bg-rose-50 text-rose-700"
                : "bg-emerald-50 text-emerald-700",
            ].join(" ")}
          >
            {atRisk} ending soon
          </div>
        </div>
      }
    >
      {sortedProjects.length === 0 ? (
        <EmptyState title="No ongoing projects found" />
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
            {visibleProjects.map((proj) => (
              <OngoingProjectCard
                key={`${proj.project_id}-${proj.expert_id}`}
                proj={proj}
              />
            ))}
          </div>

          {hasMoreThanOneRow ? (
            <div className="mt-5 flex justify-center">
              <button
                type="button"
                onClick={() => setShowAll((current) => !current)}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-[#365561] shadow-sm transition hover:border-[#365561]/25 hover:bg-[#365561]/5 focus:outline-none"
              >
                {showAll ? "Show less" : "See more"}
              </button>
            </div>
          ) : null}
        </>
      )}
    </SectionCard>
  );
}

function ProjectVolumeTrend({ monthlyProjects = [], loading }) {
  const [compareMode, setCompareMode] = useState("last_year");

  const years = useMemo(() => {
    return Array.from(
      new Set((monthlyProjects || []).map((item) => Number(item.year)).filter(Boolean))
    ).sort((a, b) => a - b);
  }, [monthlyProjects]);

  const currentYear = years[years.length - 1] || new Date().getFullYear();

  const visibleYears = useMemo(() => {
    if (compareMode === "since_2022") return years;
    if (compareMode === "last_5_years") return years.slice(-5);
    return years.filter((year) => year >= currentYear - 1);
  }, [compareMode, currentYear, years]);

  const chartData = useMemo(() => {
    const monthNames = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];

    const lookup = new Map(
      (monthlyProjects || []).map((item) => [
        `${Number(item.year)}-${Number(item.month_number)}`,
        item.total_projects === null || item.total_projects === undefined
          ? null
          : Number(item.total_projects || 0),
      ])
    );

    return monthNames.map((month, index) => {
      const monthNumber = index + 1;
      const row = { month };
      visibleYears.forEach((year) => {
        row[String(year)] = lookup.has(`${year}-${monthNumber}`)
          ? lookup.get(`${year}-${monthNumber}`)
          : 0;
      });
      return row;
    });
  }, [monthlyProjects, visibleYears]);

  const yearTotals = useMemo(() => {
    const totals = {};

    (monthlyProjects || []).forEach((item) => {
      const year = Number(item.year);
      if (!year) return;
      if (item.total_projects === null || item.total_projects === undefined) return;
      totals[year] = Number(totals[year] || 0) + Number(item.total_projects || 0);
    });

    return totals;
  }, [monthlyProjects]);

  const currentYearTotal = Number(yearTotals[currentYear] || 0);

  // Assign colors by the year, not by its position in visibleYears.
  // The previous year stays blue in every comparison mode.
  const historicalLineColors = [
    "#0ea5e9", // previous year
    "#f59e0b",
    "#10b981",
    "#ef4444",
    "#8b5cf6",
    "#14b8a6",
  ];
  const getYearColor = (year) => {
    if (year === currentYear) return BRAND;
    const yearsBack = Math.max(1, currentYear - year);
    return historicalLineColors[(yearsBack - 1) % historicalLineColors.length];
  };

  return (
    <SectionCard
      title="Project volume trend"
      subtitle="January–December project requests, with historical comparison back to 2022"
      right={
        <div className="flex flex-wrap items-center gap-2">
          <div className="rounded-2xl bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600">
            {currentYear}: {formatNumber(currentYearTotal)} requests
          </div>
          <select
            value={compareMode}
            onChange={(event) => setCompareMode(event.target.value)}
            className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm focus:border-[#365561]/30 focus:outline-none"
          >
            <option value="last_year">Compare with last year</option>
            <option value="last_5_years">Last 5 years</option>
            <option value="since_2022">Since 2022</option>
          </select>
        </div>
      }
    >
      {loading ? (
        <LoadingSpinner label="Loading project trend..." />
      ) : chartData.length === 0 ? (
        <EmptyState title="No project trend data" />
      ) : (
        <div className="h-[360px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="month"
                tickLine={false}
                axisLine={false}
                tick={{ fill: "#64748b", fontSize: 12 }}
              />
              <YAxis
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                tick={{ fill: "#64748b", fontSize: 12 }}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 16,
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 12px 24px rgba(15,23,42,0.08)",
                }}
              />
              <Legend
                formatter={(value) => {
                  const year = Number(value);
                  return `${value} (${formatNumber(yearTotals[year] || 0)} requests)`;
                }}
                wrapperStyle={{
                  fontSize: "11px",
                  color: "#64748b",
                  fontWeight: 500,
                  paddingTop: "10px",
                }}
              />
              {visibleYears.map((year) => (
                <Line
                  key={year}
                  type="monotone"
                  dataKey={String(year)}
                  name={String(year)}
                  stroke={getYearColor(year)}
                  strokeWidth={year === currentYear ? 3.5 : 2.25}
                  dot={{ r: year === currentYear ? 4 : 3 }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </SectionCard>
  );
}

export default function Dashboard() {
  useEffect(() => {
    document.title = "Dashboard";
  }, []);

  const { API_URL } = useContext(UrlContext);

  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState("");
  const [dashboardUsers, setDashboardUsers] = useState([]);

  const [selectedPeriodType, setSelectedPeriodType] = useState("week");
  const [selectedPeriod, setSelectedPeriod] = useState(() => getWeekKey());
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const [ongoingProjects, setOngoingProjects] = useState([]);

  const [dashboardCalls, setDashboardCalls] = useState({
    expert_calls: [],
    get_to_know_calls: [],
  });
  const [callsLoading, setCallsLoading] = useState(true);
  const [clockTick, setClockTick] = useState(Date.now());

  const [weeklyActivity, setWeeklyActivity] = useState({
    expert_calls: [],
    long_term_projects: [],
  });
  const [weeklyActivityLoading, setWeeklyActivityLoading] = useState(true);

  const monthlyProjects = dashboardData?.charts?.projects_per_month || [];
  const bdPerformance = dashboardData?.charts?.bd_status_breakdown || [];
  const bdActivity = dashboardData?.charts?.bd_activity_breakdown || bdPerformance;
  const bdDeliveryLeaderboard = dashboardData?.charts?.bd_delivery_leaderboard || [];
  const supporterDeliveryLeaderboard = dashboardData?.charts?.supporter_delivery_leaderboard || [];
  const bdExecutionKpis = dashboardData?.charts?.bd_execution_kpis || [];
  const supporterExecutionKpis = dashboardData?.charts?.supporter_execution_kpis || [];
  const clientPerformance = dashboardData?.charts?.client_status_breakdown || [];
  const projectCategoryBreakdown = dashboardData?.charts?.project_category_breakdown || [];
  const projectCategoryInsights = dashboardData?.charts?.project_category_insights || [];
  const consultantCountries = dashboardData?.charts?.verified_consultants_by_country || [];
  const projectSupporterLoad = dashboardData?.charts?.project_supporter_load || [];
  const kpis = dashboardData?.kpis || {};
  const pipelineComparison = dashboardData?.pipeline_comparison || {};
  const pipelineInsights = dashboardData?.pipeline_insights || {};
  const profilesAddedInPeriod = Number(
    kpis.profiles_added_in_period ?? kpis.profiles_added_today ?? 0
  );

  void bdDeliveryLeaderboard;
  void supporterDeliveryLeaderboard;

  const periodLabel = useMemo(
    () => getRangeLabel(selectedPeriodType, selectedPeriod),
    [selectedPeriodType, selectedPeriod]
  );

  const handlePeriodTypeChange = (type) => {
    setSelectedPeriodType(type);
    setSelectedPeriod(getCurrentRangeKey(type));
  };

  // LinkedIn-style compact range navigation once the main dashboard header scrolls away.
  useEffect(() => {
    const handleScroll = () => {
      const triggerHeight = 260;
      setShowStickyHeader(window.scrollY > triggerHeight);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const fetchDashboardUsers = async () => {
    try {
      const response = await fetch(`${API_URL}fetch-dashboard-users`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      const data = await response.json();
      if (response.ok && data.status === "success") {
        setDashboardUsers(data.dashboardUsers || []);
      } else {
        console.error(
          "Failed to load dashboard users:",
          data.message || "Unknown error"
        );
      }
    } catch (error) {
      console.error("Error fetching dashboard users:", error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setDashboardLoading(true);
      setDashboardError("");

      const res = await fetch(
        `${API_URL}fetch-dashboard-data?period=${encodeURIComponent(selectedPeriod)}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );

      const data = await res.json();
      if (data.status === "success") {
        setDashboardData(data.dashboardData);
      } else {
        setDashboardError(data.message || "Failed to load dashboard data");
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setDashboardError("Something went wrong while loading dashboard data");
    } finally {
      setDashboardLoading(false);
    }
  };

  const fetchOngoingProjects = async () => {
    try {
      const res = await fetch(
        `${API_URL}fetch-dashboard-ongoing-projects?period=${encodeURIComponent(selectedPeriod)}`
      );
      const data = await res.json();
      if (data.status === "success") {
        setOngoingProjects(data.ongoingProjects || []);
      }
    } catch (err) {
      console.error("Error fetching ongoing projects:", err);
    }
  };

  const fetchDashboardCalls = async () => {
    try {
      setCallsLoading(true);
      const res = await fetch(
        `${API_URL}fetch-dashboard-calls?period=${encodeURIComponent(selectedPeriod)}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );

      const data = await res.json();
      if (data.status === "success") {
        setDashboardCalls(
          data.dashboardCalls || { expert_calls: [], get_to_know_calls: [] }
        );
      }
    } catch (err) {
      console.error("Error fetching dashboard calls:", err);
    } finally {
      setCallsLoading(false);
    }
  };

  const fetchWeeklyActivity = async () => {
    try {
      setWeeklyActivityLoading(true);
      const res = await fetch(
        `${API_URL}fetch-dashboard-weekly-activity?period=${encodeURIComponent(selectedPeriod)}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );

      const data = await res.json();
      if (data.status === "success") {
        setWeeklyActivity(
          data.weeklyActivity || { expert_calls: [], long_term_projects: [] }
        );
      }
    } catch (err) {
      console.error("Error fetching dashboard wins:", err);
    } finally {
      setWeeklyActivityLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardUsers();
  }, []);

  useEffect(() => {
    fetchDashboardData();
    fetchOngoingProjects();
    fetchDashboardCalls();
    fetchWeeklyActivity();
  }, [selectedPeriod]);

  useEffect(() => {
    const timer = setInterval(() => setClockTick(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);

  if (dashboardError) {
    return (
      <div className="min-h-screen w-full bg-[radial-gradient(circle_at_top,rgba(54,85,97,0.14),transparent_28%),linear-gradient(to_bottom,#f8fafc,#eef2f6)] p-4 sm:p-6 lg:p-8">
        <div className="mx-auto max-w-[1600px]">
          <div className="rounded-3xl border border-rose-200 bg-white p-8 shadow-[0_10px_30px_rgba(15,23,42,0.06)]">
            <p className="text-sm font-semibold text-rose-600">Failed to load dashboard</p>
            <p className="mt-2 text-sm text-slate-500">{dashboardError}</p>
            <button
              type="button"
              onClick={fetchDashboardData}
              className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-[#365561] px-4 py-2 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(54,85,97,0.22)] transition hover:bg-[#2c444d] focus:outline-none"
            >
              <RefreshCw className="h-4 w-4" />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[radial-gradient(circle_at_top,rgba(54,85,97,0.14),transparent_28%),linear-gradient(to_bottom,#f8fafc,#eef2f6)] p-4 sm:p-6 lg:p-8">
      <style>{`
        button:focus,
        button:focus-visible {
          outline: none !important;
          box-shadow: none;
        }
      `}</style>

      <DashboardStickyRangeHeader
        visible={showStickyHeader}
        periodLabel={periodLabel}
        periodType={selectedPeriodType}
        rangeKey={selectedPeriod}
        loading={dashboardLoading}
        onPeriodTypeChange={handlePeriodTypeChange}
        onRangeChange={setSelectedPeriod}
      />

      <div className="mx-auto max-w-[1600px]">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-medium text-[#365561]">Expertpowerhouse dashboard</p>
            <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">
              Business overview
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-500">
              Live analytics for project pipeline, BD performance, consultant coverage,
              calls, and long-term delivery.
            </p>
          </div>

          <DashboardRangeControls
            periodType={selectedPeriodType}
            rangeKey={selectedPeriod}
            loading={dashboardLoading}
            onPeriodTypeChange={handlePeriodTypeChange}
            onRangeChange={setSelectedPeriod}
          />
        </div>

        <DashboardHeroStats
          periodLabel={periodLabel}
          periodType={selectedPeriodType}
          bdPerformance={bdPerformance}
          pipelineComparison={pipelineComparison}
          pipelineInsights={pipelineInsights}
          projectCategoryBreakdown={projectCategoryBreakdown}
          clientPerformance={clientPerformance}
        />

        {/* Finance section intentionally disabled for the current dashboard phase.
        <div className="mt-4">
          <ExecutiveDashboardData
            periodLabel={periodLabel}
            kpis={kpis}
            financeSplit={dashboardData?.charts?.finance_split || {}}
          />
        </div>
        */}

        <div className="mt-4 grid grid-cols-1 gap-4">
          <ProjectVolumeTrend
            monthlyProjects={monthlyProjects}
            loading={dashboardLoading}
          />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4">
          <RangeWinsSection
            expertCalls={weeklyActivity.expert_calls || []}
            longTermProjects={weeklyActivity.long_term_projects || []}
            loading={weeklyActivityLoading}
            periodLabel={periodLabel}
          />
        </div>

        <div className="mt-4 grid gap-4 xl:grid-cols-[45fr_55fr] xl:items-stretch">
          <BusinessDeveloperPerformance
            bdPerformance={bdActivity}
            periodLabel={periodLabel}
          />

          <ProjectSupporterLoadSection
            items={projectSupporterLoad}
            loading={dashboardLoading}
            periodLabel={periodLabel}
          />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4">
          <ExecutionKpiSection
            items={bdExecutionKpis}
            loading={dashboardLoading}
            periodLabel={periodLabel}
            periodKey={selectedPeriod}
            roleType="business_developer"
            API_URL={API_URL}
            dashboardUsers={dashboardUsers}
          />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4">
          <ExecutionKpiSection
            items={supporterExecutionKpis}
            loading={dashboardLoading}
            periodLabel={periodLabel}
            periodKey={selectedPeriod}
            roleType="project_supporter"
            API_URL={API_URL}
            dashboardUsers={dashboardUsers}
          />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4">
          <ClientLeaderboardSection
            clientPerformance={clientPerformance}
            periodLabel={periodLabel}
          />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4">
          <ProjectCategorySection
            categories={projectCategoryBreakdown}
            categoryInsights={projectCategoryInsights}
            periodLabel={periodLabel}
            periodType={selectedPeriodType}
            rangeKey={selectedPeriod}
          />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4">
          <WorldMapCard
            consultantCountries={consultantCountries}
            profilesAddedInPeriod={profilesAddedInPeriod}
            periodLabel={periodLabel}
          />
        </div>

        <div className="mt-4 w-full">
          <DashboardCallsSection
            title={`Expert calls · ${periodLabel}`}
            subtitle="Expert Calls scheduled inside the selected range"
            calls={dashboardCalls.expert_calls || []}
            loading={callsLoading}
            nowValue={clockTick}
          />
        </div>

        <div className="mt-4 w-full">
          <DashboardCallsSection
            title={`Get-to-know calls · ${periodLabel}`}
            subtitle="Get-to-know calls scheduled inside the selected range"
            calls={dashboardCalls.get_to_know_calls || []}
            loading={callsLoading}
            nowValue={clockTick}
          />
        </div>

        <div className="mt-4 w-full">
          <OngoingProjectsSection
            projects={ongoingProjects}
            periodLabel={periodLabel}
          />
        </div>
      </div>
    </div>
  );
}
